/**
 * Adaptador do Supabase: a `PortaDeDados` sobre PostgREST.
 *
 * Nenhuma consulta filtra por clínica — e isso é proposital. O `clinica_id` sai
 * da RLS, que já limita cada SELECT ao tenant de quem está logado (ver
 * `docs/BANCO.md`). Filtro repetido na aplicação daria a impressão de que é ele
 * quem protege; não é, e no dia em que faltasse ninguém notaria.
 *
 * Na ESCRITA o `clinica_id` vai explícito, porque a política de INSERT confere
 * o valor enviado: é a linha que prova que quem escreve pertence ao tenant.
 */

import { supabase } from '@/supabase';
import { paraEntregaDeWebhook, paraWebhook } from './webhooks';
import type { DadosDoWebhook } from './webhooks';
import { atual } from '@/session/sessao';
import { unidadeAtiva } from '@/session/unidadeAtiva';
import { fluxoDiario, fluxoMensal, janelaDeMeses, relatorioDeCompetencia, totaisPorCategoria } from './relatoriosFinanceiros';
import { montarRelatorio } from './relatoriosDaClinica';
import { horasDaGrade, horasSemanais, normalizarDias } from './jornada';
import { indicadoresDeAutomacoes, montarAutomacao } from './automacoesDaClinica';
import { derivadosDaCampanha, indicadoresDeMarketing, publicosDisponiveis } from './marketingDaClinica';
import { indicadoresDaPlataforma, indicadoresDoFaturamento, situacaoDaFatura } from './plataformaDaVecchia';
import { executarProposta } from './propostasDoAgente';
import { retornosEmAbertoDe } from './retornos';
import { lerTodasAsLinhas, lerTudo } from './paginacaoNoServidor';
import { caminhoDoAnexo, enviarSemDuplicar } from './anexoResiliente';
import { emAbertoPorContato } from './saldosDoContato';
import { contagemPorCanal, contatosComLeadAberto, vinculosDaConversa } from './caixaDeEntrada';
import { tamanhoDoPublico as tamanhoDoPublicoDe } from './publicos';
import type { BaldeDeAnexos } from './anexoResiliente';
import { executar, simular } from './simulacaoDeFluxo';
import { validarFluxo } from './fluxos';
import { indisponibilidadeDoProvedor } from './integracoes';
import { camposDaCobranca, paraInadimplencia } from './gateways';
import { lerResultadoDoDisparo, problemaDaMensagemDaCampanha } from './disparoDeCampanha';
import type { ResultadoDoDisparo } from './disparoDeCampanha';
import { nomeSeguroDoArquivo, problemaDoArquivo, tipoDaMensagemDoArquivo } from './midiaDaMensagem';
import { contatoDoCaminhoDoAnexo, registrarAcessoAoProntuario } from './auditoriaDoProntuario';
import { clinicaAtiva } from './clinicaAtiva';
import type { PassoDoFluxo } from './fluxos';
import { etapasDe, horasAteResolver, indicadoresDoOnboarding, indicadoresDoSuporte } from './suporteDaPlataforma';
import { funilPadrao, montarPipeline, ordenarFunis, proximaEtapa } from './funis';
import type { EtapaEditada, ModeloDeFunil } from './funis';
import type { LinhaDeAutomacao } from './automacoesDaClinica';
import type { AgendamentoDoRelatorio } from './relatoriosDaClinica';
import { custoDeConsumoCentavos, embalagensDe } from '@/comuns/unidades';
import { contarCampos, preencherCorpo } from '@/comuns/documentos';
import type { PortaDeDados } from './porta';
import { cadastrosNoSupabase } from './adaptadorSupabaseCadastros';
import { paraNotificacao } from './notificacoes';
import { comunicacaoNoSupabase } from './adaptadorSupabaseComunicacao';
import { anunciosNoSupabase } from './adaptadorSupabaseAnuncios';
import { financeiroAvulsoNoSupabase } from './adaptadorSupabaseFinanceiro';
import { atendimentoNoSupabase } from './adaptadorSupabaseAtendimento';
import { notaDeIaNoSupabase } from './adaptadorSupabaseNotaDeIa';
import { vendasNoSupabase } from './adaptadorSupabaseVendas';
import { planosNoSupabase } from './adaptadorSupabasePlanos';
import { fichaNoSupabase } from './adaptadorSupabaseFicha';
import { brainNoSupabase } from './adaptadorSupabaseBrain';
import { notasFiscaisNoSupabase } from './adaptadorSupabaseNotasFiscais';
import type {
    AgendaDaSemana, Agendamento, Atendimento, CargaDeAtendimentos, CargaDeContatos, CargaDoPaciente,
    Convenio, EdicaoDeAgendamento, EdicaoDeLead, EdicaoDeMembro, NovoUsuarioDaEquipe, FichaDoPaciente, FiltroDeAtendimentos,
    Canal, CargaDaCaixa, Conversa, FiltroDeContatos, FiltroDeConversas, Lead, Lembrete, AtribuicaoDoLead,
    CargaDeVendas, CargaDoPipeline, CorDaEtapa, FiltroDeVendas, Funil, MembroDaEquipe, SituacaoDoLead, TipoDaEtapa, VagasDeMembros, VagasDaClinicaNaPlataforma,
    CargaDoFinanceiro, CategoriaDeConta, CategoriaFinanceira, ContaFinanceira, FiltroDoFinanceiro, Mensagem,
    MetodoDePagamento, Movimentacao, NovoAgendamento, NovoContato, NovoOrcamento, NovoPagamento,
    AtendimentoEmCurso, CampoDeFicha, FichaPreenchida, ModeloDeFicha, NovaFicha, Orcamento, Pacote,
    Paciente,
    ProntuarioDoPaciente, ResumoDoPaciente, RespostaDaFicha, StatusDoOrcamento, Titulo, CondicaoDeCobranca,
    RegistroDeHistorico, ResumoDoDia, SituacaoDoAtendimento, StatusDoAgendamento, Tarefa, TipoDeContato, TipoImportavel,
    CategoriaParaEscolha, DadosDoPrestador, PrestadorDeServico, TipoDeServico, TotaisDeContatos,
    CargaDoEstoque, CategoriaDeProduto, DadosDaCategoriaDeProduto, DadosDaMarca, DadosDoFornecedor,
    DadosDoProduto, FiltroDoEstoque, Fornecedor, Marca, MovimentacaoDeEstoque, NovaMovimentacao,
    NovoProduto, Produto, ReservaDeEstoque, SituacaoDoProduto,
    CampoPersonalizado, CargaDeProcedimentos, ContextoDaReserva, CustoExtra, DadosDoProcedimento,
    FiltroDeProcedimentos, InsumoDoProcedimento, Procedimento,
    CargaDeCompras, DadosDoPedido, FiltroDeCompras, ItemDoPedido, PedidoDeCompra, Recebimento,
    SugestaoDeCompra,
    CargaDeDocumentos, DadosDoModeloDeDocumento, Documento, FiltroDeDocumentos, ModeloDeDocumento,
    NovoDocumento, StatusDoDocumento,
    CargaDeRelatorios, FiltroDeRelatorios,
    AbaDeConfiguracoes, CargaDeConfiguracoes, DadosDaClinica, DiaDeFuncionamento, EdicaoDaClinica,
    EdicaoDaUnidade, NovaUnidade, SalaDaClinica, UnidadeDaClinica,
    AbaDeAutomacoes, Automacao, CargaDeAutomacoes, ResultadoDaExecucao,
    AbaDeMarketing, Campanha, CargaDeMarketing, DadosDaCampanha, LinhaDeOrigem, PublicoDaCampanha,
    DadosDoLembrete,
    CargaDaClinicaNaPlataforma, CargaDaPlataforma, ClinicaNaPlataforma, EventoDeAuditoria,
    ImpersonacaoRegistrada, UsuarioNaPlataforma,
    AssinaturaDaClinica, CargaDoFaturamento, DadosDoPlano, Fatura, Plano,
    CargaDoOnboarding, CargaDoSuporte, Chamado, DadosDoChamado, OnboardingDaClinica,
    CargaDasFlags, ExcecaoDaFlag, FlagDeModulo,
    AcaoDePareamento, ArquivoDaFicha, CanalDeIntegracao, ChaveDeIa, DadosDoCanal, DocumentoDaBase, LinhaDeConsumoDeIa, MeuOrcamentoDeIa, OrcamentoDeIa, PareamentoDoCanal, ProvedorDeCanalDaPlataforma,
    ProvedorDeIa, RetornoEmAberto, StatusDoCanal,
    Agente, DadosDoAgente, PrioridadeDaTarefa, MensagemDoAgente, PropostaDoAgente, RespostaDoAgente,
    DadosDoFluxo, Fluxo, ResultadoDoFluxo, SimulacaoDoFluxo,
    ContatoParaEscolha, MinhaConta, OpcoesDoAgendamento, LinhaImportada, RecusaDaImportacao, ResultadoDaImportacao,
    ChaveDeApi, ChaveDeApiCriada, CreditoDaClinica, DadosDaFaturaAvulsa, DadosDaNovaClinica, DadosDoCredito,
    DadosDoDesconto, DescontoDaClinica, OperadorDaPlataforma, SaudeDoBanco, SituacaoDaClinica,
    UsoDeChaveDeApi,
    CobrancaDaFatura, CobrancasCriadas, DadosDoGateway, GatewayDePagamento, InadimplenciaDaClinica,
    ProvedorDeGateway, TesteDoGateway,
    EtiquetaAplicada,
} from './tipos';
import { gerarLinkDeTeleconsulta } from './teleconsulta';

/** A borda devolve o motivo no corpo; o cliente das funções só dá a mensagem genérica. */
async function mensagemDaBordaDoResumo(erro: unknown): Promise<string> {
    const contexto = (erro as { context?: unknown })?.context;
    if (contexto instanceof Response) {
        try {
            const corpo = await contexto.json();
            if (typeof corpo?.erro === 'string') return corpo.erro;
        } catch {
            /* Sem corpo legível: fica a mensagem de quem chamou. */
        }
    }
    return 'Não foi possível gerar o resumo agora.';
}

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

/** Tenant da sessão. Sem ele não há escrita possível — nem deve haver. */
function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string } | null): never | void {
    if (e) throw new Error(`${mensagem}: ${e.message}`);
}

/* ------------------------------------------------------------------ agenda */

interface LinhaDeAgendamento {
    id: string;
    inicio_em: string;
    duracao_min: number;
    status: StatusDoAgendamento;
    titulo: string | null;
    observacoes: string | null;
    teleconsulta: boolean;
    lembrete_whatsapp: boolean;
    link_teleconsulta: string | null;
    contato_id: string | null;
    lead_id: string | null;
    contatos: { nome: string } | null;
    procedimentos: { nome: string; preco_centavos: number } | null;
    profissional: { nome: string } | null;
    salas: { nome: string } | null;
    unidade_id: string | null;
    unidades: { nome: string } | null;
    leads: {
        origem: string | null; interesse: string | null; etapa: string; valor_estimado_centavos: number;
        funil_etapas: { titulo: string } | { titulo: string }[] | null;
    } | null;
}

/* `agendamentos` tem DUAS chaves para `membros` — o profissional e quem criou —
   e o PostgREST recusa a consulta ambígua em vez de escolher uma. O nome da
   coluna depois do `!` desfaz a dúvida, e o apelido deixa o resultado legível. */
const CAMPOS_DO_AGENDAMENTO =
    'id, inicio_em, duracao_min, status, titulo, observacoes, teleconsulta, lembrete_whatsapp, link_teleconsulta, contato_id, lead_id,'
    + ' unidade_id, contatos(nome), procedimentos(nome, preco_centavos), profissional:membros!profissional_id(nome), salas(nome), unidades(nome),'
    + ' leads(origem, interesse, etapa, valor_estimado_centavos, funil_etapas(titulo))';

function paraAgendamento(l: LinhaDeAgendamento): Agendamento {
    return {
        id: l.id,
        inicioEm: l.inicio_em,
        duracaoMin: l.duracao_min,
        pacienteId: l.contato_id ?? '',
        /* Bloqueio não tem contato: o título é o que aparece na grade. */
        paciente: l.contatos?.nome ?? l.titulo ?? 'Bloqueio',
        procedimento: l.procedimentos?.nome ?? '',
        profissional: l.profissional?.nome ?? '',
        sala: l.salas?.nome ?? undefined,
        status: l.status,
        vinculo: l.lead_id && l.contatos
            ? {
                tipo: 'lead', id: l.lead_id, nome: l.contatos.nome,
                oportunidade: l.leads?.interesse ?? undefined,
                /* O título da etapa; `leads.etapa` é a situação desde a 0100. */
                etapa: ([] as { titulo: string }[]).concat(l.leads?.funil_etapas ?? [])[0]?.titulo,
            }
            : l.contato_id && l.contatos
                ? { tipo: 'paciente', id: l.contato_id, nome: l.contatos.nome }
                : undefined,
        teleconsulta: l.teleconsulta,
        observacoes: l.observacoes ?? undefined,
        lembretePorWhatsapp: l.lembrete_whatsapp,
        ...(l.link_teleconsulta ? { linkTeleconsulta: l.link_teleconsulta } : {}),
        ...(l.unidade_id ? { unidadeId: l.unidade_id } : {}),
        ...(l.unidades?.nome ? { unidade: l.unidades.nome } : {}),
    };
}

const FUSO = 'America/Sao_Paulo';

function diaLocal(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: FUSO });
}

function segundaDa(dia: string): string {
    const d = new Date(`${dia}T12:00:00-03:00`);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.toLocaleDateString('en-CA', { timeZone: FUSO });
}

function ultimoDiaDoMes(dia: string): string {
    const [ano, mes] = dia.split('-').map(Number);
    const primeiroDoSeguinte = new Date(Date.UTC(ano ?? 2026, mes ?? 1, 1, 12));
    primeiroDoSeguinte.setUTCDate(primeiroDoSeguinte.getUTCDate() - 1);
    return primeiroDoSeguinte.toISOString().slice(0, 10);
}

function somaDias(dia: string, dias: number): string {
    const d = new Date(`${dia}T12:00:00-03:00`);
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: FUSO });
}

/* ----------------------------------------------------------------- contatos */

interface LinhaDePaciente {
    contato_id: string;
    nascimento: string | null;
    convenio_id: string | null;
    situacao: 'ativo' | 'pendente' | 'vencido';
    contatos: { nome: string; telefone: string } | null;
    profissional: { nome: string } | null;
}

interface LinhaDeLead {
    id: string;
    contato_id: string;
    origem: string | null;
    interesse: string | null;
    /** A situação desde a 0100: aberta, ganho ou perdido. */
    etapa: SituacaoDoLead;
    funil_id: string;
    etapa_id: string;
    funil_etapas: { titulo: string } | { titulo: string }[] | null;
    valor_estimado_centavos: number;
    proximo_contato_em: string | null;
    criado_em: string;
    contatos: { nome: string; telefone: string } | null;
    membros: { nome: string } | null;
    /** 0162: a campanha de anúncio. Sem permissão de marketing, o nome vem nulo. */
    campanha_de_anuncio_id: string | null;
    atribuicao: AtribuicaoDoLead | null;
    campanhas_de_anuncio: { nome: string } | { nome: string }[] | null;
}

function campanhaDoLead(l: LinhaDeLead): Pick<Lead, 'campanhaDeAnuncioId' | 'campanhaDeAnuncio' | 'atribuicao'> {
    if (!l.campanha_de_anuncio_id) return {};
    const nome = ([] as { nome: string }[]).concat(l.campanhas_de_anuncio ?? [])[0]?.nome;
    return {
        campanhaDeAnuncioId: l.campanha_de_anuncio_id,
        ...(nome ? { campanhaDeAnuncio: nome } : {}),
        ...(l.atribuicao ? { atribuicao: l.atribuicao } : {}),
    };
}

interface LinhaDeMembro {
    id: string;
    nome: string;
    email: string | null;
    telefone: string | null;
    papel: string;
    registro_conselho: string | null;
    especialidades: string | null;
    carga_semanal: string | null;
    acesso: MembroDaEquipe['acesso'];
    cep: string | null;
    rua: string | null;
    numero: string | null;
    complemento: string | null;
    bairro: string | null;
    cidade: string | null;
    estado: string | null;
}

const CAMPOS_DO_MEMBRO =
    'id, nome, email, telefone, papel, registro_conselho, especialidades, carga_semanal, acesso,'
    + ' cep, rua, numero, complemento, bairro, cidade, estado';

const PARTES_DO_ENDERECO = ['cep', 'rua', 'numero', 'complemento', 'bairro', 'cidade', 'estado'] as const;

/** As colunas de endereço que a edição mandou — vazio apaga, ausente não toca. */
function parteDoEndereco(mudancas: EdicaoDeMembro): Record<string, string | null> {
    const campos: Record<string, string | null> = {};
    for (const parte of PARTES_DO_ENDERECO) {
        const valor = mudancas[parte];
        if (valor !== undefined) campos[parte] = valor.trim() || null;
    }
    return campos;
}

/**
 * As vagas de acesso (0165), como a tela as usa.
 *
 * O `ocupadas` vem do banco, que conta ativo + convite pendente na clínica
 * inteira; a lista da tela é só o reforço para quando a RPC não responder —
 * ela pode estar paginada ou filtrada, e por isso nunca vence o banco.
 */
function paraVagas(linha: Record<string, unknown> | undefined, equipe: MembroDaEquipe[]): VagasDeMembros {
    const limite = linha?.limite === null || linha?.limite === undefined ? null : Number(linha.limite);
    const ocupadas = linha?.ocupadas === undefined || linha?.ocupadas === null
        ? equipe.filter((m) => m.acesso !== 'desativado').length
        : Number(linha.ocupadas);
    return { limite: limite !== null && Number.isFinite(limite) ? limite : null, ocupadas };
}

const numeroOuNulo = (v: unknown): number | null => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));

/** As vagas como a ficha da clínica no painel as mostra (`painel_das_vagas`). */
function paraVagasDoPainel(linha: Record<string, unknown> | undefined): VagasDaClinicaNaPlataforma {
    return {
        limite: numeroOuNulo(linha?.limite),
        ocupadas: numeroOuNulo(linha?.ocupadas) ?? 0,
        folgaDoCadastro: numeroOuNulo(linha?.folga_do_cadastro),
        limiteDoPlano: numeroOuNulo(linha?.limite_do_plano),
    };
}

/* Uma montagem só: a lista da equipe e o retorno de quem acabou de ser salvo
   discordariam no primeiro campo novo — foi o que aconteceu com o endereço. */
function paraMembro(m: LinhaDeMembro): MembroDaEquipe {
    return {
        id: m.id,
        nome: m.nome,
        ...(m.email ? { email: m.email } : {}),
        telefone: m.telefone ?? '',
        papel: m.papel,
        registro: m.registro_conselho ?? undefined,
        especialidades: m.especialidades ?? '—',
        cargaSemanal: m.carga_semanal ?? undefined,
        acesso: m.acesso,
        ...(m.cep ? { cep: m.cep } : {}),
        ...(m.rua ? { rua: m.rua } : {}),
        ...(m.numero ? { numero: m.numero } : {}),
        ...(m.complemento ? { complemento: m.complemento } : {}),
        ...(m.bairro ? { bairro: m.bairro } : {}),
        ...(m.cidade ? { cidade: m.cidade } : {}),
        ...(m.estado ? { estado: m.estado } : {}),
    };
}

/**
 * Nome de um relacionamento embutido.
 *
 * Sem os tipos gerados do banco, o supabase-js devolve `membros(nome)` ora como
 * objeto, ora como lista — depende do que ele consegue inferir da consulta.
 * Normalizar aqui evita espalhar `as` por todo mapeamento.
 */
function nomeEmbutido(v: unknown): string | undefined {
    const alvo = Array.isArray(v) ? v[0] : v;
    return (alvo as { nome?: string } | undefined | null)?.nome;
}

/** A linha do banco como a tela a espera. */
function paraDocumentoDaBase(l: Record<string, unknown>): DocumentoDaBase {
    return {
        id: String(l.id),
        titulo: String(l.titulo ?? ''),
        tipo: String(l.tipo ?? 'texto'),
        bytes: Number(l.bytes ?? 0),
        situacao: (l.situacao as DocumentoDaBase['situacao']) ?? 'pendente',
        ...(l.erro ? { erro: String(l.erro) } : {}),
        trechos: Number(l.trechos ?? 0),
        criadoEm: String(l.criado_em ?? ''),
    };
}

function paraModelo(linha: Record<string, unknown>): ModeloDeFicha {
    return {
        id: linha.id as string,
        nome: linha.nome as string,
        campos: ((linha.definicao as CampoDeFicha[] | null) ?? []),
        versao: (linha.versao as number | null) ?? 1,
        usos: ((linha.fichas_preenchidas as { id: string }[] | null) ?? []).length,
    };
}

function semAcento(v: string): string {
    return v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

async function totaisDeContatos(): Promise<TotaisDeContatos> {
    const [p, l, m, f, s] = await Promise.all([
        /* Paciente arquivado não conta: o total diria um número que a lista não mostra. */
        cliente().from('pacientes').select('contato_id, contatos!inner(arquivado_em)', { count: 'exact', head: true }).is('contatos.arquivado_em', null),
        cliente().from('leads').select('id', { count: 'exact', head: true }),
        cliente().from('membros').select('id', { count: 'exact', head: true }).is('arquivado_em', null),
        cliente().from('fornecedores').select('id', { count: 'exact', head: true }),
        cliente().from('prestadores_de_servico').select('id', { count: 'exact', head: true }),
    ]);
    return {
        pacientes: p.count ?? 0, leads: l.count ?? 0, profissionais: m.count ?? 0,
        fornecedores: f.count ?? 0, prestadores: s.count ?? 0,
    };
}

/* A categoria vem pelo nome da chave composta (0159). Sem acesso ao financeiro, a RLS a devolve nula. */
const CAMPOS_DO_FORNECEDOR = 'id, nome, cnpj, contato, telefone, email, prazo_entrega, ativo, categoria_id, marcas(nome), categorias_financeiras!fornecedores_categoria_da_clinica(nome)';
const CAMPOS_DO_PRESTADOR = 'id, nome, documento, telefone, email, tipo_servico_id, categoria_id, observacoes, ativo, tipos_de_servico(nome), categorias_financeiras(nome)';

function paraFornecedor(l: Record<string, unknown>): Fornecedor {
    const categoriaId = (l.categoria_id as string | null) ?? undefined;
    const categoria = nomeEmbutido(l.categorias_financeiras);
    return {
        id: l.id as string,
        nome: l.nome as string,
        cnpj: (l.cnpj as string | null) ?? undefined,
        contato: (l.contato as string | null) ?? undefined,
        telefone: (l.telefone as string | null) ?? undefined,
        email: (l.email as string | null) ?? undefined,
        prazoEntrega: (l.prazo_entrega as string | null) ?? undefined,
        ativo: l.ativo as boolean,
        marcas: ((l.marcas as { nome: string }[] | null) ?? []).map((m) => m.nome),
        ...(categoriaId ? { categoriaId } : {}),
        ...(categoria ? { categoria } : {}),
    };
}

function paraPrestador(l: Record<string, unknown>): PrestadorDeServico {
    const opcional = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
    const campos: Partial<PrestadorDeServico> = {};
    for (const [chave, valor] of [
        ['documento', opcional(l.documento)], ['email', opcional(l.email)], ['observacoes', opcional(l.observacoes)],
        ['tipoServicoId', opcional(l.tipo_servico_id)], ['tipoServico', nomeEmbutido(l.tipos_de_servico)],
        ['categoriaId', opcional(l.categoria_id)], ['categoria', nomeEmbutido(l.categorias_financeiras)],
    ] as const) {
        if (valor) campos[chave] = valor;
    }
    return { id: l.id as string, nome: l.nome as string, telefone: (l.telefone as string | null) ?? '', ativo: l.ativo as boolean, ...campos };
}

/**
 * As etiquetas de cada pessoa da clínica (0168), por `contatoId`.
 *
 * Uma consulta para a lista inteira, e não uma por linha: a lista de pacientes já é
 * paginada no navegador, e N+1 aqui seria uma ida por paciente. A RLS recorta pela
 * clínica, então não há filtro a escrever.
 */
async function etiquetasPorContato(): Promise<Map<string, EtiquetaAplicada[]>> {
    interface LinhaDeEtiquetaDoContato {
        contato_id: string;
        origem: 'manual' | 'gatilho';
        etiquetas: { id: string; nome: string; cor: string } | { id: string; nome: string; cor: string }[] | null;
    }
    const { data, error } = await cliente()
        .from('contato_etiquetas')
        .select('contato_id, origem, etiquetas(id, nome, cor)')
        .returns<LinhaDeEtiquetaDoContato[]>();
    erro('Falha ao ler as etiquetas das pessoas', error);
    const porContato = new Map<string, EtiquetaAplicada[]>();
    for (const linha of data ?? []) {
        const e = ([] as { id: string; nome: string; cor: string }[]).concat(linha.etiquetas ?? [])[0];
        if (!e) continue;
        const lista = porContato.get(linha.contato_id) ?? [];
        lista.push({ id: e.id, nome: e.nome, cor: e.cor, origem: linha.origem });
        porContato.set(linha.contato_id, lista);
    }
    for (const lista of porContato.values()) lista.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    return porContato;
}

/**
 * A busca da lista acha por etiqueta também.
 *
 * A tela de Contatos tem caixa de busca e nenhuma barra de filtros; acrescentar um seletor
 * só para etiqueta seria interface nova para um caso que a caixa já resolve — quem digita
 * "VIP" quer os marcados como VIP, e é o que passa a acontecer. Filtro dedicado, se vier,
 * é outra conversa.
 */
function casaNaBusca(item: { nome: string; telefone: string; etiquetas?: { nome: string }[] }, busca: string): boolean {
    const alvo = semAcento(busca);
    return semAcento(item.nome).includes(alvo)
        || item.telefone.includes(busca)
        || (item.etiquetas ?? []).some((e) => semAcento(e.nome).includes(alvo));
}

function paginar<T>(itens: T[], filtro: { pagina?: number; porPagina?: number }) {
    const porPagina = filtro.porPagina ?? 8;
    const paginas = Math.max(1, Math.ceil(itens.length / porPagina));
    const pagina = Math.min(Math.max(filtro.pagina ?? 1, 1), paginas);
    return { itens: itens.slice((pagina - 1) * porPagina, pagina * porPagina), total: itens.length, pagina, paginas };
}


/* ---------------------------------------------------------------- estoque */

/* As reservas abertas vêm embutidas no produto: é delas que sai o reservado, e
   uma segunda consulta para somá-las daria um número de outro instante. */
const CAMPOS_DO_PRODUTO =
    'id, nome, categoria_id, marca_id, fornecedor_id, lote, validade, quantidade, minimo, unidade,'
    + ' conteudo, unidade_conteudo, unidade_consumo, custo_centavos, ativo,'
    + ' categorias_produto(nome), marcas(nome), fornecedores(nome),'
    + ' reserva_itens(quantidade_consumo, reservas_estoque(status))';

function situacaoDe(disponivel: number, minimo: number): SituacaoDoProduto {
    if (disponivel <= 0 || disponivel < minimo * 0.5) return 'critico';
    if (disponivel < minimo) return 'baixo';
    return 'normal';
}

function paraProduto(l: Record<string, unknown>): Produto {
    const medida = {
        unidade: l.unidade as string,
        conteudo: Number(l.conteudo ?? 1),
        unidadeConteudo: l.unidade_conteudo as string,
        unidadeConsumo: l.unidade_consumo as string,
    };
    const itens = (l.reserva_itens as { quantidade_consumo: number; reservas_estoque: unknown }[] | null) ?? [];
    /* A quantidade da reserva está em unidade de CONSUMO e o saldo, em
       embalagens: somar direto reservaria cinquenta frascos para 50 UI. */
    const reservado = itens
        .filter((i) => {
            const reserva = Array.isArray(i.reservas_estoque) ? i.reservas_estoque[0] : i.reservas_estoque;
            return (reserva as { status?: string } | null)?.status !== 'consumida';
        })
        .reduce((soma, i) => soma + embalagensDe(medida, Number(i.quantidade_consumo)), 0);

    const quantidade = Number(l.quantidade ?? 0);
    const minimo = Number(l.minimo ?? 0);
    const disponivel = quantidade - reservado;

    return {
        id: l.id as string,
        nome: l.nome as string,
        categoriaId: (l.categoria_id as string | null) ?? undefined,
        categoria: nomeEmbutido(l.categorias_produto),
        marcaId: (l.marca_id as string | null) ?? undefined,
        marca: nomeEmbutido(l.marcas),
        fornecedorId: (l.fornecedor_id as string | null) ?? undefined,
        fornecedor: nomeEmbutido(l.fornecedores),
        lote: (l.lote as string | null) ?? '',
        validade: (l.validade as string | null) ?? undefined,
        quantidade,
        reservado,
        disponivel,
        minimo,
        situacao: situacaoDe(disponivel, minimo),
        ...medida,
        custoCentavos: Number(l.custo_centavos ?? 0),
        ativo: l.ativo as boolean,
    };
}

async function lerProduto(id: string): Promise<Produto> {
    const { data, error } = await cliente().from('produtos').select(CAMPOS_DO_PRODUTO).eq('id', id).single();
    erro('Falha ao ler o produto', error);
    return paraProduto(data as unknown as Record<string, unknown>);
}


/* ---------------------------------------------------------- procedimentos */

const CAMPOS_DO_PROCEDIMENTO =
    'id, nome, categoria, duracao_min, preco_centavos, exige_sala, ativo, cor, tipo_valor,'
    + ' reconsulta_dias, descricao, campos_personalizados,'
    + ' procedimento_profissionais(membro_id, membros(nome)),'
    + ' procedimento_custos_extras(id, nome, quantidade, unidade, valor_centavos, ordem),'
    + ' procedimento_insumos(produto_id, quantidade_consumo)';

function paraProcedimento(l: Record<string, unknown>, produtos: Map<string, Produto>): Procedimento {
    const insumos = ((l.procedimento_insumos as { produto_id: string; quantidade_consumo: number }[] | null) ?? [])
        .map((i): InsumoDoProcedimento | null => {
            const produto = produtos.get(i.produto_id);
            if (!produto) return null;
            const quantidade = Number(i.quantidade_consumo);
            return {
                produtoId: produto.id,
                produto: produto.nome,
                quantidade,
                unidadeConsumo: produto.unidadeConsumo,
                /* Pela unidade de consumo — o custo da embalagem multiplicado
                   pela quantidade consumida infla o procedimento em até 100×. */
                custoCentavos: custoDeConsumoCentavos(produto) * quantidade,
                embalagens: embalagensDe(produto, quantidade),
                unidade: produto.unidade,
                disponivel: produto.disponivel,
                situacao: produto.situacao,
            };
        })
        .filter((i): i is InsumoDoProcedimento => i !== null);

    const custosExtras: CustoExtra[] = ((l.procedimento_custos_extras as Record<string, unknown>[] | null) ?? [])
        .sort((a, b) => Number(a.ordem) - Number(b.ordem))
        .map((c) => ({
            id: c.id as string,
            nome: c.nome as string,
            quantidade: Number(c.quantidade),
            unidade: c.unidade as string,
            valorCentavos: Number(c.valor_centavos),
        }));

    const precoCentavos = Number(l.preco_centavos ?? 0);
    const custoDeInsumosCentavos = insumos.reduce((s, i) => s + i.custoCentavos, 0);
    const custoExtraCentavos = custosExtras.reduce((s, c) => s + c.quantidade * c.valorCentavos, 0);
    const custo = custoDeInsumosCentavos + custoExtraCentavos;

    return {
        id: l.id as string,
        nome: l.nome as string,
        categoria: (l.categoria as string | null) ?? undefined,
        duracaoMin: Number(l.duracao_min ?? 0),
        precoCentavos,
        tipoValor: (l.tipo_valor as Procedimento['tipoValor'] | null) ?? 'fixo',
        reconsultaDias: Number(l.reconsulta_dias ?? 0),
        cor: (l.cor as string | null) ?? undefined,
        descricao: (l.descricao as string | null) ?? undefined,
        exigeSala: l.exige_sala as boolean,
        ativo: l.ativo as boolean,
        profissionais: ((l.procedimento_profissionais as { membro_id: string; membros: unknown }[] | null) ?? [])
            .map((v) => ({ id: v.membro_id, nome: nomeEmbutido(v.membros) ?? '—' })),
        insumos,
        custosExtras,
        camposPersonalizados: ((l.campos_personalizados as CampoPersonalizado[] | null) ?? []),
        custoDeInsumosCentavos,
        custoExtraCentavos,
        margem: precoCentavos === 0 ? 0 : Math.round(((precoCentavos - custo) / precoCentavos) * 100),
        /* O insumo mais escasso manda. Sem insumo não há trava — `null`, e não
           zero, que significaria "bloqueado". */
        execucoesPossiveis: insumos.length === 0
            ? null
            /* Piso em zero: disponível negativo — reserva além do estoque —
               daria "-1 execuções", que não é número que alguém leia. */
            : Math.max(0, Math.min(...insumos.map((i) => Math.floor(i.disponivel / (i.embalagens || 1))))),
    };
}


/* --------------------------------------------------------------- compras */

const CAMPOS_DO_PEDIDO =
    'id, numero, fornecedor_id, status, previsao_entrega, observacoes, criado_em, criado_por_nome, enviado_em, recebido_em,'
    + ' fornecedores(nome),'
    + ' pedido_compra_itens(id, produto_id, quantidade, quantidade_recebida, custo_unitario_centavos, ordem, produtos(nome, unidade, custo_centavos))';

function paraPedido(l: Record<string, unknown>): PedidoDeCompra {
    const itens: ItemDoPedido[] = ((l.pedido_compra_itens as Record<string, unknown>[] | null) ?? [])
        .sort((a, b) => Number(a.ordem) - Number(b.ordem))
        .map((i) => {
            const produto = (Array.isArray(i.produtos) ? i.produtos[0] : i.produtos) as
                { nome?: string; unidade?: string; custo_centavos?: number } | null;
            return {
                id: i.id as string,
                produtoId: i.produto_id as string,
                produto: produto?.nome ?? '—',
                unidade: produto?.unidade ?? 'und',
                quantidade: Number(i.quantidade),
                quantidadeRecebida: Number(i.quantidade_recebida),
                custoUnitarioCentavos: Number(i.custo_unitario_centavos),
                custoAtualCentavos: Number(produto?.custo_centavos ?? i.custo_unitario_centavos),
            };
        });

    return {
        id: l.id as string,
        numero: l.numero as string,
        fornecedorId: (l.fornecedor_id as string | null) ?? undefined,
        fornecedor: nomeEmbutido(l.fornecedores) ?? '—',
        status: l.status as PedidoDeCompra['status'],
        previsaoEntrega: (l.previsao_entrega as string | null) ?? undefined,
        observacoes: (l.observacoes as string | null) ?? undefined,
        criadoEm: l.criado_em as string,
        criadoPor: (l.criado_por_nome as string | null) ?? undefined,
        enviadoEm: (l.enviado_em as string | null) ?? undefined,
        recebidoEm: (l.recebido_em as string | null) ?? undefined,
        itens,
        totalCentavos: itens.reduce((s, i) => s + i.quantidade * i.custoUnitarioCentavos, 0),
        recebidoCentavos: itens.reduce((s, i) => s + i.quantidadeRecebida * i.custoUnitarioCentavos, 0),
    };
}

/**
 * O que repor, agrupado por fornecedor — que é como o pedido é feito.
 *
 * A sugestão leva ao DOBRO do mínimo: comprar exatamente o mínimo faz o alerta
 * voltar na primeira saída.
 */
function sugestoesDe(produtos: Produto[]): SugestaoDeCompra[] {
    const porFornecedor = new Map<string, SugestaoDeCompra>();

    for (const produto of produtos.filter((p) => p.situacao !== 'normal')) {
        const chave = produto.fornecedorId ?? 'sem-fornecedor';
        const sugestao = Math.max(1, Math.ceil(produto.minimo * 2 - produto.disponivel));
        const grupo = porFornecedor.get(chave) ?? {
            fornecedorId: produto.fornecedorId,
            fornecedor: produto.fornecedor ?? 'Sem fornecedor',
            itens: [],
            totalCentavos: 0,
        };
        grupo.itens.push({
            produtoId: produto.id,
            produto: produto.nome,
            unidade: produto.unidade,
            disponivel: produto.disponivel,
            minimo: produto.minimo,
            sugestao,
            custoUnitarioCentavos: produto.custoCentavos,
            situacao: produto.situacao,
        });
        grupo.totalCentavos += sugestao * produto.custoCentavos;
        porFornecedor.set(chave, grupo);
    }

    return [...porFornecedor.values()].sort((a, b) => b.totalCentavos - a.totalCentavos);
}

async function lerPedido(id: string): Promise<PedidoDeCompra> {
    const { data, error } = await cliente().from('pedidos_compra').select(CAMPOS_DO_PEDIDO).eq('id', id).single();
    erro('Falha ao ler o pedido', error);
    return paraPedido(data as unknown as Record<string, unknown>);
}


/* ------------------------------------------------------------ documentos */

const CAMPOS_DO_DOCUMENTO =
    'id, titulo, tipo, contato_id, modelo_id, corpo, assinantes, canal, status, paginas,'
    + ' criado_em, atualizado_em, enviado_em, assinado_em, recusado_em, motivo_da_recusa,'
    + ' contatos(nome), profissional:membros!profissional_id(nome), procedimentos(nome)';

const CAMPOS_DO_MODELO = 'id, nome, categoria, assinantes, corpo, publicado, favorito, atualizado_em, documentos(count)';

interface LinhaDeHorario {
    dia_semana?: number;
    diaSemana?: number;
    inicio: string;
    fim: string;
}

/**
 * As sete linhas da semana, sempre — inclusive as vazias.
 *
 * O banco guarda faixa, não dia: dia fechado simplesmente não tem linha. A tela
 * precisa dos sete para desenhar a semana, então o dia sem faixa nasce aqui.
 */
function paraJornada(linhas: LinhaDeHorario[] | null): DiaDeFuncionamento[] {
    const dias: DiaDeFuncionamento[] = Array.from({ length: 7 }, (_, diaSemana) => ({ diaSemana, faixas: [] }));
    for (const l of linhas ?? []) {
        const indice = Number(l.dia_semana ?? l.diaSemana ?? -1);
        dias[indice]?.faixas.push({ inicio: String(l.inicio).slice(0, 5), fim: String(l.fim).slice(0, 5) });
    }
    return dias;
}

/** Dia (YYYY-MM-DD) somado de `dias`, no fuso da clínica. */
function diaComOffset(dias: number): string {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: FUSO });
}

/**
 * O alcance de cada gatilho, numa rodada só.
 *
 * Contagem pelo `head: true` do PostgREST: trazer as linhas para contá-las no
 * navegador seria pagar a lista inteira para exibir um número.
 */
async function alcancesDasAutomacoes(): Promise<Partial<Record<LinhaDeAutomacao['gatilho'], number>>> {
    const agora = new Date().toISOString();
    const em24h = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

    const [proximas, vencidos, abaixo, semRetorno] = await Promise.all([
        cliente().from('agendamentos').select('id', { count: 'exact', head: true })
            .gt('inicio_em', agora).lte('inicio_em', em24h)
            .not('status', 'in', '("bloqueio","cancelado")'),
        cliente().from('titulos').select('id', { count: 'exact', head: true })
            .eq('tipo', 'receber').lte('vencimento', diaComOffset(-3))
            .neq('situacao', 'pago'),
        cliente().from('produtos').select('id, quantidade, minimo').eq('ativo', true),
        contatosSemRetorno(90),
    ]);

    const produtos = (abaixo.data ?? []) as unknown as { quantidade: number; minimo: number }[];
    return {
        'agendamento-24h': proximas.count ?? 0,
        'titulo-vencido': vencidos.count ?? 0,
        'estoque-minimo': produtos.filter((p) => Number(p.quantidade) <= Number(p.minimo)).length,
        'sem-retorno-90d': semRetorno.length,
    };
}

/**
 * Pacientes atendidos antes do corte e sem nada marcado daqui para a frente.
 *
 * A regra mora em `retornos.ts` e serve à lista, à automação e ao marketing —
 * eram duas consultas e davam dois números.
 */
async function retornosEmAbertoDoServidor(dias: number): Promise<RetornoEmAberto[]> {
    const linhas = await lerTudo<Record<string, unknown>>((de, ate) => cliente().from('agendamentos')
        .select('id, contato_id, inicio_em, status, contatos(nome, telefone), procedimentos(nome)')
        .not('contato_id', 'is', null)
        .order('inicio_em').order('id')
        .range(de, ate));

    return retornosEmAbertoDe(linhas.map((l) => {
        const contato = (Array.isArray(l.contatos) ? l.contatos[0] : l.contatos) as { nome?: string; telefone?: string } | null;
        const procedimento = (Array.isArray(l.procedimentos) ? l.procedimentos[0] : l.procedimentos) as { nome?: string } | null;
        return {
            contatoId: String(l.contato_id),
            inicioEm: String(l.inicio_em),
            status: String(l.status),
            ...(contato?.nome ? { nome: contato.nome } : {}),
            ...(contato?.telefone ? { telefone: contato.telefone } : {}),
            ...(procedimento?.nome ? { procedimento: procedimento.nome } : {}),
        };
    }), dias, new Date());
}

async function contatosSemRetorno(dias: number): Promise<{ id: string; nome: string }[]> {
    return (await retornosEmAbertoDoServidor(dias)).map((r) => ({ id: r.contatoId, nome: r.nome }));
}

/** O que cada regra de ação interna escreve como tarefa. */
async function titulosDeTarefaDaAutomacao(gatilho: LinhaDeAutomacao['gatilho']): Promise<string[]> {
    if (gatilho === 'titulo-vencido') {
        const { data } = await cliente().from('titulos')
            .select('descricao, contatos(nome)')
            .eq('tipo', 'receber').lte('vencimento', diaComOffset(-3)).neq('situacao', 'pago');
        return ((data ?? []) as unknown as Record<string, unknown>[])
            .map((t) => `Cobrar ${nomeEmbutido(t.contatos) ?? 'contato'} — ${String(t.descricao ?? '')}`);
    }
    if (gatilho === 'estoque-minimo') {
        const { data } = await cliente().from('produtos').select('nome, quantidade, minimo').eq('ativo', true);
        return ((data ?? []) as unknown as { nome: string; quantidade: number; minimo: number }[])
            .filter((p) => Number(p.quantidade) <= Number(p.minimo))
            .map((p) => `Repor ${p.nome} — abaixo do mínimo`);
    }
    return [];
}

function paraClinicaDaPlataforma(c: Record<string, unknown>): ClinicaNaPlataforma {
    return {
        id: String(c.id),
        nome: String(c.nome ?? ''),
        apelido: String(c.apelido ?? ''),
        plano: String(c.plano ?? ''),
        situacao: c.situacao as ClinicaNaPlataforma['situacao'],
        criadoEm: String(c.criado_em ?? ''),
        membrosAtivos: Number(c.membros_ativos ?? 0),
        contatos: Number(c.contatos ?? 0),
        agendamentos30d: Number(c.agendamentos_30d ?? 0),
        ...(c.ultimo_acesso ? { ultimoAcesso: String(c.ultimo_acesso) } : {}),
    };
}

function paraUsuarioDaPlataforma(u: Record<string, unknown>): UsuarioNaPlataforma {
    return {
        id: String(u.id),
        nome: String(u.nome ?? ''),
        email: String(u.email ?? ''),
        papel: String(u.papel ?? ''),
        acesso: u.acesso as UsuarioNaPlataforma['acesso'],
        clinica: String(u.clinica ?? ''),
        temLogin: Boolean(u.tem_login),
        ...(u.ultimo_acesso ? { ultimoAcesso: String(u.ultimo_acesso) } : {}),
    };
}

function paraEventoDeAuditoria(a: Record<string, unknown>): EventoDeAuditoria {
    return {
        id: String(a.id),
        clinica: String(a.clinica ?? '—'),
        autorNome: String(a.autor_nome ?? 'Sistema'),
        acao: String(a.acao ?? ''),
        tabela: String(a.tabela ?? ''),
        ocorridoEm: String(a.ocorrido_em ?? ''),
        ...(a.clinica_id ? { clinicaId: String(a.clinica_id) } : {}),
        ...(a.registro_id ? { registroId: String(a.registro_id) } : {}),
        /* O banco só devolve o detalhe das ações da própria plataforma. */
        ...(a.detalhe && typeof a.detalhe === 'object' ? { detalhe: a.detalhe as Record<string, unknown> } : {}),
    };
}

function paraImpersonacao(i: Record<string, unknown>): ImpersonacaoRegistrada {
    return {
        id: String(i.id),
        clinica: String(i.clinica ?? ''),
        operador: String(i.operador ?? ''),
        motivo: String(i.motivo ?? ''),
        iniciadaEm: String(i.iniciada_em ?? ''),
        expiraEm: String(i.expira_em ?? ''),
        ...(i.encerrada_em ? { encerradaEm: String(i.encerrada_em) } : {}),
        viva: Boolean(i.viva),
    };
}

/** A mensagem que a função de borda devolveu no corpo, quando houver. */
async function mensagemDaFuncao(erro: unknown): Promise<string | undefined> {
    const contexto = (erro as { context?: unknown })?.context;
    if (contexto instanceof Response) {
        try {
            const corpo = await contexto.json();
            if (typeof corpo?.erro === 'string') return corpo.erro;
        } catch {
            /* Sem corpo legível: fica a mensagem genérica do cliente. */
        }
    }
    return undefined;
}

/** A função de borda da cobrança (T9): o erro dela vem no corpo, e é ele que a tela mostra. */
async function chamarCobranca<T>(corpo: Record<string, unknown>, falha: string): Promise<T> {
    const { data, error } = await cliente().functions.invoke('cobranca', { body: corpo });
    if (error) throw new Error((await mensagemDaFuncao(error)) ?? falha);
    const resposta = data as (T & { ok?: boolean; erro?: string }) | null;
    if (!resposta) throw new Error(falha);
    if (resposta.ok === false) throw new Error(resposta.erro ?? falha);
    return resposta;
}

function paraCobrancaDaFatura(r: { gateway?: string; link?: string | null; pix?: string | null; linhaDigitavel?: string | null }): CobrancaDaFatura {
    if (r.gateway !== 'asaas' && r.gateway !== 'stripe' && r.gateway !== 'mercadopago') {
        throw new Error('O servidor não disse por qual gateway a cobrança saiu.');
    }
    return { gateway: r.gateway, link: r.link ?? null, pix: r.pix ?? null, linhaDigitavel: r.linhaDigitavel ?? null };
}

/**
 * O canal daquela conversa, **se ele estiver conectado**.
 *
 * Nulo quando não há canal ou quando ele está fora do ar — e aí a mensagem fica
 * `pendente` no histórico em vez de estourar: escrever continua valendo, mesmo
 * quando o WhatsApp caiu.
 */
async function canalDaConversa(conversaId: string): Promise<string | null> {
    const { data } = await cliente()
        .from('conversas')
        .select('canal_id, canais(status, provedor)')
        .eq('id', conversaId)
        .maybeSingle();

    const linha = data as unknown as { canal_id: string | null; canais: unknown } | null;
    if (!linha?.canal_id) return null;
    const canal = (Array.isArray(linha.canais) ? linha.canais[0] : linha.canais) as { status?: string; provedor?: string } | null;
    return canal?.status === 'conectado' && canal.provedor === 'uazapi' ? linha.canal_id : null;
}

function canalPorId(canais: CanalDeIntegracao[], id: string): CanalDeIntegracao {
    const canal = canais.find((c) => c.id === id);
    if (!canal) throw new Error(`Canal ${id} não existe.`);
    return canal;
}

function paraFluxo(f: Record<string, unknown>): Fluxo {
    return {
        id: String(f.id),
        nome: String(f.nome ?? ''),
        descricao: (f.descricao as string | null) ?? '',
        gatilho: f.gatilho as Fluxo['gatilho'],
        passos: Array.isArray(f.passos) ? f.passos : [],
        ativo: Boolean(f.ativo),
        execucoes: Number(f.execucoes ?? 0),
        ...(f.ultima_execucao_em ? { ultimaExecucaoEm: String(f.ultima_execucao_em) } : {}),
        atualizadoEm: String(f.atualizado_em ?? ''),
    };
}

function paraAgente(a: Record<string, unknown>): Agente {
    return {
        id: String(a.id),
        tipo: a.tipo as Agente['tipo'],
        nome: String(a.nome ?? ''),
        ativo: Boolean(a.ativo),
        tom: a.tom as Agente['tom'],
        missao: (a.missao as string | null) ?? '',
        instrucoes: (a.instrucoes as string | null) ?? '',
        temperatura: Number(a.temperatura ?? 0.3),
        ...(a.modelo ? { modelo: String(a.modelo) } : {}),
        ferramentas: ((a.ferramentas ?? []) as string[]).map(String),
        ...(a.saudacao ? { saudacao: String(a.saudacao) } : {}),
        limites: (a.limites ?? {}) as Record<string, unknown>,
        atualizadoEm: String(a.atualizado_em ?? ''),
    };
}

function paraFlag(f: Record<string, unknown>): FlagDeModulo {
    const chave = String(f.chave ?? '');
    return {
        id: String(f.id),
        chave,
        modulo: chave.replace('modulo.', ''),
        descricao: String(f.descricao ?? ''),
        ativoPadrao: Boolean(f.ativo_padrao),
        excecoes: ((f.excecoes ?? []) as unknown as ExcecaoDaFlag[]).map((e) => ({
            clinicaId: String(e.clinicaId),
            clinica: String(e.clinica),
            ativo: Boolean(e.ativo),
        })),
        ...(f.rollout_percentual === null || f.rollout_percentual === undefined
            ? {}
            : { rolloutPercentual: Number(f.rollout_percentual) }),
        ...(f.desligada_em ? { desligadaEm: String(f.desligada_em) } : {}),
        ...(f.desligada_motivo ? { desligadaMotivo: String(f.desligada_motivo) } : {}),
        ...(f.ligadas === undefined ? {} : { ligadas: Number(f.ligadas) }),
        ...(f.total === undefined ? {} : { total: Number(f.total) }),
    };
}

function flagPorId(carga: CargaDasFlags, id: string): FlagDeModulo {
    const flag = carga.flags.find((f) => f.id === id);
    if (!flag) throw new Error(`Flag ${id} não existe.`);
    return flag;
}

function paraChamadoDaPlataforma(c: Record<string, unknown>): Chamado {
    const base = {
        id: String(c.id),
        clinicaId: String(c.clinica_id),
        clinica: String(c.clinica ?? ''),
        assunto: String(c.assunto ?? ''),
        descricao: (c.descricao as string | null) ?? '',
        canal: c.canal as Chamado['canal'],
        prioridade: c.prioridade as Chamado['prioridade'],
        situacao: c.situacao as Chamado['situacao'],
        ...(c.responsavel_nome ? { responsavelNome: String(c.responsavel_nome) } : {}),
        abertoEm: String(c.aberto_em ?? ''),
        ...(c.resolvido_em ? { resolvidoEm: String(c.resolvido_em) } : {}),
    };
    const horas = horasAteResolver(base);
    return { ...base, ...(horas === undefined ? {} : { horasAteResolver: horas }) };
}

function paraPlano(p: Record<string, unknown>): Omit<Plano, 'clinicas'> {
    return {
        id: String(p.id),
        codigo: String(p.codigo ?? ''),
        nome: String(p.nome ?? ''),
        ciclo: p.ciclo as Plano['ciclo'],
        /* Nulo vira AUSENTE, não zero: preço não decidido e plano gratuito são
           coisas diferentes, e a tela precisa distinguir as duas. */
        ...(p.preco_centavos === null || p.preco_centavos === undefined
            ? {}
            : { precoCentavos: Number(p.preco_centavos) }),
        ...(p.limite_unidades === null || p.limite_unidades === undefined
            ? {}
            : { limiteUnidades: Number(p.limite_unidades) }),
        ...(p.limite_membros === null || p.limite_membros === undefined
            ? {}
            : { limiteMembros: Number(p.limite_membros) }),
        ativo: Boolean(p.ativo),
    };
}

function paraAssinatura(a: Record<string, unknown>): AssinaturaDaClinica {
    return {
        clinicaId: String(a.clinica_id),
        clinica: String(a.clinica ?? ''),
        situacao: a.situacao as AssinaturaDaClinica['situacao'],
        plano: String(a.plano ?? ''),
        planoCodigo: String(a.plano_codigo ?? ''),
        ciclo: a.ciclo as AssinaturaDaClinica['ciclo'],
        ...(a.preco_centavos === null || a.preco_centavos === undefined
            ? {}
            : { precoCentavos: Number(a.preco_centavos) }),
        inicio: String(a.inicio ?? ''),
        ...(a.proxima_cobranca ? { proximaCobranca: String(a.proxima_cobranca) } : {}),
        ...(a.cancelada_em ? { canceladaEm: String(a.cancelada_em) } : {}),
    };
}

function paraFaturaDaPlataforma(f: Record<string, unknown>): Fatura {
    const base = {
        id: String(f.id),
        clinicaId: String(f.clinica_id),
        clinica: String(f.clinica ?? ''),
        competencia: String(f.competencia ?? ''),
        vencimento: String(f.vencimento ?? ''),
        valorCentavos: Number(f.valor_centavos ?? 0),
        ...(f.pago_em ? { pagoEm: String(f.pago_em) } : {}),
        ...(f.nota_fiscal ? { notaFiscal: String(f.nota_fiscal) } : {}),
        tipo: f.tipo === 'avulsa' ? 'avulsa' as const : 'assinatura' as const,
        ...(f.descricao ? { descricao: String(f.descricao) } : {}),
        descontoCentavos: Number(f.desconto_centavos ?? 0),
        creditoCentavos: Number(f.credito_centavos ?? 0),
        ...(f.estornada_em ? { estornadaEm: String(f.estornada_em) } : {}),
        ...(f.estorno_motivo ? { estornoMotivo: String(f.estorno_motivo) } : {}),
        ...camposDaCobranca(f),
    };
    return { ...base, situacao: situacaoDaFatura(base) };
}

/* ---- trilha T5b: leitura do painel ---- */

function paraUnidadeDoPainel(u: Record<string, unknown>): UnidadeDaClinica {
    return {
        id: String(u.id),
        nome: String(u.nome ?? ''),
        telefone: (u.telefone as string | null) ?? '',
        email: (u.email as string | null) ?? '',
        cep: (u.cep as string | null) ?? '',
        estado: (u.estado as string | null) ?? '',
        cidade: (u.cidade as string | null) ?? '',
        bairro: (u.bairro as string | null) ?? '',
        rua: (u.rua as string | null) ?? '',
        numero: (u.numero as string | null) ?? '',
        complemento: (u.complemento as string | null) ?? '',
        salas: Number(u.salas ?? 0),
    };
}

function paraCredito(c: Record<string, unknown>): CreditoDaClinica {
    return {
        id: String(c.id),
        tipo: c.tipo as CreditoDaClinica['tipo'],
        motivo: String(c.motivo ?? ''),
        valorCentavos: Number(c.valor_centavos ?? 0),
        saldoCentavos: Number(c.saldo_centavos ?? 0),
        ...(c.autor_nome ? { autorNome: String(c.autor_nome) } : {}),
        criadoEm: String(c.criado_em ?? ''),
        ...(c.excluido_em ? { excluidoEm: String(c.excluido_em) } : {}),
    };
}

function paraDesconto(d: Record<string, unknown>): DescontoDaClinica {
    return {
        id: String(d.id),
        percentual: Number(d.percentual ?? 0),
        inicioCompetencia: String(d.inicio_competencia ?? ''),
        fimCompetencia: String(d.fim_competencia ?? ''),
        meses: Number(d.meses ?? 0),
        motivo: String(d.motivo ?? ''),
        ...(d.autor_nome ? { autorNome: String(d.autor_nome) } : {}),
        criadoEm: String(d.criado_em ?? ''),
        ...(d.revogado_em ? { revogadoEm: String(d.revogado_em) } : {}),
        vigente: Boolean(d.vigente),
    };
}

function paraOperador(o: Record<string, unknown>): OperadorDaPlataforma {
    return {
        usuarioId: String(o.usuario_id),
        nome: String(o.nome ?? ''),
        email: String(o.email ?? ''),
        permissoes: ((o.permissoes ?? []) as unknown[]).map(String),
        temMfa: Boolean(o.tem_mfa),
        ...(o.ultimo_acesso ? { ultimoAcesso: String(o.ultimo_acesso) } : {}),
        souEu: Boolean(o.sou_eu),
    };
}

function paraChaveDeApi(k: Record<string, unknown>): ChaveDeApi {
    return {
        id: String(k.id),
        nome: String(k.nome ?? ''),
        prefixo: String(k.prefixo ?? ''),
        escopos: ((k.escopos ?? []) as unknown[]).map(String),
        ...(k.autor_nome ? { autorNome: String(k.autor_nome) } : {}),
        criadaEm: String(k.criada_em ?? ''),
        ...(k.ultimo_uso_em ? { ultimoUsoEm: String(k.ultimo_uso_em) } : {}),
        usos: Number(k.usos ?? 0),
        ...(k.revogada_em ? { revogadaEm: String(k.revogada_em) } : {}),
    };
}

function paraLembrete(l: Record<string, unknown>): Lembrete {
    return {
        id: String(l.id),
        quando: String(l.quando ?? ''),
        descricao: (l.descricao as string | null) ?? '',
        ativo: Boolean(l.ativo),
        canal: l.canal as Lembrete['canal'],
        offsetMin: Number(l.offset_min ?? 0),
    };
}

function paraDocumento(l: Record<string, unknown>): Documento {
    return {
        id: l.id as string,
        titulo: l.titulo as string,
        tipo: l.tipo as string,
        contatoId: (l.contato_id as string | null) ?? undefined,
        paciente: nomeEmbutido(l.contatos) ?? '—',
        modeloId: (l.modelo_id as string | null) ?? undefined,
        corpo: (l.corpo as string | null) ?? undefined,
        assinantes: (l.assinantes as string | null) ?? undefined,
        canal: (l.canal as Documento['canal'] | null) ?? undefined,
        status: l.status as Documento['status'],
        paginas: Number(l.paginas ?? 1),
        criadoEm: l.criado_em as string,
        atualizadoEm: l.atualizado_em as string,
        enviadoEm: (l.enviado_em as string | null) ?? undefined,
        assinadoEm: (l.assinado_em as string | null) ?? undefined,
        recusadoEm: (l.recusado_em as string | null) ?? undefined,
        motivoDaRecusa: (l.motivo_da_recusa as string | null) ?? undefined,
        profissional: nomeEmbutido(l.profissional),
        procedimento: nomeEmbutido(l.procedimentos),
    };
}

function paraModeloDeDocumento(l: Record<string, unknown>): ModeloDeDocumento {
    const corpo = (l.corpo as string | null) ?? '';
    return {
        id: l.id as string,
        nome: l.nome as string,
        categoria: l.categoria as ModeloDeDocumento['categoria'],
        assinantes: l.assinantes as ModeloDeDocumento['assinantes'],
        corpo,
        publicado: l.publicado as boolean,
        favorito: l.favorito as boolean,
        /* Uso é contagem do que saiu dele: contador guardado fica errado no
           primeiro documento excluído. */
        usos: ((l.documentos as { count: number }[] | null) ?? [])[0]?.count ?? 0,
        campos: contarCampos(corpo),
        atualizadoEm: l.atualizado_em as string,
    };
}

async function lerDocumento(id: string): Promise<Documento> {
    const { data, error } = await cliente().from('documentos').select(CAMPOS_DO_DOCUMENTO).eq('id', id).single();
    erro('Falha ao ler o documento', error);
    return paraDocumento(data as unknown as Record<string, unknown>);
}

async function lerModelo(id: string): Promise<ModeloDeDocumento> {
    const { data, error } = await cliente().from('documentos_modelos').select(CAMPOS_DO_MODELO).eq('id', id).single();
    erro('Falha ao ler o modelo', error);
    return paraModeloDeDocumento(data as unknown as Record<string, unknown>);
}

export const adaptadorSupabase: PortaDeDados = {
    async resumoDoDia(): Promise<ResumoDoDia> {
        const hoje = new Date().toLocaleDateString('en-CA', { timeZone: FUSO });

        const [{ data: agenda, error: erroAgenda }, { data: tarefas, error: erroTarefas }] = await Promise.all([
            cliente()
                .from('agendamentos')
                .select(CAMPOS_DO_AGENDAMENTO)
                .gte('inicio_em', `${hoje}T00:00:00-03:00`)
                .lte('inicio_em', `${hoje}T23:59:59-03:00`)
                .neq('status', 'bloqueio')
                .order('inicio_em')
                .returns<LinhaDeAgendamento[]>(),
            cliente()
                .from('tarefas')
                .select('id, titulo, vence_em, prioridade, concluida_em, membros!responsavel_id(nome)')
                .order('vence_em'),
        ]);
        erro('Falha ao ler a agenda de hoje', erroAgenda);
        erro('Falha ao ler as tarefas', erroTarefas);

        const concluidos = (agenda ?? []).filter((a) => a.status === 'concluido');
        const faturado = concluidos.reduce((s, a) => s + (a.procedimentos?.preco_centavos ?? 0), 0);

        return {
            indicadores: [
                { id: 'atendimentos-hoje', rotulo: 'Atendimentos de hoje', valor: String((agenda ?? []).length), apoio: 'na agenda', icone: 'stethoscope' },
                { id: 'concluidos', rotulo: 'Concluídos', valor: String(concluidos.length), apoio: 'até agora', icone: 'circle-check' },
                { id: 'faturado', rotulo: 'Faturado hoje', valor: `R$ ${(faturado / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, apoio: 'consultas concluídas', icone: 'circle-dollar-sign' },
                { id: 'tarefas', rotulo: 'Tarefas abertas', valor: String((tarefas ?? []).filter((t) => !t.concluida_em).length), apoio: 'follow-ups', icone: 'clipboard-list' },
            ],
            agendaDeHoje: (agenda ?? []).map(paraAgendamento),
            tarefas: (tarefas ?? []).map((t): Tarefa => ({
                id: t.id as string,
                titulo: t.titulo as string,
                venceEm: (t.vence_em as string | null) ?? hoje,
                responsavel: nomeEmbutido(t.membros) ?? '—',
                prioridade: t.prioridade as Tarefa['prioridade'],
                concluida: Boolean(t.concluida_em),
            })),
        };
    },

    async agendaDaSemana(diaDeReferencia: string): Promise<AgendaDaSemana> {
        const inicio = segundaDa(diaDeReferencia);
        const fim = somaDias(inicio, 5);

        /* A unidade da topbar recorta a semana e as salas (trilha T7). */
        const recorte = await recorteDaUnidade();
        const daSemana = cliente()
            .from('agendamentos')
            .select(CAMPOS_DO_AGENDAMENTO)
            .gte('inicio_em', `${inicio}T00:00:00-03:00`)
            .lte('inicio_em', `${fim}T23:59:59-03:00`);
        const [{ data: agenda, error: erroAgenda }, { data: profissionais }, salas, { data: jornada }] = await Promise.all([
            (recorte ? daSemana.or(filtroDosAgendamentos(recorte)) : daSemana)
                .order('inicio_em')
                .returns<LinhaDeAgendamento[]>(),
            cliente().from('membros').select('id, nome').eq('acesso', 'ativo').is('arquivado_em', null).order('nome'),
            salasParaAgendar(recorte),
            cliente().from('horarios_funcionamento').select('dia_semana, inicio, fim'),
        ]);
        erro('Falha ao ler a agenda', erroAgenda);

        const agendamentos = (agenda ?? []).map(paraAgendamento);
        return {
            inicio,
            agendamentos,
            profissionais: (profissionais ?? []).map((p) => ({ id: p.id as string, nome: p.nome as string })),
            salas,
            /* A grade sai da jornada cadastrada e alarga para caber o que foi
               marcado fora dela. Era 8h–18h fixo: a clínica que fecha às 19h
               perdia a última hora da tela. */
            ...horasDaGrade(paraJornada(jornada as unknown as LinhaDeHorario[] | null), agendamentos),
        };
    },

    async historicoDoAgendamento(id: string): Promise<RegistroDeHistorico[]> {
        const { data, error } = await cliente()
            .from('agendamentos_historico')
            .select('id, titulo, descricao, icone, tom, autor_nome, criado_em')
            .eq('agendamento_id', id)
            .order('criado_em', { ascending: false });
        erro('Falha ao ler o histórico', error);

        return (data ?? []).map((h): RegistroDeHistorico => ({
            id: h.id as string,
            titulo: h.titulo as string,
            quandoEm: h.criado_em as string,
            autor: h.autor_nome as string,
            icone: (h.icone as string) ?? 'pencil',
            tom: (h.tom as RegistroDeHistorico['tom']) ?? undefined,
            descricao: (h.descricao as string | null) ?? undefined,
        }));
    },

    async lembretesDoAgendamento(id: string): Promise<Lembrete[]> {
        /* A régua é da clínica; a exceção é do agendamento. O id era ignorado, e
           desligar um lembrete aqui só mudava a tela. */
        const ehDoBanco = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        const [regua, excecoes] = await Promise.all([
            cliente()
                .from('lembretes_regua')
                .select('id, quando, descricao, ativo, canal, offset_min')
                .order('offset_min', { ascending: false }),
            ehDoBanco
                ? cliente().from('agendamentos_lembretes').select('lembrete_id, ativo').eq('agendamento_id', id)
                : Promise.resolve({ data: [], error: null }),
        ]);
        erro('Falha ao ler a régua de lembretes', regua.error);
        erro('Falha ao ler os lembretes deste agendamento', excecoes.error);

        const porLembrete = new Map(
            ((excecoes.data ?? []) as unknown as { lembrete_id: string; ativo: boolean }[]).map((e) => [String(e.lembrete_id), Boolean(e.ativo)]),
        );
        return (regua.data ?? []).map((linha) => {
            const lembrete = paraLembrete(linha);
            const excecao = porLembrete.get(lembrete.id);
            return excecao === undefined ? lembrete : { ...lembrete, ativo: excecao };
        });
    },

    async atualizarAgendamento(id: string, mudancas: EdicaoDeAgendamento, registros: RegistroDeHistorico[]): Promise<Agendamento> {
        const clinica = clinicaId();

        /* Id quando a tela manda id; nome só na falta dele, e aí tem de bater com
           UM cadastro. O nome sem correspondência era ignorado em silêncio — e o
           histórico registrava uma troca que não tinha acontecido. */
        const [profissionalId, salaId, procedimentoId] = await Promise.all([
            mudancas.profissionalId ?? (mudancas.profissional ? idPorNome('membros', mudancas.profissional, 'profissional') : undefined),
            mudancas.salaId !== undefined ? mudancas.salaId : (mudancas.sala ? idPorNome('salas', mudancas.sala, 'sala') : undefined),
            mudancas.procedimentoId ?? (mudancas.procedimento ? idPorNome('procedimentos', mudancas.procedimento, 'procedimento') : undefined),
        ]);

        const { data, error } = await cliente()
            .from('agendamentos')
            .update({
                ...(mudancas.inicioEm ? { inicio_em: mudancas.inicioEm } : {}),
                ...(mudancas.duracaoMin ? { duracao_min: mudancas.duracaoMin } : {}),
                ...(mudancas.status ? { status: mudancas.status } : {}),
                ...(profissionalId ? { profissional_id: profissionalId } : {}),
                /* `null` é "sem sala": tirar a sala precisa chegar ao banco. */
                ...(salaId !== undefined ? { sala_id: salaId } : {}),
                ...(procedimentoId ? { procedimento_id: procedimentoId } : {}),
                ...(mudancas.observacoes !== undefined ? { observacoes: mudancas.observacoes || null } : {}),
            })
            .eq('id', id)
            .select(CAMPOS_DO_AGENDAMENTO)
            .returns<LinhaDeAgendamento[]>()
            .single();
        erro('Falha ao salvar o agendamento', error);

        if (registros.length > 0) {
            const { error: erroHistorico } = await cliente().from('agendamentos_historico').insert(
                registros.map((r) => ({
                    clinica_id: clinica,
                    agendamento_id: id,
                    titulo: r.titulo,
                    descricao: r.descricao ?? null,
                    icone: r.icone,
                    tom: r.tom ?? null,
                    autor_nome: r.autor,
                })),
            );
            erro('Alteração salva, mas o histórico falhou', erroHistorico);
        }

        return paraAgendamento(data as unknown as LinhaDeAgendamento);
    },

    async criarAgendamento(novo: NovoAgendamento): Promise<Agendamento> {
        const clinica = clinicaId();
        const recorte = await recorteDaUnidade();
        /* Tudo por id. Nome só entra quando falta o id — é o caminho da proposta
           do agente —, e aí precisa bater com UM cadastro. Gravar sem
           profissional em silêncio, ou criar um contato sem telefone para o
           homônimo digitado, era o defeito. Quem não está no cadastro é
           cadastrado antes, por quem sabe o telefone. */
        const [contatoId, procedimentoId, profissionalId, salaId] = await Promise.all([
            novo.pacienteId ?? idPorNome('contatos', novo.pacienteNome, 'paciente'),
            novo.procedimentoId ?? (novo.procedimento ? idPorNome('procedimentos', novo.procedimento, 'procedimento') : null),
            novo.profissionalId ?? (novo.profissional ? idPorNome('membros', novo.profissional, 'profissional') : null),
            novo.teleconsulta ? null : (novo.salaId ?? (novo.sala ? idPorNome('salas', novo.sala, 'sala') : null)),
        ]);

        /* Quem ainda é só lead leva o vínculo com a oportunidade aberta: é ela
           que o detalhe do agendamento oferece abrir. */
        const [{ data: paciente }, { data: lead }] = await Promise.all([
            cliente().from('pacientes').select('contato_id').eq('contato_id', contatoId).maybeSingle(),
            cliente().from('leads').select('id').eq('contato_id', contatoId)
                .not('etapa', 'in', '("ganho","perdido")')
                .order('criado_em', { ascending: false }).limit(1).maybeSingle(),
        ]);
        const leadId = !paciente && lead ? String((lead as unknown as { id: string }).id) : null;

        /* "Repetir semanalmente" prometia 4 sessões e criava uma. Uma sala de
           vídeo por sessão: reaproveitar a mesma deixaria a consulta de uma
           semana aberta para quem guardou o link da anterior. */
        const sessoes = novo.repetirSemanalmente ? 4 : 1;
        const linhas = Array.from({ length: sessoes }, (_, i) => ({
            clinica_id: clinica,
            contato_id: contatoId,
            lead_id: leadId,
            procedimento_id: procedimentoId,
            profissional_id: profissionalId,
            sala_id: salaId,
            /* Com "Todas as unidades", nula: o banco põe a da sala ou a principal (0101). */
            unidade_id: recorte?.id ?? null,
            inicio_em: `${somaDias(novo.data, i * 7)}T${novo.hora}:00-03:00`,
            duracao_min: novo.duracaoMin,
            status: novo.status ?? 'confirmado',
            teleconsulta: novo.teleconsulta,
            link_teleconsulta: novo.teleconsulta ? gerarLinkDeTeleconsulta() : null,
            observacoes: novo.observacoes ?? null,
            lembrete_whatsapp: novo.lembretePorWhatsapp,
        }));

        const { data, error } = await cliente()
            .from('agendamentos')
            .insert(linhas)
            .select(CAMPOS_DO_AGENDAMENTO)
            .returns<LinhaDeAgendamento[]>();
        erro('Falha ao criar o agendamento', error);

        const [primeiro] = [...(data ?? [])].sort((a, b) => a.inicio_em.localeCompare(b.inicio_em));
        if (!primeiro) throw new Error('O agendamento foi pedido, mas não voltou do servidor.');
        return paraAgendamento(primeiro);
    },

    async alternarTarefa(id: string, concluida: boolean): Promise<Tarefa> {
        const { data, error } = await cliente()
            .from('tarefas')
            .update({ concluida_em: concluida ? new Date().toISOString() : null })
            .eq('id', id)
            .select('id, titulo, vence_em, prioridade, concluida_em, membros!responsavel_id(nome)')
            .single();
        erro('Falha ao atualizar a tarefa', error);

        return {
            id: data?.id as string,
            titulo: data?.titulo as string,
            venceEm: (data?.vence_em as string | null) ?? '',
            responsavel: nomeEmbutido(data?.membros) ?? '—',
            prioridade: data?.prioridade as Tarefa['prioridade'],
            concluida: Boolean(data?.concluida_em),
        };
    },

    async contatos(tipo: TipoDeContato, filtro: FiltroDeContatos): Promise<CargaDeContatos> {
        const busca = filtro.busca?.trim() ?? '';
        const totais = await totaisDeContatos();

        if (tipo === 'lead') {
            const { data, error } = await lerTodasAsLinhas<LinhaDeLead>((de, ate) => cliente()
                .from('leads')
                .select('id, contato_id, origem, interesse, etapa, funil_id, etapa_id, valor_estimado_centavos, proximo_contato_em, criado_em, campanha_de_anuncio_id, atribuicao, contatos(nome, telefone), membros!responsavel_id(nome), funil_etapas(titulo), campanhas_de_anuncio(nome)')
                .order('criado_em', { ascending: false })
                .order('id')
                .range(de, ate)
                .returns<LinhaDeLead[]>());
            erro('Falha ao ler os leads', error);
            const etiquetas = await etiquetasPorContato();

            const itens: Lead[] = (data ?? []).map((l) => ({
                id: l.id,
                contatoId: l.contato_id,
                etiquetas: etiquetas.get(l.contato_id) ?? [],
                nome: l.contatos?.nome ?? '—',
                telefone: l.contatos?.telefone ?? '',
                origem: l.origem ?? '—',
                interesse: l.interesse ?? '—',
                responsavel: l.membros?.nome ?? '—',
                criadoEm: l.criado_em.slice(0, 10),
                proximoContatoEm: l.proximo_contato_em ?? undefined,
                funilId: l.funil_id,
                etapaId: l.etapa_id,
                etapa: ([] as { titulo: string }[]).concat(l.funil_etapas ?? [])[0]?.titulo ?? '—',
                situacao: l.etapa,
                valorEstimadoCentavos: l.valor_estimado_centavos,
                ...campanhaDoLead(l),
            }));
            const filtrados = busca ? itens.filter((i) => casaNaBusca(i, busca)) : itens;
            return { tipo, ...paginar(filtrados, filtro), totais };
        }

        if (tipo === 'fornecedor') {
            const { data, error } = await lerTodasAsLinhas<Record<string, unknown>>((de, ate) => cliente()
                .from('fornecedores').select(CAMPOS_DO_FORNECEDOR).order('nome').order('id').range(de, ate));
            erro('Falha ao ler os fornecedores', error);
            const itens = (data ?? []).map(paraFornecedor);
            const filtrados = busca
                ? itens.filter((i) => semAcento(i.nome).includes(semAcento(busca)) || (i.telefone ?? '').includes(busca) || (i.cnpj ?? '').includes(busca))
                : itens;
            return { tipo, ...paginar(filtrados, filtro), totais };
        }

        if (tipo === 'prestador') {
            const { data, error } = await lerTodasAsLinhas<Record<string, unknown>>((de, ate) => cliente()
                .from('prestadores_de_servico').select(CAMPOS_DO_PRESTADOR).order('nome').order('id').range(de, ate));
            erro('Falha ao ler os prestadores de serviço', error);
            const itens = (data ?? []).map(paraPrestador);
            const filtrados = busca
                ? itens.filter((i) => semAcento(i.nome).includes(semAcento(busca)) || i.telefone.includes(busca)
                    || semAcento(i.tipoServico ?? '').includes(semAcento(busca)))
                : itens;
            return { tipo, ...paginar(filtrados, filtro), totais };
        }

        if (tipo === 'profissional') {
            const { data, error } = await lerTodasAsLinhas<LinhaDeMembro>((de, ate) => cliente()
                .from('membros')
                .select(CAMPOS_DO_MEMBRO)
                .is('arquivado_em', null)
                .order('nome')
                .order('id')
                .range(de, ate)
                .returns<LinhaDeMembro[]>());
            erro('Falha ao ler a equipe', error);

            const itens: MembroDaEquipe[] = (data ?? []).map(paraMembro);
            const filtrados = busca ? itens.filter((i) => semAcento(i.nome).includes(semAcento(busca))) : itens;
            return { tipo, ...paginar(filtrados, filtro), totais };
        }

        const [{ data, error }, { data: convenios }, abertos, etiquetas] = await Promise.all([
            lerTodasAsLinhas<LinhaDePaciente>((de, ate) => cliente()
                .from('pacientes')
                .select('contato_id, nascimento, convenio_id, situacao, contatos!inner(nome, telefone, arquivado_em), profissional:membros!profissional_id(nome)')
                .is('contatos.arquivado_em', null)
                .order('contato_id')
                .range(de, ate)
                .returns<LinhaDePaciente[]>()),
            cliente().from('convenios').select('id, nome, tipo, cor').eq('ativo', true).order('nome'),
            /* O que cada paciente ainda deve, dos títulos a receber em aberto.
               Quem não tem acesso ao financeiro recebe a lista vazia da RLS — e
               vê zero, que é o que ele pode saber. */
            lerTudo<Record<string, unknown>>((de, ate) => cliente().from('titulos')
                .select('id, contato_id, valor_centavos, pagamentos(valor_centavos)')
                .eq('tipo', 'receber').in('situacao', ['aberto', 'parcial']).not('contato_id', 'is', null)
                .order('id').range(de, ate)),
            etiquetasPorContato(),
        ]);
        erro('Falha ao ler os pacientes', error);
        const emAberto = emAbertoPorContato(abertos);

        const itens: Paciente[] = (data ?? []).map((p) => ({
            id: p.contato_id,
            nome: p.contatos?.nome ?? '—',
            telefone: p.contatos?.telefone ?? '',
            nascimento: p.nascimento ?? '',
            convenioId: p.convenio_id ?? undefined,
            profissional: p.profissional?.nome ?? '—',
            situacao: p.situacao,
            emAbertoCentavos: emAberto.get(p.contato_id) ?? 0,
            etiquetas: etiquetas.get(p.contato_id) ?? [],
        })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

        const filtrados = busca ? itens.filter((i) => casaNaBusca(i, busca)) : itens;
        return {
            tipo: 'paciente',
            ...paginar(filtrados, filtro),
            totais,
            convenios: (convenios ?? []).map((c): Convenio => ({ id: c.id as string, nome: c.nome as string, tipo: c.tipo as string, cor: c.cor as string })),
        };
    },

    async criarContato(novo: NovoContato): Promise<{ id: string }> {
        const clinica = clinicaId();
        const nome = novo.nome.trim();
        if (!nome) throw new Error('O nome é obrigatório.');
        /* Fornecedor e prestador têm cadastro próprio: salvarFornecedor e salvarPrestador. */
        if (novo.tipo === 'fornecedor' || novo.tipo === 'prestador') throw new Error('Fornecedor e prestador se cadastram pelo cadastro próprio.');

        /* Profissional é membro da equipe, não contato de paciente. Criava-se um
           contato solto junto, e nenhum convite saía. O e-mail é obrigatório: é
           ele que amarra a conta que a pessoa criar a este cadastro (0007). */
        if (novo.tipo === 'profissional') {
            const email = novo.email?.trim();
            if (!email) throw new Error('O e-mail é obrigatório: é com ele que a pessoa cria a conta e entra.');
            const { data, error } = await cliente()
                .from('membros')
                .insert({
                    clinica_id: clinica, nome, telefone: novo.telefone.trim() || null, email,
                    papel: 'profissional', acesso: 'convite-pendente',
                    especialidades: novo.especialidades?.trim() || null,
                    registro_conselho: novo.registro?.trim() || null,
                })
                .select('id')
                .single();
            if (error?.code === '23505') throw new Error(`Já existe alguém na equipe com o e-mail ${email}.`);
            erro('Não foi possível cadastrar o profissional', error);
            return { id: String((data as unknown as { id: string }).id) };
        }

        const { data, error } = await cliente()
            .from('contatos')
            .insert({ clinica_id: clinica, nome, telefone: novo.telefone.trim(), email: novo.email?.trim() || null })
            .select('id')
            .single();
        erro('Falha ao criar o contato', error);
        const id = String((data as unknown as { id: string }).id);

        /* Contato e ficha nascem juntos ou não nascem: sem desfazer, a falha na
           segunda gravação deixava um contato órfão na lista. */
        const desfazer = async () => { await cliente().from('contatos').delete().eq('id', id); };

        if (novo.tipo === 'paciente') {
            const { error: erroPaciente } = await cliente().from('pacientes').insert({
                contato_id: id, clinica_id: clinica,
                cpf: novo.cpf?.trim() || null, nascimento: novo.nascimento || null,
                profissional_id: novo.profissionalId ?? null,
                ...(novo.sexo ? { sexo: novo.sexo } : {}),
                ...(novo.cep ? { cep: novo.cep } : {}),
                ...(novo.rua ? { rua: novo.rua } : {}),
                ...(novo.cidade ? { cidade: novo.cidade } : {}),
                ...(novo.observacoes ? { observacoes: novo.observacoes } : {}),
            });
            if (erroPaciente) {
                await desfazer();
                if (erroPaciente.code === '23505') throw new Error(`Já existe paciente com o CPF ${novo.cpf ?? ''}.`);
                erro('Não foi possível criar a ficha do paciente', erroPaciente);
            }
        } else {
            const { error: erroLead } = await cliente().from('leads').insert({
                clinica_id: clinica, contato_id: id,
                origem: novo.origem?.trim() || 'Cadastro manual',
                interesse: novo.interesse?.trim() || null,
                responsavel_id: novo.profissionalId ?? null,
                valor_estimado_centavos: novo.valorEstimadoCentavos ?? 0,
                /* Sem etapa: o gatilho da 0100 põe na primeira aberta do pipeline padrão. */
            });
            if (erroLead) {
                await desfazer();
                erro('Não foi possível criar o lead', erroLead);
            }
        }

        return { id };
    },

    async atualizarLead(id: string, mudancas: EdicaoDeLead): Promise<Lead> {
        /* Nome e telefone moram no CONTATO, não no lead. O adaptador descartava os
           dois — e o responsável — e a tela dizia "Lead atualizado". */
        if (mudancas.nome !== undefined || mudancas.telefone !== undefined) {
            if (mudancas.nome !== undefined && !mudancas.nome.trim()) throw new Error('O nome não pode ficar vazio.');
            const { data: linha, error: erroLeitura } = await cliente().from('leads').select('contato_id').eq('id', id).single();
            erro('Falha ao ler o lead', erroLeitura);
            const { error: erroContato } = await cliente()
                .from('contatos')
                .update({
                    ...(mudancas.nome !== undefined ? { nome: mudancas.nome.trim() } : {}),
                    ...(mudancas.telefone !== undefined ? { telefone: mudancas.telefone.trim() } : {}),
                })
                .eq('id', String((linha as unknown as { contato_id: string }).contato_id));
            erro('Falha ao salvar nome e telefone', erroContato);
        }

        const campos = {
            ...(mudancas.origem ? { origem: mudancas.origem } : {}),
            ...(mudancas.interesse ? { interesse: mudancas.interesse } : {}),
            ...(mudancas.etapaId ? { etapa_id: mudancas.etapaId } : {}),
            /* A final de ganho ou de perda do pipeline do lead: o gatilho da 0100
               acha a etapa pelo texto. */
            ...(mudancas.situacao ? { etapa: mudancas.situacao } : {}),
            ...(mudancas.valorEstimadoCentavos !== undefined ? { valor_estimado_centavos: mudancas.valorEstimadoCentavos } : {}),
            ...(mudancas.proximoContatoEm !== undefined ? { proximo_contato_em: mudancas.proximoContatoEm || null } : {}),
            ...(mudancas.responsavelId !== undefined ? { responsavel_id: mudancas.responsavelId || null } : {}),
            ...(mudancas.campanhaDeAnuncioId !== undefined ? { campanha_de_anuncio_id: mudancas.campanhaDeAnuncioId || null } : {}),
        };
        const selecao = 'id, contato_id, origem, interesse, etapa, funil_id, etapa_id, valor_estimado_centavos, proximo_contato_em, criado_em, campanha_de_anuncio_id, atribuicao, contatos(nome, telefone), membros!responsavel_id(nome), funil_etapas(titulo), campanhas_de_anuncio(nome)';
        const { data, error } = Object.keys(campos).length > 0
            ? await cliente().from('leads').update(campos).eq('id', id).select(selecao).returns<LinhaDeLead[]>().single()
            : await cliente().from('leads').select(selecao).eq('id', id).returns<LinhaDeLead[]>().single();
        erro('Falha ao salvar o lead', error);

        const l = data as unknown as LinhaDeLead;
        /* A etiqueta é da pessoa, não da negociação: salvar o lead não a muda, mas a
           linha devolvida alimenta a tela e não pode voltar sem ela. */
        const etiquetas = await etiquetasPorContato();
        return {
            id: l.id,
            contatoId: l.contato_id,
            etiquetas: etiquetas.get(l.contato_id) ?? [],
            nome: l.contatos?.nome ?? '—',
            telefone: l.contatos?.telefone ?? '',
            origem: l.origem ?? '—',
            interesse: l.interesse ?? '—',
            responsavel: l.membros?.nome ?? '—',
            criadoEm: l.criado_em.slice(0, 10),
            proximoContatoEm: l.proximo_contato_em ?? undefined,
            funilId: l.funil_id,
            etapaId: l.etapa_id,
            etapa: ([] as { titulo: string }[]).concat(l.funil_etapas ?? [])[0]?.titulo ?? '—',
            situacao: l.etapa,
            valorEstimadoCentavos: l.valor_estimado_centavos,
            ...campanhaDoLead(l),
        };
    },

    async avancarEtapaDoLead(id: string): Promise<Lead> {
        const { data: atualLead, error: erroLeitura } = await cliente().from('leads').select('funil_id, etapa_id').eq('id', id).maybeSingle();
        erro('Falha ao ler o lead', erroLeitura);
        const linha = atualLead as { funil_id: string; etapa_id: string } | null;
        const funil = linha ? (await this.funis()).find((f) => f.id === linha.funil_id) : undefined;
        const proxima = funil && linha ? proximaEtapa(funil, linha.etapa_id) : undefined;
        if (!proxima) throw new Error('Este lead não tem próxima etapa.');
        return this.atualizarLead(id, { etapaId: proxima.id });
    },

    async atualizarMembro(id: string, mudancas: EdicaoDeMembro): Promise<MembroDaEquipe> {
        const { data, error } = await cliente()
            .from('membros')
            .update({
                ...(mudancas.nome ? { nome: mudancas.nome } : {}),
                /* Texto vazio APAGA: antes era ignorado, e o telefone não saía mais. */
                ...(mudancas.telefone !== undefined ? { telefone: mudancas.telefone.trim() || null } : {}),
                ...(mudancas.papel ? { papel: mudancas.papel } : {}),
                ...(mudancas.registro !== undefined ? { registro_conselho: mudancas.registro.trim() || null } : {}),
                ...(mudancas.especialidades !== undefined
                    ? { especialidades: mudancas.especialidades.trim() && mudancas.especialidades.trim() !== '—' ? mudancas.especialidades.trim() : null }
                    : {}),
                ...(mudancas.acesso ? { acesso: mudancas.acesso } : {}),
                /* Endereço (0163): as sete partes seguem a regra do telefone —
                   texto vazio apaga a coluna, em vez de deixar o antigo lá. */
                ...parteDoEndereco(mudancas),
            })
            .eq('id', id)
            .select(CAMPOS_DO_MEMBRO)
            .returns<LinhaDeMembro[]>()
            .single();
        erro('Falha ao salvar o profissional', error);

        return paraMembro(data as unknown as LinhaDeMembro);
    },

    async atendimentoEmCurso(id: string): Promise<AtendimentoEmCurso> {
        /* O id pode ser o do atendimento (quando ele já existe) ou o derivado
           do agendamento, `at-<id>`, que é como a lista de Consultas monta a
           linha de uma consulta que ainda não foi aberta. */
        const derivado = id.startsWith('at-');
        const chave = derivado ? id.slice(3) : id;

        /* Filtrar por coluna de recurso EMBUTIDO não recorta o pai no PostgREST:
           sem `!inner`, `atendimentos.id=eq.X` devolvia a agenda inteira e o
           `maybeSingle` estourava com "multiple rows". */
        const { data, error } = await cliente()
            .from('agendamentos')
            .select(
                `${CAMPOS_DO_AGENDAMENTO}, pacientes:contatos(pacientes(nascimento, alergias)),`
                + ` atendimentos${derivado ? '' : '!inner'}(id, situacao, iniciado_em, encerrado_em)`,
            )
            .eq(derivado ? 'id' : 'atendimentos.id', chave)
            .maybeSingle();
        erro('Falha ao ler a consulta', error);
        if (!data) throw new Response('Consulta não encontrada.', { status: 404, statusText: 'Consulta não encontrada' });

        const linha = data as unknown as LinhaDeAgendamento & {
            pacientes: { pacientes: { nascimento: string | null; alergias: string | null }[] | null } | null;
            atendimentos: { id: string; situacao: SituacaoDoAtendimento; iniciado_em: string | null; encerrado_em: string | null } | null;
        };
        const agendamento = paraAgendamento(linha);
        const atendimento = Array.isArray(linha.atendimentos) ? linha.atendimentos[0] : linha.atendimentos;
        const ficha = linha.pacientes?.pacientes?.[0];
        const prontuario = await this.prontuario(agendamento.pacienteId);
        const idReal = atendimento?.id ?? id;

        return {
            id: idReal,
            agendamentoId: linha.id,
            contatoId: agendamento.pacienteId,
            paciente: agendamento.paciente,
            nascimento: ficha?.nascimento ?? undefined,
            procedimento: agendamento.procedimento,
            profissional: agendamento.profissional,
            sala: agendamento.sala,
            previstoPara: agendamento.inicioEm,
            duracaoPrevistaMin: agendamento.duracaoMin,
            situacao: atendimento?.situacao ?? (agendamento.status === 'concluido' ? 'concluido' : 'aguardando'),
            iniciadoEm: atendimento?.iniciado_em ?? undefined,
            encerradoEm: atendimento?.encerrado_em ?? undefined,
            alergias: ficha?.alergias ?? undefined,
            fichasDaConsulta: prontuario.fichas.filter((f) => f.atendimentoId === idReal),
            modelos: prontuario.modelos,
            historico: prontuario.fichas.filter((f) => f.atendimentoId !== idReal),
        };
    },

    async iniciarAtendimento(id: string): Promise<AtendimentoEmCurso> {
        const clinica = clinicaId();
        const consulta = await this.atendimentoEmCurso(id);
        const agora = new Date().toISOString();

        if (consulta.id.startsWith('at-')) {
            /* Abrir a consulta é quando o atendimento passa a existir de fato. */
            const { data, error } = await cliente()
                .from('atendimentos')
                .insert({
                    clinica_id: clinica, agendamento_id: consulta.agendamentoId, contato_id: consulta.contatoId,
                    situacao: 'em-atendimento', iniciado_em: agora,
                })
                .select('id')
                .single();
            erro('Falha ao abrir a consulta', error);
            return this.atendimentoEmCurso(data?.id as string);
        }

        const { error } = await cliente()
            .from('atendimentos')
            .update({ situacao: 'em-atendimento', iniciado_em: consulta.iniciadoEm ?? agora })
            .eq('id', consulta.id);
        erro('Falha ao abrir a consulta', error);
        return this.atendimentoEmCurso(consulta.id);
    },

    async abrirConsultaPresencial(pacienteId: string): Promise<string> {
        const { data, error } = await cliente().rpc('abrir_consulta_presencial', { p_contato: pacienteId });
        /* A regra (só paciente, só quem atende) fala com quem está na tela: a frase é do banco. */
        if (error && (error.code === '42501' || error.code === 'P0001' || error.code === '22023')) throw new Error(error.message);
        erro('Falha ao abrir a consulta', error);
        return String(data);
    },

    async encerrarAtendimento(id: string): Promise<AtendimentoEmCurso> {
        const consulta = await this.atendimentoEmCurso(id);
        if (!consulta.iniciadoEm) throw new Error('A consulta ainda não foi aberta.');

        const { error } = await cliente()
            .from('atendimentos')
            .update({ situacao: 'concluido', encerrado_em: new Date().toISOString() })
            .eq('id', consulta.id);
        erro('Falha ao encerrar a consulta', error);

        /* Encerrar a consulta conclui o agendamento: são o mesmo fato visto de
           duas telas, e deixá-los divergir foi o defeito do protótipo. */
        if (consulta.agendamentoId) {
            await cliente().from('agendamentos').update({ status: 'concluido' }).eq('id', consulta.agendamentoId);
        }
        return this.atendimentoEmCurso(consulta.id);
    },

    async resumoDoPaciente(contatoId: string): Promise<ResumoDoPaciente | null> {
        const { data, error } = await cliente().rpc('resumo_do_paciente', { p_contato: contatoId });
        erro('Falha ao ler o resumo do paciente', error);
        const r = ((data ?? []) as Record<string, unknown>[])[0];
        if (!r) return null;
        return {
            texto: typeof r.texto === 'string' && r.texto ? r.texto : null,
            consultas: Number(r.consultas ?? 0),
            ateEm: typeof r.ate_em === 'string' ? r.ate_em : null,
            desatualizado: r.desatualizado === true,
            geradoEm: typeof r.gerado_em === 'string' ? r.gerado_em : null,
        };
    },

    async gerarResumoDoPaciente(contatoId: string): Promise<ResumoDoPaciente> {
        const { data, error } = await cliente().functions.invoke('nota-ia/resumo-do-paciente', {
            body: { contato: contatoId, clinica: clinicaId() },
        });
        if (error) throw new Error(await mensagemDaBordaDoResumo(error));
        const r = (data ?? {}) as Record<string, unknown>;
        if (typeof r.erro === 'string') throw new Error(r.erro);
        return {
            texto: typeof r.texto === 'string' && r.texto ? r.texto : null,
            consultas: Number(r.consultas ?? 0),
            ateEm: typeof r.ateEm === 'string' ? r.ateEm : null,
            desatualizado: r.desatualizado === true,
            geradoEm: new Date().toISOString(),
        };
    },

    async prontuario(contatoId: string): Promise<ProntuarioDoPaciente> {
        const [fichasResp, modelosResp, agendaResp] = await Promise.all([
            cliente()
                .from('fichas_preenchidas')
                .select('id, atendimento_id, contato_id, modelo_id, nome, respostas, definicao_usada, versao_do_modelo, criado_em, atualizado_em, membros!preenchida_por(nome)')
                .eq('contato_id', contatoId)
                .order('criado_em', { ascending: false }),
            cliente().from('fichas_modelos').select('id, nome, definicao, versao, fichas_preenchidas(id)').eq('ativo', true).is('arquivado_em', null).order('nome'),
            cliente()
                .from('agendamentos')
                .select('id, inicio_em, procedimentos(nome), atendimentos(id)')
                .eq('contato_id', contatoId)
                .neq('status', 'bloqueio')
                .order('inicio_em', { ascending: false }),
            /* Ler prontuário é evento de auditoria (regra 7). Se o registro falhar,
               a leitura falha junto: prontuário aberto sem rastro é o defeito que
               isto conserta. */
            registrarAcessoAoProntuario((args) => cliente().rpc('registrar_acesso_ao_prontuario', args), contatoId, 'prontuario'),
        ]);
        erro('Falha ao ler o prontuário', fichasResp.error);
        erro('Falha ao ler os modelos', modelosResp.error);

        return {
            fichas: (fichasResp.data ?? []).map((f): FichaPreenchida => ({
                id: f.id as string,
                atendimentoId: f.atendimento_id as string,
                contatoId: f.contato_id as string,
                modeloId: (f.modelo_id as string | null) ?? undefined,
                nome: f.nome as string,
                respostas: (f.respostas as Record<string, RespostaDaFicha> | null) ?? {},
                definicaoUsada: (f.definicao_usada as CampoDeFicha[] | null) ?? undefined,
                versaoDoModelo: (f.versao_do_modelo as number | null) ?? undefined,
                preenchidaPor: nomeEmbutido(f.membros),
                criadoEm: f.criado_em as string,
                atualizadoEm: f.atualizado_em as string,
            })),
            modelos: (modelosResp.data ?? []).map(paraModelo),
            atendimentos: (agendaResp.data ?? []).map((a) => {
                const atendimento = (Array.isArray(a.atendimentos) ? a.atendimentos[0] : a.atendimentos) as { id: string } | null;
                return {
                    /* A ficha pertence ao ATENDIMENTO. Quando o agendamento
                       ainda não virou atendimento, é o id dele que vai — e o
                       registro nasce junto no salvamento. */
                    id: atendimento?.id ?? `agendamento:${a.id as string}`,
                    rotulo: `${new Date(a.inicio_em as string).toLocaleDateString('pt-BR', { timeZone: FUSO })} · ${nomeEmbutido(a.procedimentos) ?? 'Consulta'}`,
                };
            }),
        };
    },

    async modelosDeFicha(): Promise<ModeloDeFicha[]> {
        const { data, error } = await cliente()
            .from('fichas_modelos')
            .select('id, nome, definicao, versao, fichas_preenchidas(id)')
            .eq('ativo', true)
            .is('arquivado_em', null)
            .order('nome');
        erro('Falha ao ler os modelos', error);
        return (data ?? []).map(paraModelo);
    },

    async salvarModeloDeFicha(modelo: { id?: string; nome: string; campos: CampoDeFicha[] }): Promise<ModeloDeFicha> {
        const clinica = clinicaId();
        if (!modelo.nome.trim()) throw new Error('Dê um nome ao modelo.');
        if (modelo.campos.length === 0) throw new Error('Um modelo sem campo nenhum não coleta nada.');

        /* A versão sobe no banco, por gatilho, e só quando os campos mudam:
           contar à mão é o passo que se esquece, e aí duas fichas diferentes
           dizem ser da mesma versão. */
        const { data, error } = modelo.id
            ? await cliente()
                .from('fichas_modelos')
                .update({ nome: modelo.nome.trim(), definicao: modelo.campos })
                .eq('id', modelo.id)
                .select('id, nome, definicao, versao, fichas_preenchidas(id)')
                .single()
            : await cliente()
                .from('fichas_modelos')
                .insert({ clinica_id: clinica, nome: modelo.nome.trim(), definicao: modelo.campos })
                .select('id, nome, definicao, versao, fichas_preenchidas(id)')
                .single();
        erro('Falha ao salvar o modelo', error);
        return paraModelo(data as Record<string, unknown>);
    },

    async arquivarModeloDeFicha(id: string): Promise<void> {
        /* Arquivar, não apagar: as fichas preenchidas apontam para ele, e o
           prontuário não pode perder a referência do que foi usado. */
        const { error } = await cliente().from('fichas_modelos').update({ arquivado_em: new Date().toISOString() }).eq('id', id);
        erro('Falha ao arquivar o modelo', error);
    },

    async enviarAnexoDaFicha(contatoId: string, uid: string, arquivo: File): Promise<ArquivoDaFicha> {
        /* Os bytes, e não o `File`: o cliente monta o corpo de formas diferentes
           conforme o ambiente, e com `File` a subida trava fora do navegador — o
           que deixaria esta função sem teste justamente onde ela grava dado de
           paciente. O tipo vai explícito porque o buffer não o carrega. */
        const bytes = await arquivo.arrayBuffer();
        /* O caminho sai do conteúdo e começa pela clínica (ver
           `anexoResiliente.ts`): reenviar o mesmo arquivo cai no mesmo lugar, e
           um 504 com o arquivo já gravado não vira erro na tela. */
        const caminho = caminhoDoAnexo(clinicaId(), contatoId, uid, arquivo.name, bytes);
        const balde = cliente().storage.from('fichas') as unknown as BaldeDeAnexos;
        try {
            await enviarSemDuplicar(balde, caminho, bytes, arquivo.type);
        } catch (e) {
            throw new Error(`Não foi possível enviar ${arquivo.name}: ${e instanceof Error ? e.message : String(e)}`);
        }

        return { nome: arquivo.name, caminho, tipo: arquivo.type, bytes: arquivo.size };
    },

    async enderecoDoAnexo(caminho: string): Promise<string> {
        /* Assinada e curta. Link permanente para foto de paciente vaza para
           sempre: basta alguém encaminhar o endereço uma vez. */
        /* Abrir anexo de prontuário também é leitura auditada — antes de o
           endereço existir. */
        await registrarAcessoAoProntuario((args) => cliente().rpc('registrar_acesso_ao_prontuario', args), contatoDoCaminhoDoAnexo(caminho), 'anexo', caminho);
        const { data, error } = await cliente().storage.from('fichas').createSignedUrl(caminho, 300);
        if (error) throw new Error('Não foi possível abrir o anexo.');
        return data?.signedUrl ?? '';
    },

    async salvarFicha(nova: NovaFicha): Promise<FichaPreenchida> {
        const clinica = clinicaId();
        const { data: modelo } = await cliente().from('fichas_modelos').select('nome, definicao, versao').eq('id', nova.modeloId).maybeSingle();
        if (!modelo) throw new Error('Modelo de ficha não encontrado.');

        const campos = ((modelo.definicao as CampoDeFicha[] | null) ?? []);
        const faltando = campos.filter((c) => c.obrigatorio && !nova.respostas[c.uid]);
        if (faltando.length > 0) throw new Error(`Preencha: ${faltando.map((c) => c.rotulo).join(', ')}.`);

        /* Agendamento que ainda não virou atendimento vira agora: a ficha não
           pode ficar órfã esperando alguém "abrir" a consulta. */
        let atendimentoId = nova.atendimentoId;
        if (atendimentoId.startsWith('agendamento:')) {
            const agendamentoId = atendimentoId.slice('agendamento:'.length);
            const { data: criado, error: erroAtendimento } = await cliente()
                .from('atendimentos')
                .insert({ clinica_id: clinica, agendamento_id: agendamentoId, contato_id: nova.contatoId, situacao: 'em-atendimento', iniciado_em: new Date().toISOString() })
                .select('id')
                .single();
            erro('Falha ao abrir o atendimento', erroAtendimento);
            atendimentoId = criado?.id as string;
        }

        const { data: membro } = await cliente().from('membros').select('id').eq('email', atual()?.email ?? '').maybeSingle();
        const { data, error } = await cliente()
            .from('fichas_preenchidas')
            .insert({
                clinica_id: clinica, atendimento_id: atendimentoId, contato_id: nova.contatoId,
                modelo_id: nova.modeloId, nome: modelo.nome as string, respostas: nova.respostas,
                /* A cópia do modelo vai junto com a resposta. */
                definicao_usada: campos, versao_do_modelo: modelo.versao as number,
                preenchida_por: membro?.id ?? null,
            })
            .select('id, atendimento_id, contato_id, modelo_id, nome, respostas, criado_em, atualizado_em')
            .single();
        erro('Falha ao salvar a ficha', error);

        return {
            id: data?.id as string,
            atendimentoId,
            contatoId: nova.contatoId,
            modeloId: nova.modeloId,
            nome: modelo.nome as string,
            respostas: nova.respostas,
            definicaoUsada: campos,
            versaoDoModelo: modelo.versao as number,
            preenchidaPor: atual()?.nome,
            criadoEm: (data?.criado_em as string) ?? new Date().toISOString(),
            atualizadoEm: (data?.atualizado_em as string) ?? new Date().toISOString(),
        };
    },

    async financeiro(filtro: FiltroDoFinanceiro): Promise<CargaDoFinanceiro> {
        const hoje = new Date().toLocaleDateString('en-CA', { timeZone: FUSO });
        const mes = filtro.mes ?? hoje.slice(0, 7);
        const ultimoDia = ultimoDiaDoMes(`${mes}-01`);
        /* No financeiro, quem tem unidade é a conta; título e pagamento não têm (trilha T7). */
        const recorte = await recorteDaUnidade();
        const contasAtivas = cliente().from('contas_financeiras').select('id, nome, tipo, banco, saldo_inicial_centavos').eq('ativa', true);

        /* Um contato ou um título são pedidos ao servidor: a ficha do paciente e
           a releitura da baixa procuravam numa página de 500 da clínica inteira.
           Uma consulta nova por faixa: o construtor do supabase-js acumula
           `order` a cada chamada, e reaproveitá-lo repetiria a ordenação. */
        const consultaDeTitulos = () => {
            let consulta = cliente()
                .from('titulos')
                .select('id, tipo, descricao, contato_id, fornecedor, categoria_id, valor_centavos, vencimento, competencia, parcela, parcelas_total, situacao, contatos(nome), categorias_financeiras(nome), pagamentos(valor_centavos)')
                .neq('situacao', 'cancelado');
            if (filtro.contatoId) consulta = consulta.eq('contato_id', filtro.contatoId);
            if (filtro.tituloId) consulta = consulta.eq('id', filtro.tituloId);
            return consulta;
        };

        const [titulosResp, pagamentosResp, contasResp, categoriasResp, metodosResp] = await Promise.all([
            lerTodasAsLinhas((de, ate) => consultaDeTitulos().order('vencimento').order('id').range(de, ate)),
            lerTodasAsLinhas((de, ate) => cliente()
                .from('pagamentos')
                .select('id, titulo_id, valor_centavos, taxa_centavos, pago_em, liquidado_em, contas_financeiras(nome), metodos_pagamento(nome), titulos(tipo, descricao, contato_id, fornecedor, contatos(nome))')
                .order('pago_em', { ascending: false })
                .order('id')
                .range(de, ate)),
            (recorte ? contasAtivas.or(filtroDaUnidade(recorte)) : contasAtivas).order('nome'),
            /* Sem filtro de `ativa`: o cadastro precisa mostrar a categoria
               inativada, senão ela some da tela e ninguém a reativa. Quem só
               quer escolher categoria usa a lista filtrada abaixo. */
            cliente().from('categorias_financeiras').select('id, nome, natureza, pai_id, ativa, titulos(count)').order('nome'),
            cliente().from('metodos_pagamento').select('id, nome, tipo, taxa_percentual, prazo_dias, contas_financeiras(nome)').eq('ativo', true).order('nome'),
        ]);
        erro('Falha ao ler os títulos', titulosResp.error);
        erro('Falha ao ler os pagamentos', pagamentosResp.error);

        const todos: Titulo[] = (titulosResp.data ?? []).map((t) => ({
            id: t.id as string,
            tipo: t.tipo as Titulo['tipo'],
            descricao: t.descricao as string,
            contraparte: nomeEmbutido(t.contatos) ?? (t.fornecedor as string | null) ?? '—',
            contatoId: (t.contato_id as string | null) ?? undefined,
            categoria: nomeEmbutido(t.categorias_financeiras),
            categoriaId: (t.categoria_id as string | null) ?? undefined,
            valorCentavos: t.valor_centavos as number,
            pagoCentavos: ((t.pagamentos as { valor_centavos: number }[] | null) ?? []).reduce((s, p) => s + p.valor_centavos, 0),
            vencimento: t.vencimento as string,
            competencia: t.competencia as string,
            parcela: t.parcela as number,
            parcelasTotal: t.parcelas_total as number,
            situacao: t.situacao as Titulo['situacao'],
        }));

        const movimentos: Movimentacao[] = (pagamentosResp.data ?? []).map((p) => {
            const titulo = (Array.isArray(p.titulos) ? p.titulos[0] : p.titulos) as unknown as {
                tipo: Titulo['tipo']; descricao: string; fornecedor: string | null; contatos: unknown;
            } | null;
            const valor = p.valor_centavos as number;
            const taxa = p.taxa_centavos as number;
            return {
                id: p.id as string,
                tituloId: p.titulo_id as string,
                tipo: titulo?.tipo ?? 'receber',
                descricao: titulo?.descricao ?? '—',
                contraparte: nomeEmbutido(titulo?.contatos) ?? titulo?.fornecedor ?? '—',
                valorCentavos: valor,
                taxaCentavos: taxa,
                liquidoCentavos: valor - taxa,
                pagoEm: p.pago_em as string,
                liquidadoEm: (p.liquidado_em as string | null) ?? undefined,
                conta: nomeEmbutido(p.contas_financeiras),
                metodo: nomeEmbutido(p.metodos_pagamento),
            };
        });

        const busca = filtro.busca?.trim() ?? '';
        const combinaTitulo = (t: Titulo) => !busca || semAcento(t.descricao).includes(semAcento(busca)) || semAcento(t.contraparte).includes(semAcento(busca));
        const emAberto = (t: Titulo) => t.situacao === 'aberto' || t.situacao === 'parcial';

        const receber = todos.filter((t) => t.tipo === 'receber' && emAberto(t));
        const pagar = todos.filter((t) => t.tipo === 'pagar' && emAberto(t));
        const vencidos = [...receber, ...pagar].filter((t) => t.vencimento < hoje);

        const lista = filtro.aba === 'pagar' ? pagar.filter(combinaTitulo)
            : filtro.aba === 'receber' ? receber.filter(combinaTitulo)
                : todos.filter(combinaTitulo);
        const doMes = movimentos.filter((m) => m.pagoEm >= `${mes}-01` && m.pagoEm <= ultimoDia);

        const paginaDeTitulos = paginar(lista, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const paginaDeMovimentos = paginar(doMes, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const pagina = filtro.aba === 'extrato' ? paginaDeMovimentos : paginaDeTitulos;

        const meses = janelaDeMeses(mes, 6);
        const linhasDeCategoria = (categoriasResp.data ?? []);
        const nomeDaCategoria = new Map(linhasDeCategoria.map((c) => [c.id as string, c.nome as string]));
        const categoriasDeConta: CategoriaDeConta[] = linhasDeCategoria.map((c) => {
            const paiId = (c.pai_id as string | null) ?? undefined;
            return {
                id: c.id as string,
                nome: c.nome as string,
                natureza: c.natureza as CategoriaFinanceira['natureza'],
                paiId,
                grupo: paiId ? nomeDaCategoria.get(paiId) : undefined,
                lancamentos: ((c.titulos as { count: number }[] | null) ?? [])[0]?.count ?? 0,
                ativa: c.ativa as boolean,
            };
        });

        const recebidoNoMes = doMes.filter((m) => m.tipo === 'receber').reduce((s, m) => s + m.liquidoCentavos, 0);
        const pagoNoMes = doMes.filter((m) => m.tipo === 'pagar').reduce((s, m) => s + m.valorCentavos, 0);
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

        return {
            aba: filtro.aba,
            titulos: filtro.aba === 'extrato' ? [] : paginaDeTitulos.itens,
            movimentacoes: filtro.aba === 'extrato' ? paginaDeMovimentos.itens : doMes,
            contas: (contasResp.data ?? []).map((c): ContaFinanceira => {
                const nome = c.nome as string;
                /* Saldo é o inicial mais o que já LIQUIDOU nela: crédito em
                   trinta dias não é dinheiro em caixa hoje. */
                const liquidado = movimentos
                    .filter((m) => m.conta === nome && m.liquidadoEm && m.liquidadoEm <= hoje)
                    .reduce((s, m) => s + (m.tipo === 'receber' ? m.liquidoCentavos : -m.valorCentavos), 0);
                return {
                    id: c.id as string,
                    nome,
                    tipo: c.tipo as ContaFinanceira['tipo'],
                    banco: (c.banco as string | null) ?? undefined,
                    saldoCentavos: (c.saldo_inicial_centavos as number) + liquidado,
                };
            }),
            categorias: categoriasDeConta.filter((c) => c.ativa).map((c): CategoriaFinanceira => ({ id: c.id, nome: c.nome, natureza: c.natureza })),
            metodos: (metodosResp.data ?? []).map((m): MetodoDePagamento => ({
                id: m.id as string,
                nome: m.nome as string,
                tipo: m.tipo as string,
                taxaPercentual: Number(m.taxa_percentual ?? 0),
                prazoDias: m.prazo_dias as number,
                conta: nomeEmbutido(m.contas_financeiras),
            })),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'receber', rotulo: 'A receber', valor: reais(receber.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0)), apoio: `${receber.length} títulos`, icone: 'circle-dollar-sign' },
                { id: 'pagar', rotulo: 'A pagar', valor: reais(pagar.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0)), apoio: `${pagar.length} títulos`, icone: 'receipt' },
                { id: 'vencidos', rotulo: 'Vencidos', valor: String(vencidos.length), apoio: reais(vencidos.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0)), tendencia: vencidos.length ? 'down' : 'neutral', icone: 'triangle-alert' },
                { id: 'resultado', rotulo: 'Resultado do mês', valor: reais(recebidoNoMes - pagoNoMes), apoio: 'recebido menos pago', tendencia: recebidoNoMes >= pagoNoMes ? 'up' : 'down', icone: 'chart-no-axes-column' },
            ],
            totais: { receber: receber.length, pagar: pagar.length, vencidos: vencidos.length },
            competencia: relatorioDeCompetencia(todos, movimentos, meses),
            fluxo: filtro.aba === 'fluxo-diario' ? fluxoDiario(movimentos, mes) : fluxoMensal(movimentos, meses),
            categoriasDeConta,
            porCategoria: totaisPorCategoria(todos, mes),
        };
    },

    async salvarCategoriaDeConta(categoria: { id?: string; nome: string; natureza: 'receita' | 'despesa'; paiId?: string }): Promise<CategoriaDeConta> {
        const clinica = clinicaId();
        const nome = categoria.nome.trim();
        if (!nome) throw new Error('Dê um nome à categoria.');

        const corpo = { nome, natureza: categoria.natureza, pai_id: categoria.paiId ?? null };
        const { data, error } = categoria.id
            ? await cliente().from('categorias_financeiras').update(corpo).eq('id', categoria.id)
                .select('id, nome, natureza, pai_id, ativa, titulos(count)').single()
            : await cliente().from('categorias_financeiras').insert({ clinica_id: clinica, ...corpo })
                .select('id, nome, natureza, pai_id, ativa, titulos(count)').single();

        /* `unique (clinica_id, nome, natureza)` no banco: duas categorias com o
           mesmo nome partiriam o valor do relatório em duas linhas. */
        if (error?.code === '23505') throw new Error(`Já existe uma categoria de ${categoria.natureza} chamada ${nome}.`);
        erro('Falha ao salvar a categoria', error);

        const linha = data as Record<string, unknown>;
        const paiId = (linha.pai_id as string | null) ?? undefined;
        return {
            id: linha.id as string,
            nome: linha.nome as string,
            natureza: linha.natureza as CategoriaFinanceira['natureza'],
            paiId,
            lancamentos: ((linha.titulos as { count: number }[] | null) ?? [])[0]?.count ?? 0,
            ativa: linha.ativa as boolean,
        };
    },

    async inativarCategoriaDeConta(id: string, ativa: boolean): Promise<void> {
        /* Inativar, nunca apagar: os títulos já lançados apontam para ela, e o
           relatório de um mês fechado não pode perder a classificação. */
        const { error } = await cliente().from('categorias_financeiras').update({ ativa }).eq('id', id);
        erro('Falha ao mudar a situação da categoria', error);
    },

    async registrarPagamento(dados: NovoPagamento): Promise<Titulo> {
        const clinica = clinicaId();
        const { data: titulo, error: erroTitulo } = await cliente()
            .from('titulos')
            .select('id, valor_centavos, pagamentos(valor_centavos)')
            .eq('id', dados.tituloId)
            .single();
        erro('Falha ao ler o título', erroTitulo);

        const total = titulo?.valor_centavos as number;
        const pago = ((titulo?.pagamentos as { valor_centavos: number }[] | null) ?? []).reduce((s, p) => s + p.valor_centavos, 0);
        const falta = total - pago;
        if (dados.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
        if (dados.valorCentavos > falta) throw new Error(`O valor não pode passar de ${(falta / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`);

        const { data: metodo } = dados.metodoId
            ? await cliente().from('metodos_pagamento').select('taxa_percentual, prazo_dias').eq('id', dados.metodoId).maybeSingle()
            : { data: null };
        const taxa = dados.taxaCentavos ?? Math.round(dados.valorCentavos * (Number(metodo?.taxa_percentual ?? 0) / 100));
        const liquidado = somaDias(dados.pagoEm, Number(metodo?.prazo_dias ?? 0));

        const { error } = await cliente().from('pagamentos').insert({
            clinica_id: clinica, titulo_id: dados.tituloId, conta_id: dados.contaId ?? null,
            metodo_id: dados.metodoId ?? null, valor_centavos: dados.valorCentavos, taxa_centavos: taxa,
            pago_em: dados.pagoEm, liquidado_em: liquidado,
        });
        erro('Falha ao registrar o pagamento', error);

        /* A situação vem do gatilho do banco, então relemos em vez de deduzir. */
        /* Só o título baixado, pedido ao servidor: numa página de 500 da clínica
           inteira, o 501º título respondia "não encontrado" depois de gravar. */
        const carga = await this.financeiro({ aba: 'visao', tituloId: dados.tituloId, porPagina: Number.MAX_SAFE_INTEGER });
        const atualizado = carga.titulos.find((t) => t.id === dados.tituloId);
        if (!atualizado) throw new Error('Pagamento registrado, mas o título não foi encontrado.');
        return atualizado;
    },

    async pipeline(filtro: { busca?: string; responsavel?: string; funilId?: string }): Promise<CargaDoPipeline> {
        const [funis, carga] = await Promise.all([this.funis(), this.contatos('lead', { porPagina: 500 })]);
        if (carga.tipo !== 'lead') throw new Error('Resposta inesperada do servidor.');
        const funil = funis.find((f) => f.id === filtro.funilId) ?? funilPadrao(funis);
        if (!funil) throw new Error('A clínica não tem pipeline ativo.');

        const busca = filtro.busca?.trim() ?? '';
        const visiveis = carga.itens.filter((l) => {
            if (l.funilId !== funil.id) return false;
            if (filtro.responsavel && l.responsavel !== filtro.responsavel) return false;
            if (busca && !semAcento(l.nome).includes(semAcento(busca)) && !semAcento(l.interesse).includes(semAcento(busca))) return false;
            return true;
        });
        return montarPipeline(funis, funil, visiveis, [...new Set(carga.itens.map((l) => l.responsavel))].sort());
    },

    async moverLeadDeEtapa(id: string, etapaId: string): Promise<Lead> {
        return this.atualizarLead(id, { etapaId });
    },

    async vendas(filtro: FiltroDeVendas): Promise<CargaDeVendas> {
        /* Os de um contato são pedidos ao servidor, e não procurados numa
           página da clínica inteira. Uma consulta nova por faixa (ver `financeiro`). */
        const consultaDeOrcamentos = () => {
            const consulta = cliente()
                .from('orcamentos')
                .select('id, numero, contato_id, status, total_centavos, validade, criado_em, contatos(nome), membros!responsavel_id(nome), orcamento_itens(id, procedimento_id, descricao, quantidade, valor_unitario_centavos, desconto_centavos, ordem)');
            return filtro.contatoId ? consulta.eq('contato_id', filtro.contatoId) : consulta;
        };
        const consultaDePacotes = () => {
            const consulta = cliente()
                .from('pacotes')
                .select('id, contato_id, nome, sessoes_total, valor_centavos, validade, status, contatos(nome), pacote_sessoes(id)');
            return filtro.contatoId ? consulta.eq('contato_id', filtro.contatoId) : consulta;
        };
        const [{ data: linhas, error }, { data: linhasDePacote, error: erroPacote }] = await Promise.all([
            lerTodasAsLinhas((de, ate) => consultaDeOrcamentos().order('criado_em', { ascending: false }).order('id').range(de, ate)),
            lerTodasAsLinhas((de, ate) => consultaDePacotes().order('criado_em', { ascending: false }).order('id').range(de, ate)),
        ]);
        erro('Falha ao ler os orçamentos', error);
        erro('Falha ao ler os pacotes', erroPacote);

        const todos: Orcamento[] = (linhas ?? []).map((o) => {
            const itens = (o.orcamento_itens ?? []) as unknown as {
                id: string; procedimento_id: string | null; descricao: string; quantidade: number; valor_unitario_centavos: number; desconto_centavos: number;
            }[];
            return {
                id: o.id as string,
                numero: o.numero as string,
                contatoId: (o.contato_id as string | null) ?? '',
                paciente: nomeEmbutido(o.contatos) ?? '—',
                responsavel: nomeEmbutido(o.membros) ?? '—',
                status: o.status as StatusDoOrcamento,
                totalCentavos: o.total_centavos as number,
                validade: (o.validade as string | null) ?? undefined,
                criadoEm: (o.criado_em as string).slice(0, 10),
                itens: itens.map((i) => ({
                    id: i.id, descricao: i.descricao, quantidade: i.quantidade,
                    ...(i.procedimento_id ? { procedimentoId: i.procedimento_id } : {}),
                    valorUnitarioCentavos: i.valor_unitario_centavos, descontoCentavos: i.desconto_centavos,
                })),
                resumo: itens.map((i) => i.descricao).join(' · '),
            };
        });

        const pacotesTodos: Pacote[] = (linhasDePacote ?? []).map((p) => ({
            id: p.id as string,
            contatoId: (p.contato_id as string | null) ?? '',
            paciente: nomeEmbutido(p.contatos) ?? '—',
            nome: p.nome as string,
            sessoesTotais: p.sessoes_total as number,
            /* Sessão usada é linha, não contador: o total sai da contagem. */
            sessoesUsadas: ((p.pacote_sessoes as { id: string }[] | null) ?? []).length,
            valorCentavos: p.valor_centavos as number,
            validade: (p.validade as string | null) ?? undefined,
            status: p.status as Pacote['status'],
        }));

        const busca = filtro.busca?.trim() ?? '';
        const daAba = filtro.aba === 'aguardando' ? todos.filter((o) => o.status === 'enviado')
            : filtro.aba === 'aprovados' ? todos.filter((o) => o.status === 'aprovado')
                : todos;
        const orcamentosFiltrados = daAba.filter((o) => !busca || semAcento(o.paciente).includes(semAcento(busca)) || o.numero.includes(busca) || semAcento(o.resumo).includes(semAcento(busca)));
        const pacotesFiltrados = pacotesTodos.filter((p) => !busca || semAcento(p.paciente).includes(semAcento(busca)) || semAcento(p.nome).includes(semAcento(busca)));

        const paginaDeOrcamentos = paginar(orcamentosFiltrados, filtro);
        const paginaDePacotes = paginar(pacotesFiltrados, filtro);
        const pagina = filtro.aba === 'pacotes' ? paginaDePacotes : paginaDeOrcamentos;

        const aprovados = todos.filter((o) => o.status === 'aprovado');
        const enviados = todos.filter((o) => o.status === 'enviado');
        const decididos = todos.filter((o) => o.status === 'aprovado' || o.status === 'recusado');

        return {
            aba: filtro.aba,
            orcamentos: filtro.aba === 'pacotes' ? [] : paginaDeOrcamentos.itens,
            pacotes: filtro.aba === 'pacotes' ? paginaDePacotes.itens : [],
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'aprovado', rotulo: 'Aprovado no período', valor: `R$ ${(aprovados.reduce((s, o) => s + o.totalCentavos, 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icone: 'circle-check' },
                { id: 'aguardando', rotulo: 'Aguardando resposta', valor: String(enviados.length), apoio: `R$ ${(enviados.reduce((s, o) => s + o.totalCentavos, 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icone: 'clock' },
                { id: 'conversao', rotulo: 'Taxa de aprovação', valor: decididos.length ? `${Math.round((aprovados.length / decididos.length) * 100)}%` : '—', apoio: 'dos decididos', icone: 'target' },
                { id: 'pacotes', rotulo: 'Pacotes ativos', valor: String(pacotesTodos.filter((p) => p.status === 'ativo').length), apoio: 'em uso', icone: 'layers' },
            ],
            totais: {
                orcamentos: todos.length,
                aguardando: enviados.length,
                aprovados: aprovados.length,
                pacotes: pacotesTodos.length,
            },
        };
    },

    async criarOrcamento(novo: NovoOrcamento): Promise<Orcamento> {
        const clinica = clinicaId();
        const total = novo.itens.reduce((s, i) => s + i.valorUnitarioCentavos * i.quantidade - i.descontoCentavos, 0);

        let contatoId = novo.contatoId;
        if (!contatoId) {
            const { data: achado } = await cliente().from('contatos').select('id').ilike('nome', novo.pacienteNome).maybeSingle();
            contatoId = achado?.id as string | undefined;
        }
        if (!contatoId) throw new Error('Escolha um paciente cadastrado para o orçamento.');

        /* Por id; nome só na falta dele, e aí precisa bater com a equipe. A lista
           fixa da tela ("Ana Prado", sem "Dra.") nunca batia, e o orçamento saía
           sem responsável sem ninguém saber. */
        const responsavelId = novo.responsavelId
            ?? (novo.responsavel.trim() ? await idPorNome('membros', novo.responsavel, 'responsável') : null);
        /* O número NÃO vai daqui: nasce no banco, de um contador por clínica
           (`0060`). Contar linhas no navegador dava o mesmo número a dois
           orçamentos criados juntos. */
        const { data, error } = await cliente()
            .from('orcamentos')
            .insert({
                clinica_id: clinica, contato_id: contatoId, responsavel_id: responsavelId,
                status: 'rascunho', total_centavos: total, validade: novo.validade ?? null,
            })
            .select('id')
            .single();
        erro('Falha ao criar o orçamento', error);

        const { error: erroItens } = await cliente().from('orcamento_itens').insert(
            novo.itens.map((i, ordem) => ({
                clinica_id: clinica, orcamento_id: data?.id as string, descricao: i.descricao,
                /* É o procedimento, com a quantidade, que faz a aprovação gerar pacote (`0062`). */
                procedimento_id: i.procedimentoId ?? null,
                quantidade: i.quantidade, valor_unitario_centavos: i.valorUnitarioCentavos,
                desconto_centavos: i.descontoCentavos, ordem,
            })),
        );
        erro('Orçamento criado, mas os itens falharam', erroItens);

        const carga = await this.vendas({ aba: 'orcamentos', porPagina: 500 });
        const criado = carga.orcamentos.find((o) => o.id === data?.id);
        if (!criado) throw new Error('Orçamento criado, mas não foi possível lê-lo.');
        return criado;
    },

    async decidirOrcamento(id: string, status: StatusDoOrcamento, cobranca?: CondicaoDeCobranca): Promise<Orcamento> {
        /* Status, pacote e parcelas numa transação só (`0062`, `0175`), e o
           pacote sai dos ITENS — procedimento e quantidade de sessões —, não de
           "pacote" e "10 sess" no texto. */
        const { error } = await cliente().rpc('decidir_orcamento', {
            p_orcamento: id, p_status: status,
            p_cobranca: cobranca ? {
                gerar: cobranca.gerar, parcelas: cobranca.parcelas, primeiroVencimento: cobranca.primeiroVencimento,
                ...(cobranca.categoriaId ? { categoriaId: cobranca.categoriaId } : {}),
            } : null,
        });
        if (error) throw new Error(`Falha ao mudar a situação do orçamento: ${error.message}`);

        const carga = await this.vendas({ aba: 'orcamentos', porPagina: 500 });
        const orcamento = carga.orcamentos.find((o) => o.id === id);
        if (!orcamento) throw new Error('Orçamento não encontrado.');
        return orcamento;
    },

    async caixaDeEntrada(filtro: FiltroDeConversas): Promise<CargaDaCaixa> {
        const [{ data: linhas, error }, { data: canais }, pacientesDaClinica, leadsDaClinica] = await Promise.all([
            cliente()
                .from('conversas')
                .select('id, contato_id, estado, conduz, fila, nao_lidas, ultima_mensagem_em, canal_id, atendente_id, resolvida_em, motivo_resolucao, reservada_para, contatos(nome), canais(nome, tipo), atendente:membros!atendente_id(nome), reserva:membros!conversas_reserva_da_mesma_clinica(nome)')
                .order('ultima_mensagem_em', { ascending: false }),
            cliente().from('canais').select('id, nome, tipo, provedor, instancia_externa_id, numero_e164, status, ultima_conexao_em, ultimo_erro').eq('ativo', true).order('nome'),
            lerTudo<Record<string, unknown>>((de, ate) => cliente().from('pacientes').select('contato_id').order('contato_id').range(de, ate)),
            lerTudo<Record<string, unknown>>((de, ate) => cliente().from('leads').select('id, contato_id, etapa').order('id').range(de, ate)),
        ]);
        erro('Falha ao ler as conversas', error);

        /* A prévia é a última mensagem de CADA conversa (`0066`). Vinha das
           últimas 200 mensagens da clínica: conversa parada havia dias ficava
           sem prévia. Lotes de 100 para o pedido não crescer sem limite. */
        const previa = new Map<string, string>();
        const ids = (linhas ?? []).map((l) => l.id as string);
        for (let i = 0; i < ids.length; i += 100) {
            const { data: ultimas, error: erroPrevia } = await cliente().rpc('ultimas_mensagens', { p_conversas: ids.slice(i, i + 100) });
            erro('Falha ao ler a última mensagem das conversas', erroPrevia);
            for (const m of (ultimas ?? []) as { conversa_id: string; texto: string | null }[]) {
                previa.set(m.conversa_id, m.texto ?? '');
            }
        }
        /* A etiqueta sai do vínculo: ficha de paciente, lead em aberto. Antes
           "Lead" era quem NÃO tinha contato — e lead tem contato. */
        const vinculos = {
            pacientes: new Set(pacientesDaClinica.map((p) => String(p.contato_id))),
            leadsAbertos: contatosComLeadAberto(leadsDaClinica),
        };

        const todas: Conversa[] = (linhas ?? []).map((l) => {
            const canal = (Array.isArray(l.canais) ? l.canais[0] : l.canais) as { nome: string; tipo: Canal['tipo'] } | null;
            return {
                id: l.id as string,
                contatoId: (l.contato_id as string | null),
                nome: nomeEmbutido(l.contatos) ?? 'Contato',
                canalId: (l.canal_id as string | null),
                canalNome: canal?.nome ?? '—',
                canalTipo: canal?.tipo ?? 'whatsapp',
                estado: l.estado as Conversa['estado'],
                conduz: l.conduz as Conversa['conduz'],
                atendente: nomeEmbutido(l.atendente),
                ...(l.atendente_id ? { atendenteId: String(l.atendente_id) } : {}),
                ...(l.resolvida_em ? { resolvidaEm: String(l.resolvida_em) } : {}),
                ...(l.motivo_resolucao ? { motivoResolucao: String(l.motivo_resolucao) } : {}),
                ...(l.reservada_para ? { reservadaPara: String(l.reservada_para), reservadaParaNome: nomeEmbutido(l.reserva) } : {}),
                fila: (l.fila as string | null) ?? undefined,
                naoLidas: l.nao_lidas as number,
                ultimaMensagem: previa.get(l.id as string),
                ultimaMensagemEm: (l.ultima_mensagem_em as string | null) ?? undefined,
                vinculos: vinculosDaConversa((l.contato_id as string | null) ?? null, vinculos),
            };
        });

        const busca = filtro.busca?.trim() ?? '';
        const visiveis = todas.filter((c) => {
            if (filtro.aba === 'nao-lidas' && c.naoLidas === 0) return false;
            if (filtro.aba === 'leads' && !c.vinculos.includes('Lead')) return false;
            if (filtro.canalId && c.canalId !== filtro.canalId) return false;
            if (filtro.fila && c.fila !== filtro.fila) return false;
            if (busca && !semAcento(c.nome).includes(semAcento(busca)) && !semAcento(c.ultimaMensagem ?? '').includes(semAcento(busca))) return false;
            return true;
        });

        return {
            conversas: visiveis,
            canais: (canais ?? []).map((c): Canal => ({
                id: c.id as string,
                nome: c.nome as string,
                tipo: c.tipo as Canal['tipo'],
                provedor: c.provedor as string,
                instanciaExternaId: (c.instancia_externa_id as string | null) ?? undefined,
                numero: (c.numero_e164 as string | null) ?? undefined,
                status: c.status as Canal['status'],
                ultimaConexaoEm: (c.ultima_conexao_em as string | null) ?? undefined,
                ultimoErro: (c.ultimo_erro as string | null) ?? undefined,
                conversasAbertas: todas.filter((x) => x.canalId === c.id && x.estado !== 'resolvida').length,
                naoLidas: todas.filter((x) => x.canalId === c.id).reduce((s, x) => s + x.naoLidas, 0),
            })),
            filas: [...new Set(todas.map((c) => c.fila).filter((f): f is string => !!f))].sort(),
            totais: {
                todas: todas.length,
                naoLidas: todas.filter((c) => c.naoLidas > 0).length,
                leads: todas.filter((c) => c.vinculos.includes('Lead')).length,
            },
        };
    },

    async mensagensDaConversa(conversaId: string): Promise<Mensagem[]> {
        const { data, error } = await cliente()
            .from('mensagens')
            .select('id, conversa_id, direcao, tipo, texto, autor_nome, status, ocorrido_em')
            .eq('conversa_id', conversaId)
            .order('ocorrido_em');
        erro('Falha ao ler as mensagens', error);

        return (data ?? []).map((m): Mensagem => ({
            id: m.id as string,
            conversaId: m.conversa_id as string,
            direcao: m.direcao as Mensagem['direcao'],
            tipo: m.tipo as Mensagem['tipo'],
            texto: (m.texto as string | null) ?? '',
            autor: (m.autor_nome as string | null) ?? undefined,
            status: m.status as Mensagem['status'],
            ocorridoEm: m.ocorrido_em as string,
        }));
    },

    async enviarMensagem(conversaId: string, texto: string): Promise<Mensagem> {
        const clinica = clinicaId();
        const agora = new Date().toISOString();
        const autor = atual()?.nome ?? 'Equipe';

        /* Canal de provedor com o qual a VecchIA não fala não recebe mensagem
           "pendente": ela nunca sairia, e a equipe acharia que o paciente vai
           receber. Recusar ANTES de gravar, com o motivo e o caminho. */
        const { data: daConversa } = await cliente()
            .from('conversas').select('canais(provedor)').eq('id', conversaId).maybeSingle();
        const embutido = (daConversa as { canais?: unknown } | null)?.canais;
        const provedorDoCanal = (Array.isArray(embutido) ? embutido[0] : embutido) as { provedor?: string } | null;
        const indisponivel = provedorDoCanal?.provedor ? indisponibilidadeDoProvedor(provedorDoCanal.provedor) : null;
        if (indisponivel) throw new Error(indisponivel);

        const { data, error } = await cliente()
            .from('mensagens')
            .insert({
                clinica_id: clinica, conversa_id: conversaId, direcao: 'enviada', tipo: 'texto',
                texto, autor_nome: autor, status: 'pendente', ocorrido_em: agora,
            })
            .select('id, conversa_id, direcao, tipo, texto, autor_nome, status, ocorrido_em')
            .single();
        erro('Falha ao enviar a mensagem', error);

        /* `pendente` é a verdade até o provedor aceitar: a mensagem existe no
           histórico e ainda não saiu. Marcar "enviada" antes seria mentir para
           quem espera resposta. */
        await cliente()
            .from('conversas')
            .update({ ultima_mensagem_em: agora, nao_lidas: 0, estado: 'em-atendimento' })
            .eq('id', conversaId);

        /* Agora sim, a entrega. Ela é um passo separado da gravação de propósito:
           se o provedor recusar, a mensagem continua no histórico marcada como
           não enviada, em vez de sumir junto com o erro. */
        let status = (data?.status as Mensagem['status']) ?? 'pendente';
        const canal = await canalDaConversa(conversaId);
        if (canal) {
            const { data: entrega, error: erroEnvio } = await cliente().functions.invoke('canal', {
                body: { acao: 'enviar', canal, mensagem: data?.id },
            });
            if (erroEnvio) {
                const detalhe = await mensagemDaFuncao(erroEnvio);
                throw new Error(detalhe ?? 'A mensagem foi gravada, mas não saiu para o WhatsApp.');
            }
            if ((entrega as { enviada?: boolean } | null)?.enviada) status = 'enviada';
        }

        return {
            id: data?.id as string,
            conversaId,
            direcao: 'enviada',
            tipo: 'texto',
            texto,
            autor,
            status,
            ocorridoEm: (data?.ocorrido_em as string) ?? agora,
        };
    },

    async abrirConversaCom(contatoId: string, canalId: string): Promise<string> {
        /* A mais recente daquela pessoa naquele canal, resolvida ou não: o
           histórico do WhatsApp é um só, e abrir outra caixa para o mesmo
           telefone faz a recepção responder sem ver o que já foi dito. */
        const { data: existente, error: erroBusca } = await cliente()
            .from('conversas')
            .select('id, estado')
            .eq('contato_id', contatoId)
            .eq('canal_id', canalId)
            .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
            .limit(1)
            .maybeSingle();
        erro('Falha ao procurar a conversa', erroBusca);

        /* Quem clica em "Enviar mensagem" está assumindo a conversa: ela nasce
           (ou volta) em Abertos, com quem clicou. */
        const { data: membro } = await cliente().from('membros').select('id').eq('email', atual()?.email ?? '').maybeSingle();
        const atendenteId = (membro as { id?: string } | null)?.id ?? null;

        const linha = existente as unknown as { id: string; estado: string } | null;
        if (linha) {
            /* Deixá-la com o assistente faria a IA responder por cima de quem digitou. */
            const { error } = await cliente()
                .from('conversas')
                .update({ estado: 'em-atendimento', conduz: 'equipe', nao_lidas: 0, atendente_id: atendenteId })
                .eq('id', linha.id);
            erro('Falha ao reabrir a conversa', error);
            return linha.id;
        }

        const { data, error } = await cliente()
            .from('conversas')
            .insert({
                clinica_id: clinicaId(),
                canal_id: canalId,
                contato_id: contatoId,
                estado: 'em-atendimento',
                conduz: 'equipe',
                nao_lidas: 0,
                atendente_id: atendenteId,
            })
            .select('id')
            .single();
        erro('Falha ao abrir a conversa', error);
        return String((data as unknown as { id: string }).id);
    },

    async assumirConversa(conversaId: string): Promise<Conversa> {
        const email = atual()?.email;
        const { data: membro } = await cliente().from('membros').select('id, nome').eq('email', email ?? '').maybeSingle();

        const { error } = await cliente()
            .from('conversas')
            .update({ conduz: 'equipe', estado: 'em-atendimento', atendente_id: membro?.id ?? null, nao_lidas: 0 })
            .eq('id', conversaId);
        erro('Falha ao assumir a conversa', error);

        const caixa = await this.caixaDeEntrada({ aba: 'todas' });
        const conversa = caixa.conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        return conversa;
    },

    async resolverConversa(conversaId: string, motivo?: string): Promise<Conversa> {
        /* Quem resolveu e quando, o gatilho da 0160 grava. */
        const { error } = await cliente()
            .from('conversas')
            .update({ estado: 'resolvida', nao_lidas: 0, motivo_resolucao: motivo?.trim() || null })
            .eq('id', conversaId);
        erro('Falha ao resolver a conversa', error);

        const caixa = await this.caixaDeEntrada({ aba: 'todas' });
        const conversa = caixa.conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        return conversa;
    },

    async reabrirConversa(conversaId: string): Promise<Conversa> {
        const { data: membro } = await cliente().from('membros').select('id').eq('email', atual()?.email ?? '').maybeSingle();
        if (!membro) throw new Error('Só quem é da equipe da clínica reabre conversa.');
        const { error } = await cliente()
            .from('conversas')
            .update({ estado: 'em-atendimento', conduz: 'equipe', atendente_id: membro.id })
            .eq('id', conversaId)
            .eq('estado', 'resolvida');
        erro('Falha ao reabrir a conversa', error);
        const caixa = await this.caixaDeEntrada({ aba: 'todas' });
        const conversa = caixa.conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        return conversa;
    },

    async devolverConversa(conversaId: string): Promise<void> {
        const { error } = await cliente().rpc('devolver_conversa', { p_conversa: conversaId });
        if (error?.code === '22023') throw new Error(error.message);
        erro('Falha ao devolver a conversa', error);
    },

    async paciente(id: string): Promise<CargaDoPaciente> {
        const [{ data: ficha, error: erroFicha }, { data: convenios }, { data: eventos }, { data: documentos }] = await Promise.all([
            cliente()
                .from('pacientes')
                .select('*, contatos(nome, telefone, email), profissional:membros!profissional_id(nome)')
                .eq('contato_id', id)
                .maybeSingle(),
            cliente().from('convenios').select('id, nome, tipo, cor').eq('ativo', true).order('nome'),
            cliente().from('eventos_do_contato').select('*').eq('contato_id', id).order('ocorrido_em', { ascending: false }),
            cliente().from('documentos').select('*').eq('contato_id', id).order('atualizado_em', { ascending: false }),
            registrarAcessoAoProntuario((args) => cliente().rpc('registrar_acesso_ao_prontuario', args), id, 'paciente'),
        ]);
        const etiquetasDaPessoa = (await etiquetasPorContato()).get(id) ?? [];
        erro('Falha ao ler a ficha', erroFicha);

        /* Vendas e financeiro do próprio paciente saem das mesmas consultas que
           as telas deles usam: um número só, num lugar só. */
        /* Com o filtro NO SERVIDOR e sem teto de página: carregar 500 linhas da
           clínica e procurar as do paciente fazia a ficha perder orçamento,
           pacote e título do 501º em diante, sem aviso. */
        const semTeto = Number.MAX_SAFE_INTEGER;
        const [vendasDoPaciente, pacotesDoPaciente, financeiroDoPaciente] = await Promise.all([
            this.vendas({ aba: 'orcamentos', contatoId: id, porPagina: semTeto }),
            this.vendas({ aba: 'pacotes', contatoId: id, porPagina: semTeto }),
            this.financeiro({ aba: 'visao', contatoId: id, porPagina: semTeto }),
        ]);
        const pacotesDele = pacotesDoPaciente.pacotes;
        const orcamentosDele = vendasDoPaciente.orcamentos;
        const titulosDele = financeiroDoPaciente.titulos;
        const pago = titulosDele.reduce((s, t) => s + t.pagoCentavos, 0);
        const emAberto = titulosDele.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0);
        const hojeIso = new Date().toLocaleDateString('en-CA', { timeZone: FUSO });
        const vencido = titulosDele
            .filter((t) => t.vencimento < hojeIso && t.situacao !== 'pago')
            .reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0);
        /* Id que não existe é 404, não ficha em branco: paciente vazio esconde
           link quebrado e cadastro apagado. */
        if (!ficha) throw new Response(`Paciente ${id} não encontrado.`, { status: 404, statusText: 'Paciente não encontrado' });

        const contato = ficha.contatos as { nome: string; telefone: string; email: string | null } | null;
        return {
            ficha: {
                id,
                nome: contato?.nome ?? '—',
                nomeSocial: (ficha.nome_social as string | null) ?? '',
                nascimento: (ficha.nascimento as string | null) ?? '',
                sexo: (ficha.sexo as FichaDoPaciente['sexo']) ?? 'nao-informado',
                cpf: (ficha.cpf as string | null) ?? '',
                rg: (ficha.rg as string | null) ?? '',
                estadoCivil: (ficha.estado_civil as string | null) ?? '',
                profissao: (ficha.profissao as string | null) ?? '',
                telefone: contato?.telefone ?? '',
                telefoneSecundario: (ficha.telefone_secundario as string | null) ?? '',
                email: contato?.email ?? '',
                notificacoes: (ficha.notificacoes as FichaDoPaciente['notificacoes']) ?? 'whatsapp',
                contatoEmergencia: (ficha.contato_emergencia as string | null) ?? '',
                cep: (ficha.cep as string | null) ?? '',
                rua: (ficha.rua as string | null) ?? '',
                numero: (ficha.numero as string | null) ?? '',
                complemento: (ficha.complemento as string | null) ?? '',
                bairro: (ficha.bairro as string | null) ?? '',
                cidade: (ficha.cidade as string | null) ?? '',
                estado: (ficha.estado as string | null) ?? '',
                profissional: nomeEmbutido(ficha.profissional) ?? '',
                convenioId: (ficha.convenio_id as string | null) ?? '',
                alergias: (ficha.alergias as string | null) ?? '',
                observacoes: (ficha.observacoes as string | null) ?? '',
                origem: (ficha.origem as string | null) ?? '',
                indicadoPor: (ficha.indicado_por as string | null) ?? '',
                cadastradoEm: (ficha.cadastrado_em as string | null) ?? '',
                atualizadoEm: (ficha.atualizado_em as string | null) ?? new Date().toISOString(),
                atualizadoPor: 'Equipe',
            },
            convenios: (convenios ?? []).map((c): Convenio => ({ id: c.id as string, nome: c.nome as string, tipo: c.tipo as string, cor: c.cor as string })),
            financeiro: {
                totalInvestidoCentavos: pago,
                emAbertoCentavos: emAberto,
                vencidoCentavos: vencido,
                ticketMedioCentavos: titulosDele.length ? Math.round(pago / titulosDele.length) : 0,
                pacote: pacotesDele[0]
                    ? { nome: pacotesDele[0].nome, sessoesConcluidas: pacotesDele[0].sessoesUsadas, sessoesTotais: pacotesDele[0].sessoesTotais }
                    : undefined,
            },
            linhaDoTempo: (eventos ?? []).map((e) => ({
                id: e.id as string,
                titulo: e.titulo as string,
                quandoEm: e.ocorrido_em as string,
                icone: (e.icone as string) ?? 'circle',
                tom: (e.tom as 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | null) ?? undefined,
                autor: (e.autor_nome as string | null) ?? undefined,
                descricao: (e.descricao as string | null) ?? undefined,
                detalhes: (e.detalhes as { rotulo: string; valor: string }[] | null) ?? undefined,
            })),
            documentos: (documentos ?? []).map((d) => ({
                id: d.id as string,
                titulo: d.titulo as string,
                tipo: d.tipo as string,
                pacienteId: id,
                atualizadoEm: d.atualizado_em as string,
                status: d.status as CargaDoPaciente['documentos'][number]['status'],
                paginas: d.paginas as number,
            })),
            pacotes: pacotesDele,
            orcamentos: orcamentosDele,
            titulos: titulosDele,
            prontuario: await this.prontuario(id),
            etiquetas: etiquetasDaPessoa,
        };
    },

    async atualizarCampoDaFicha<C extends keyof FichaDoPaciente>(id: string, campo: C, valor: FichaDoPaciente[C]): Promise<FichaDoPaciente> {
        /* Nome, telefone e e-mail moram no contato; o resto, na ficha. É a
           mesma pessoa, e a tela não precisa saber onde cada campo está. */
        const noContato: Partial<Record<keyof FichaDoPaciente, string>> = { nome: 'nome', telefone: 'telefone', email: 'email' };
        const colunas: Partial<Record<keyof FichaDoPaciente, string>> = {
            nomeSocial: 'nome_social', nascimento: 'nascimento', sexo: 'sexo', cpf: 'cpf', rg: 'rg',
            estadoCivil: 'estado_civil', profissao: 'profissao', telefoneSecundario: 'telefone_secundario',
            notificacoes: 'notificacoes', contatoEmergencia: 'contato_emergencia', cep: 'cep', rua: 'rua',
            numero: 'numero', complemento: 'complemento', bairro: 'bairro', cidade: 'cidade', estado: 'estado',
            convenioId: 'convenio_id', alergias: 'alergias', observacoes: 'observacoes', origem: 'origem',
            indicadoPor: 'indicado_por',
        };

        if (noContato[campo]) {
            const { error } = await cliente().from('contatos').update({ [noContato[campo] as string]: valor || null }).eq('id', id);
            erro('Falha ao salvar o campo', error);
        } else if (campo === 'profissional') {
            /* O valor é o id do cadastro. Nome sem correspondência gravava
               `profissional_id = null` e a tela dizia "Campo atualizado". */
            const bruto = String(valor ?? '').trim();
            const membroId = !bruto
                ? null
                : /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bruto)
                    ? bruto
                    : await idPorNome('membros', bruto, 'profissional');
            const { error } = await cliente().from('pacientes').update({ profissional_id: membroId }).eq('contato_id', id);
            erro('Falha ao salvar o profissional', error);
        } else if (colunas[campo]) {
            const { error } = await cliente().from('pacientes').update({ [colunas[campo] as string]: valor || null }).eq('contato_id', id);
            erro('Falha ao salvar o campo', error);
        }

        return (await this.paciente(id)).ficha;
    },

    async criarTarefa(dados: { titulo: string; venceEm?: string; responsavel?: string; prioridade?: PrioridadeDaTarefa }): Promise<Tarefa> {
        const titulo = dados.titulo.trim();
        if (!titulo) throw new Error('A tarefa precisa de um título.');

        /* O responsável é vínculo, não texto: a tarefa aparece para a pessoa
           certa porque aponta para a linha dela em `membros`. */
        const { data: membro } = dados.responsavel
            ? await cliente().from('membros').select('id').ilike('nome', `%${dados.responsavel}%`).limit(1).maybeSingle()
            : { data: null };

        const { data, error } = await cliente().from('tarefas').insert({
            clinica_id: clinicaId(),
            titulo,
            vence_em: dados.venceEm ?? new Date().toLocaleDateString('en-CA', { timeZone: FUSO }),
            responsavel_id: (membro as { id: string } | null)?.id ?? null,
            prioridade: dados.prioridade ?? 'media',
        }).select('id, titulo, vence_em, prioridade, concluida_em, membros(nome)').single();
        erro('Falha ao criar a tarefa', error);

        const l = data as unknown as Record<string, unknown>;
        return {
            id: String(l.id),
            titulo: String(l.titulo),
            venceEm: String(l.vence_em ?? ''),
            responsavel: nomeEmbutido(l.membros) ?? 'Equipe',
            prioridade: l.prioridade as Tarefa['prioridade'],
            concluida: Boolean(l.concluida_em),
        };
    },

    async atendimentos(filtro: FiltroDeAtendimentos): Promise<CargaDeAtendimentos> {
        const hoje = new Date().toLocaleDateString('en-CA', { timeZone: FUSO });
        const inicio = filtro.periodo === 'dia' ? filtro.referencia
            : filtro.periodo === 'semana' ? segundaDa(filtro.referencia)
                : `${filtro.referencia.slice(0, 7)}-01`;
        /* O fim do mês é o dia anterior ao primeiro do mês seguinte. Somar "31"
           ao ano-mês produz 31 de setembro, que o Postgres recusa — e a tela
           inteira vinha vazia por causa disso. */
        const fim = filtro.periodo === 'dia' ? filtro.referencia
            : filtro.periodo === 'semana' ? somaDias(segundaDa(filtro.referencia), 5)
                : ultimoDiaDoMes(filtro.referencia);

        const recorte = await recorteDaUnidade();
        const doPeriodo = cliente()
            .from('agendamentos')
            .select(`${CAMPOS_DO_AGENDAMENTO}, atendimentos(id, situacao, fichas_preenchidas(nome))`)
            .neq('status', 'bloqueio')
            .gte('inicio_em', `${inicio}T00:00:00-03:00`)
            .lte('inicio_em', `${fim}T23:59:59-03:00`);
        const { data, error } = await (recorte ? doPeriodo.or(filtroDosAgendamentos(recorte)) : doPeriodo).order('inicio_em');
        erro('Falha ao ler as consultas', error);

        const agora = Date.now();
        const itens: Atendimento[] = (data ?? []).map((linha) => {
            const l = linha as unknown as LinhaDeAgendamento & {
                atendimentos: { id: string; situacao: SituacaoDoAtendimento; fichas_preenchidas: { nome: string }[] } | null;
            };
            const comeca = new Date(l.inicio_em).getTime();
            const termina = comeca + l.duracao_min * 60_000;
            const emCurso = agora >= comeca && agora <= termina;
            const situacao: SituacaoDoAtendimento = l.atendimentos?.situacao
                ?? (l.status === 'cancelado' ? 'cancelado'
                    : l.status === 'faltou' ? 'faltou'
                        : l.status === 'concluido' ? 'concluido'
                            : emCurso ? 'em-atendimento' : 'aguardando');

            return {
                id: l.atendimentos?.id ?? `at-${l.id}`,
                agendamentoId: l.id,
                pacienteId: l.contato_id ?? '',
                paciente: l.contatos?.nome ?? '—',
                procedimento: l.procedimentos?.nome ?? '',
                profissional: l.profissional?.nome ?? '',
                inicioEm: l.inicio_em,
                duracaoMin: l.duracao_min,
                sala: l.salas?.nome ?? undefined,
                situacao,
                valorCentavos: l.procedimentos?.preco_centavos ?? 0,
                fichas: (l.atendimentos?.fichas_preenchidas ?? []).map((f) => f.nome),
            };
        });

        const busca = filtro.busca?.trim() ?? '';
        const filtrados = itens.filter((a) => {
            if (filtro.aba === 'em-aberto' && (a.situacao === 'concluido' || a.situacao === 'cancelado')) return false;
            if (busca && !semAcento(a.paciente).includes(semAcento(busca)) && !semAcento(a.procedimento).includes(semAcento(busca))) return false;
            return true;
        });

        const concluidos = itens.filter((a) => a.situacao === 'concluido');
        const faturado = concluidos.reduce((s, a) => s + a.valorCentavos, 0);

        return {
            ...paginar(filtrados, filtro),
            indicadores: [
                { id: 'realizados', rotulo: 'Realizados no período', valor: String(concluidos.length), icone: 'stethoscope' },
                { id: 'em-aberto', rotulo: 'Em aberto', valor: String(itens.filter((a) => a.situacao === 'aguardando' || a.situacao === 'em-atendimento').length), apoio: 'aguardando registro', icone: 'clipboard-list' },
                { id: 'ticket', rotulo: 'Ticket médio', valor: concluidos.length ? `R$ ${(faturado / concluidos.length / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00', apoio: 'no período', icone: 'circle-dollar-sign' },
                { id: 'sem-ficha', rotulo: 'Sem ficha preenchida', valor: String(concluidos.filter((a) => a.fichas.length === 0).length), apoio: 'consultas concluídas', icone: 'clipboard-pen' },
            ],
            totais: {
                hoje: itens.filter((a) => diaLocal(a.inicioEm) === hoje).length,
                emAberto: itens.filter((a) => a.situacao === 'aguardando' || a.situacao === 'em-atendimento').length,
                todos: itens.length,
            },
        };
    },

    async estoque(filtro: FiltroDoEstoque): Promise<CargaDoEstoque> {
        const [produtosResp, categoriasResp, marcasResp, fornecedoresResp, reservasResp, movimentosResp] = await Promise.all([
            cliente().from('produtos').select(CAMPOS_DO_PRODUTO).eq('ativo', true).order('nome'),
            /* Sem filtro de `ativa`: o cadastro precisa mostrar a categoria
               inativada, senão ela some da tela e ninguém a reativa. */
            cliente().from('categorias_produto').select('id, nome, descricao, ativa').order('nome'),
            cliente().from('marcas').select('id, nome, fabricante, fornecedor_id, categoria, registro_anvisa, ativa, fornecedores(nome)').order('nome'),
            cliente().from('fornecedores').select('id, nome, cnpj, contato, telefone, email, prazo_entrega, ativo').order('nome'),
            cliente().from('reservas_estoque')
                .select('id, procedimento_id, procedimento, contato_id, paciente, origem, status, criado_em, reserva_itens(produto_id, quantidade_consumo, unidade_consumo, produtos(nome))')
                .order('criado_em', { ascending: false }),
            cliente().from('movimentacoes_estoque')
                .select('id, produto_id, tipo, quantidade, quantidade_consumo, unidade_consumo, origem, ocorrido_em, produtos(nome, unidade)')
                .order('ocorrido_em', { ascending: false }),
        ]);
        erro('Falha ao ler os produtos', produtosResp.error);
        erro('Falha ao ler as movimentações', movimentosResp.error);

        const todos = (produtosResp.data ?? []).map((l) => paraProduto(l as unknown as Record<string, unknown>));

        const marcasCadastradas: Marca[] = (marcasResp.data ?? []).map((m) => ({
            id: m.id as string,
            nome: m.nome as string,
            fabricante: (m.fabricante as string | null) ?? undefined,
            fornecedorId: (m.fornecedor_id as string | null) ?? undefined,
            fornecedor: nomeEmbutido(m.fornecedores),
            categoria: (m.categoria as string | null) ?? undefined,
            registro: (m.registro_anvisa as string | null) ?? undefined,
            ativa: m.ativa as boolean,
            produtos: todos.filter((p) => p.marcaId === m.id).length,
        }));

        const fornecedoresCadastrados: Fornecedor[] = (fornecedoresResp.data ?? []).map((f) => ({
            id: f.id as string,
            nome: f.nome as string,
            cnpj: (f.cnpj as string | null) ?? undefined,
            contato: (f.contato as string | null) ?? undefined,
            telefone: (f.telefone as string | null) ?? undefined,
            email: (f.email as string | null) ?? undefined,
            prazoEntrega: (f.prazo_entrega as string | null) ?? undefined,
            ativo: f.ativo as boolean,
            marcas: marcasCadastradas.filter((m) => m.fornecedorId === f.id).map((m) => m.nome),
        }));

        const categoriasCadastradas: CategoriaDeProduto[] = (categoriasResp.data ?? []).map((c) => {
            const daCategoria = todos.filter((p) => p.categoriaId === c.id);
            return {
                id: c.id as string,
                nome: c.nome as string,
                descricao: (c.descricao as string | null) ?? undefined,
                ativa: c.ativa as boolean,
                produtos: daCategoria.length,
                valorEmEstoqueCentavos: daCategoria.reduce((s, p) => s + p.custoCentavos * p.quantidade, 0),
            };
        });

        const reservasCadastradas: ReservaDeEstoque[] = (reservasResp.data ?? []).map((r) => ({
            id: r.id as string,
            procedimentoId: (r.procedimento_id as string | null) ?? undefined,
            procedimento: r.procedimento as string,
            contatoId: (r.contato_id as string | null) ?? undefined,
            paciente: (r.paciente as string | null) ?? undefined,
            origem: r.origem as string,
            status: r.status as ReservaDeEstoque['status'],
            criadoEm: r.criado_em as string,
            itens: ((r.reserva_itens as Record<string, unknown>[] | null) ?? []).map((i) => ({
                produtoId: i.produto_id as string,
                produto: nomeEmbutido(i.produtos) ?? '—',
                quantidadeConsumo: Number(i.quantidade_consumo),
                unidadeConsumo: i.unidade_consumo as string,
            })),
        }));

        const movimentacoes: MovimentacaoDeEstoque[] = (movimentosResp.data ?? []).map((m) => {
            const produto = Array.isArray(m.produtos) ? m.produtos[0] : m.produtos;
            return {
                id: m.id as string,
                produtoId: m.produto_id as string,
                produto: (produto as { nome?: string } | null)?.nome ?? '—',
                tipo: m.tipo as MovimentacaoDeEstoque['tipo'],
                quantidade: Number(m.quantidade),
                unidade: (produto as { unidade?: string } | null)?.unidade ?? 'und',
                quantidadeConsumo: m.quantidade_consumo == null ? undefined : Number(m.quantidade_consumo),
                unidadeConsumo: (m.unidade_consumo as string | null) ?? undefined,
                origem: m.origem as string,
                ocorridoEm: m.ocorrido_em as string,
            };
        });

        const busca = filtro.busca?.trim() ?? '';
        const bate = (texto: string) => !busca || semAcento(texto).includes(semAcento(busca));
        const abaixoDoMinimo = todos.filter((p) => p.situacao !== 'normal');
        const criticos = todos.filter((p) => p.situacao === 'critico');
        const reservasAbertas = reservasCadastradas.filter((r) => r.status !== 'consumida');

        const listaDeProdutos = (filtro.aba === 'baixo' ? abaixoDoMinimo : todos).filter((p) => bate(p.nome) || bate(p.lote));
        const listaDeMovimentos = movimentacoes.filter((m) => bate(m.produto) || bate(m.origem));
        const listaDeReservas = reservasCadastradas.filter((r) => bate(r.procedimento) || bate(r.paciente ?? ''));
        const listaDeFornecedores = fornecedoresCadastrados.filter((f) => bate(f.nome));
        const listaDeMarcas = marcasCadastradas.filter((m) => bate(m.nome));
        const listaDeCategorias = categoriasCadastradas.filter((c) => bate(c.nome));

        /* Pagina só a lista que a aba mostra: paginar as sete de uma vez daria
           números de página que não correspondem a nada na tela. */
        const visivel: unknown[] = filtro.aba === 'movimentacoes' ? listaDeMovimentos
            : filtro.aba === 'reservas' ? listaDeReservas
                : filtro.aba === 'fornecedores' ? listaDeFornecedores
                    : filtro.aba === 'marcas' ? listaDeMarcas
                        : filtro.aba === 'categorias' ? listaDeCategorias
                            : listaDeProdutos;
        const pagina = paginar(visivel, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const recorte = <T>(lista: T[], ativa: boolean) =>
            (ativa ? (pagina.itens as unknown as T[]) : lista);

        const valorEmEstoqueCentavos = todos.reduce((s, p) => s + p.custoCentavos * p.quantidade, 0);
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

        return {
            aba: filtro.aba,
            produtos: recorte(listaDeProdutos, filtro.aba === 'produtos' || filtro.aba === 'baixo'),
            categorias: recorte(listaDeCategorias, filtro.aba === 'categorias'),
            fornecedores: recorte(listaDeFornecedores, filtro.aba === 'fornecedores'),
            marcas: recorte(listaDeMarcas, filtro.aba === 'marcas'),
            reservas: recorte(listaDeReservas, filtro.aba === 'reservas'),
            movimentacoes: recorte(listaDeMovimentos, filtro.aba === 'movimentacoes'),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'itens', rotulo: 'Itens cadastrados', valor: String(todos.length), apoio: `${marcasCadastradas.length} marcas · ${fornecedoresCadastrados.length} fornecedores`, icone: 'package' },
                { id: 'valor', rotulo: 'Valor em estoque', valor: reais(valorEmEstoqueCentavos), apoio: 'custo de reposição', icone: 'circle-dollar-sign' },
                { id: 'reservado', rotulo: 'Reservado por propostas', valor: String(reservasAbertas.length), apoio: `${reservasAbertas.reduce((s, r) => s + r.itens.length, 0)} insumos comprometidos`, icone: 'package-check' },
                { id: 'baixo', rotulo: 'Abaixo do mínimo', valor: String(abaixoDoMinimo.length), apoio: criticos.length ? 'ação necessária' : 'estoque coberto', tendencia: criticos.length ? 'down' : 'neutral', icone: 'triangle-alert' },
            ],
            totais: {
                produtos: todos.length,
                categorias: categoriasCadastradas.length,
                abaixoDoMinimo: abaixoDoMinimo.length,
                reservasAbertas: reservasAbertas.length,
                fornecedores: fornecedoresCadastrados.length,
                marcas: marcasCadastradas.length,
                movimentacoes: movimentacoes.length,
            },
            criticos,
            valorEmEstoqueCentavos,
        };
    },

    async criarProduto(dados: NovoProduto): Promise<Produto> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao produto.');
        if (dados.conteudo <= 0) throw new Error('A quantidade por embalagem precisa ser maior que zero.');

        const { data, error } = await cliente().from('produtos').insert({
            clinica_id: clinica,
            nome,
            categoria_id: dados.categoriaId ?? null,
            marca_id: dados.marcaId ?? null,
            fornecedor_id: dados.fornecedorId ?? null,
            lote: dados.lote?.trim() ?? '',
            validade: dados.validade ?? null,
            minimo: dados.minimo,
            unidade: dados.unidade,
            conteudo: dados.conteudo,
            unidade_conteudo: dados.unidadeConteudo,
            unidade_consumo: dados.unidadeConsumo,
            custo_centavos: dados.custoCentavos,
        }).select('id').single();

        /* `unique (clinica_id, nome, lote)`: dois lotes do mesmo produto
           convivem, dois cadastros do mesmo lote não. */
        if (error?.code === '23505') throw new Error(`Já existe ${nome}${dados.lote ? ` no lote ${dados.lote}` : ''}.`);
        erro('Falha ao cadastrar o produto', error);

        const id = (data as { id: string }).id;
        if (dados.quantidadeInicial > 0) {
            const { error: erroMovimento } = await cliente().from('movimentacoes_estoque').insert({
                clinica_id: clinica, produto_id: id, tipo: 'entrada',
                quantidade: dados.quantidadeInicial, origem: 'Saldo inicial',
            });
            erro('Produto cadastrado, mas o saldo inicial não entrou', erroMovimento);
        }
        return lerProduto(id);
    },

    async atualizarProduto(id: string, dados: DadosDoProduto): Promise<Produto> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao produto.');
        if (dados.conteudo <= 0) throw new Error('A quantidade por embalagem precisa ser maior que zero.');

        /* `quantidade` não está aqui de propósito: saldo só muda por
           movimentação, senão o extrato deixa de explicar o estoque. */
        const { error } = await cliente().from('produtos').update({
            nome,
            categoria_id: dados.categoriaId ?? null,
            marca_id: dados.marcaId ?? null,
            fornecedor_id: dados.fornecedorId ?? null,
            lote: dados.lote?.trim() ?? '',
            validade: dados.validade ?? null,
            minimo: dados.minimo,
            unidade: dados.unidade,
            conteudo: dados.conteudo,
            unidade_conteudo: dados.unidadeConteudo,
            unidade_consumo: dados.unidadeConsumo,
            custo_centavos: dados.custoCentavos,
        }).eq('id', id);
        if (error?.code === '23505') throw new Error(`Já existe ${nome}${dados.lote ? ` no lote ${dados.lote}` : ''}.`);
        erro('Falha ao salvar o produto', error);
        return lerProduto(id);
    },

    async registrarMovimentacao(dados: NovaMovimentacao): Promise<Produto> {
        const clinica = clinicaId();
        if (dados.quantidade <= 0) throw new Error('Informe uma quantidade maior que zero.');

        const atual = await lerProduto(dados.produtoId);
        if (dados.tipo === 'saida' && dados.quantidade > atual.quantidade) {
            throw new Error(`Há apenas ${atual.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} em estoque.`);
        }

        const { error } = await cliente().from('movimentacoes_estoque').insert({
            clinica_id: clinica,
            produto_id: dados.produtoId,
            tipo: dados.tipo,
            quantidade: dados.quantidade,
            origem: dados.origem,
            ocorrido_em: dados.ocorridoEm ? `${dados.ocorridoEm}T12:00:00-03:00` : new Date().toISOString(),
        });
        erro('Falha ao registrar a movimentação', error);

        /* O saldo vem do gatilho do banco, então relemos em vez de deduzir. */
        return lerProduto(dados.produtoId);
    },

    async salvarFornecedor(dados: DadosDoFornecedor): Promise<Fornecedor> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao fornecedor.');

        const corpo = {
            nome,
            cnpj: dados.cnpj ?? null,
            contato: dados.contato ?? null,
            telefone: dados.telefone ?? null,
            email: dados.email ?? null,
            prazo_entrega: dados.prazoEntrega ?? null,
            ...(dados.ativo === undefined ? {} : { ativo: dados.ativo }),
            /* Ausente não mexe: quem edita pelo Estoque não vê a categoria. */
            ...(dados.categoriaId === undefined ? {} : { categoria_id: dados.categoriaId }),
        };
        const { data, error } = dados.id
            ? await cliente().from('fornecedores').update(corpo).eq('id', dados.id).select(CAMPOS_DO_FORNECEDOR).single()
            : await cliente().from('fornecedores').insert({ clinica_id: clinica, ...corpo }).select(CAMPOS_DO_FORNECEDOR).single();
        if (error?.code === '23505') throw new Error(`Já existe um fornecedor chamado ${nome}.`);
        erro('Falha ao salvar o fornecedor', error);

        return paraFornecedor(data as Record<string, unknown>);
    },

    async salvarPrestador(dados: DadosDoPrestador): Promise<PrestadorDeServico> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao prestador de serviços.');

        let tipoServicoId = dados.tipoServicoId;
        const novoTipo = dados.novoTipoServico?.trim();
        if (novoTipo) tipoServicoId = (await this.salvarTipoDeServico({ nome: novoTipo })).id;

        const corpo = {
            nome,
            documento: dados.documento?.trim() || null,
            telefone: dados.telefone?.trim() || null,
            email: dados.email?.trim() || null,
            observacoes: dados.observacoes?.trim() || null,
            ...(tipoServicoId === undefined ? {} : { tipo_servico_id: tipoServicoId }),
            ...(dados.categoriaId === undefined ? {} : { categoria_id: dados.categoriaId }),
            ...(dados.ativo === undefined ? {} : { ativo: dados.ativo }),
        };
        const { data, error } = dados.id
            ? await cliente().from('prestadores_de_servico').update(corpo).eq('id', dados.id).select(CAMPOS_DO_PRESTADOR).single()
            : await cliente().from('prestadores_de_servico').insert({ clinica_id: clinica, ...corpo }).select(CAMPOS_DO_PRESTADOR).single();
        erro('Falha ao salvar o prestador de serviços', error);
        return paraPrestador(data as Record<string, unknown>);
    },

    async tiposDeServico(): Promise<TipoDeServico[]> {
        const { data, error } = await cliente()
            .from('tipos_de_servico').select('id, nome, ativo, prestadores_de_servico(count)').order('nome');
        erro('Falha ao ler os tipos de serviço', error);
        return ((data ?? []) as Record<string, unknown>[]).map((t) => ({
            id: t.id as string,
            nome: t.nome as string,
            ativo: t.ativo as boolean,
            prestadores: Number(((t.prestadores_de_servico as { count: number }[] | null) ?? [])[0]?.count ?? 0),
        }));
    },

    async salvarTipoDeServico(dados: { id?: string; nome: string; ativo?: boolean }): Promise<TipoDeServico> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao tipo de serviço.');
        const corpo = { nome, ...(dados.ativo === undefined ? {} : { ativo: dados.ativo }) };
        const { data, error } = dados.id
            ? await cliente().from('tipos_de_servico').update(corpo).eq('id', dados.id).select('id, nome, ativo').single()
            : await cliente().from('tipos_de_servico').insert({ clinica_id: clinicaId(), ...corpo }).select('id, nome, ativo').single();
        if (error?.code === '23505') throw new Error(`Já existe o tipo de serviço ${nome}.`);
        erro('Falha ao salvar o tipo de serviço', error);
        const t = data as Record<string, unknown>;
        return { id: t.id as string, nome: t.nome as string, ativo: t.ativo as boolean, prestadores: 0 };
    },

    async categoriasParaCadastro(): Promise<CategoriaParaEscolha[]> {
        const { data, error } = await cliente()
            .from('categorias_financeiras').select('id, nome').eq('natureza', 'despesa').eq('ativa', true).order('nome');
        erro('Falha ao ler as categorias de conta', error);
        return ((data ?? []) as Record<string, unknown>[]).map((c) => ({ id: c.id as string, nome: c.nome as string }));
    },

    async salvarMarca(dados: DadosDaMarca): Promise<Marca> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome à marca.');

        const corpo = {
            nome,
            fabricante: dados.fabricante ?? null,
            fornecedor_id: dados.fornecedorId ?? null,
            categoria: dados.categoria ?? null,
            registro_anvisa: dados.registro ?? null,
            ...(dados.ativa === undefined ? {} : { ativa: dados.ativa }),
        };
        const campos = 'id, nome, fabricante, fornecedor_id, categoria, registro_anvisa, ativa, fornecedores(nome), produtos(count)';
        const { data, error } = dados.id
            ? await cliente().from('marcas').update(corpo).eq('id', dados.id).select(campos).single()
            : await cliente().from('marcas').insert({ clinica_id: clinica, ...corpo }).select(campos).single();
        if (error?.code === '23505') throw new Error(`Já existe uma marca chamada ${nome}.`);
        erro('Falha ao salvar a marca', error);

        const l = data as Record<string, unknown>;
        return {
            id: l.id as string,
            nome: l.nome as string,
            fabricante: (l.fabricante as string | null) ?? undefined,
            fornecedorId: (l.fornecedor_id as string | null) ?? undefined,
            fornecedor: nomeEmbutido(l.fornecedores),
            categoria: (l.categoria as string | null) ?? undefined,
            registro: (l.registro_anvisa as string | null) ?? undefined,
            ativa: l.ativa as boolean,
            produtos: ((l.produtos as { count: number }[] | null) ?? [])[0]?.count ?? 0,
        };
    },

    async salvarCategoriaDeProduto(dados: DadosDaCategoriaDeProduto): Promise<CategoriaDeProduto> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome à categoria.');

        const corpo = { nome, descricao: dados.descricao ?? null, ativa: dados.ativa };
        const campos = 'id, nome, descricao, ativa, produtos(custo_centavos, quantidade)';
        const { data, error } = dados.id
            ? await cliente().from('categorias_produto').update(corpo).eq('id', dados.id).select(campos).single()
            : await cliente().from('categorias_produto').insert({ clinica_id: clinica, ...corpo }).select(campos).single();
        if (error?.code === '23505') throw new Error(`Já existe uma categoria chamada ${nome}.`);
        erro('Falha ao salvar a categoria', error);

        const l = data as Record<string, unknown>;
        const daCategoria = (l.produtos as { custo_centavos: number; quantidade: number }[] | null) ?? [];
        return {
            id: l.id as string,
            nome: l.nome as string,
            descricao: (l.descricao as string | null) ?? undefined,
            ativa: l.ativa as boolean,
            produtos: daCategoria.length,
            valorEmEstoqueCentavos: daCategoria.reduce((s, p) => s + Number(p.custo_centavos) * Number(p.quantidade), 0),
        };
    },

    async baixarReserva(id: string): Promise<void> {
        const clinica = clinicaId();
        const { data, error } = await cliente().from('reservas_estoque')
            .select('id, procedimento, status, reserva_itens(produto_id, quantidade_consumo, unidade_consumo, produtos(unidade, conteudo, unidade_conteudo, unidade_consumo))')
            .eq('id', id)
            .single();
        erro('Falha ao ler a reserva', error);

        const reserva = data as unknown as {
            procedimento: string;
            status: string;
            reserva_itens: {
                produto_id: string; quantidade_consumo: number; unidade_consumo: string;
                produtos: { unidade: string; conteudo: number; unidade_conteudo: string; unidade_consumo: string } | null;
            }[];
        };
        if (reserva.status === 'consumida') return;

        const linhas = reserva.reserva_itens
            .filter((i) => i.produtos)
            .map((i) => ({
                clinica_id: clinica,
                produto_id: i.produto_id,
                tipo: 'saida',
                /* Em embalagens, porque é a unidade do saldo — mas levando junto
                   o que foi USADO: "0,5 frasco" não conta a história que "50 UI"
                   conta. */
                quantidade: embalagensDe({
                    unidade: i.produtos!.unidade,
                    conteudo: Number(i.produtos!.conteudo),
                    unidadeConteudo: i.produtos!.unidade_conteudo,
                    unidadeConsumo: i.produtos!.unidade_consumo,
                }, Number(i.quantidade_consumo)),
                quantidade_consumo: Number(i.quantidade_consumo),
                unidade_consumo: i.unidade_consumo,
                origem: `Procedimento — ${reserva.procedimento}`,
                reserva_id: id,
            }));

        if (linhas.length > 0) {
            const { error: erroMovimento } = await cliente().from('movimentacoes_estoque').insert(linhas);
            erro('Falha ao baixar os insumos', erroMovimento);
        }
        const { error: erroStatus } = await cliente().from('reservas_estoque').update({ status: 'consumida' }).eq('id', id);
        erro('Insumos baixados, mas a reserva não fechou', erroStatus);
    },

    async liberarReserva(id: string): Promise<void> {
        /* Apagar, e não marcar: liberar não gera movimentação porque nada saiu
           do armário, e o disponível volta sozinho — ele é derivado das
           reservas abertas. Os itens vão junto pelo `on delete cascade`. */
        const { error } = await cliente().from('reservas_estoque').delete().eq('id', id);
        erro('Falha ao liberar a reserva', error);
    },

    async procedimentos(filtro: FiltroDeProcedimentos): Promise<CargaDeProcedimentos> {
        const [procedimentosResp, produtosResp, membrosResp] = await Promise.all([
            cliente().from('procedimentos').select(CAMPOS_DO_PROCEDIMENTO).order('nome'),
            cliente().from('produtos').select(CAMPOS_DO_PRODUTO).eq('ativo', true).order('nome'),
            cliente().from('membros').select('id, nome').is('arquivado_em', null).eq('acesso', 'ativo').order('nome'),
        ]);
        erro('Falha ao ler os procedimentos', procedimentosResp.error);
        erro('Falha ao ler o estoque', produtosResp.error);

        const catalogo = (produtosResp.data ?? []).map((l) => paraProduto(l as unknown as Record<string, unknown>));
        const porId = new Map(catalogo.map((p) => [p.id, p]));
        const todos = (procedimentosResp.data ?? []).map((l) => paraProcedimento(l as unknown as Record<string, unknown>, porId));

        const busca = filtro.busca?.trim() ?? '';
        const visiveis = todos.filter((p) => (!busca || semAcento(p.nome).includes(semAcento(busca)))
            && (!filtro.categoria || p.categoria === filtro.categoria));
        const pagina = paginar(visiveis, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });

        const ativos = todos.filter((p) => p.ativo);
        const comPreco = ativos.filter((p) => p.precoCentavos > 0);
        const bloqueados = todos.filter((p) => p.execucoesPossiveis === 0);
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
        const categorias = [...new Set(todos.map((p) => p.categoria).filter((c): c is string => !!c))]
            .map((nome) => ({ nome, total: todos.filter((p) => p.categoria === nome).length }));

        return {
            itens: pagina.itens,
            categorias,
            produtos: catalogo,
            profissionais: (membrosResp.data ?? []).map((m) => ({ id: m.id as string, nome: m.nome as string })),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'ativos', rotulo: 'Procedimentos ativos', valor: String(ativos.length), apoio: `${categorias.length} categorias`, icone: 'clipboard-list' },
                { id: 'ticket', rotulo: 'Ticket do catálogo', valor: comPreco.length ? reais(comPreco.reduce((s, p) => s + p.precoCentavos, 0) / comPreco.length) : reais(0), apoio: 'média dos que têm preço', icone: 'circle-dollar-sign' },
                { id: 'custo', rotulo: 'Custo médio', valor: ativos.length ? reais(ativos.reduce((s, p) => s + p.custoDeInsumosCentavos + p.custoExtraCentavos, 0) / ativos.length) : reais(0), apoio: 'insumos e custos extras', icone: 'package' },
                { id: 'bloqueados', rotulo: 'Bloqueados por estoque', valor: String(bloqueados.length), apoio: bloqueados.length ? 'repor insumo' : 'estoque cobre o catálogo', tendencia: bloqueados.length ? 'down' : 'neutral', icone: 'triangle-alert' },
            ],
            bloqueados,
            totais: { procedimentos: todos.length, ativos: ativos.length },
        };
    },

    async salvarProcedimento(dados: DadosDoProcedimento): Promise<Procedimento> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao procedimento.');
        if (dados.duracaoMin <= 0) throw new Error('A duração precisa ser maior que zero.');
        if (dados.precoCentavos < 0) throw new Error('O valor não pode ser negativo.');

        const corpo = {
            nome,
            categoria: dados.categoria ?? null,
            duracao_min: dados.duracaoMin,
            preco_centavos: dados.precoCentavos,
            tipo_valor: dados.tipoValor,
            reconsulta_dias: dados.reconsultaDias,
            cor: dados.cor ?? null,
            descricao: dados.descricao ?? null,
            ativo: dados.ativo,
            campos_personalizados: dados.camposPersonalizados.filter((c) => c.rotulo.trim()),
        };
        const { data, error } = dados.id
            ? await cliente().from('procedimentos').update(corpo).eq('id', dados.id).select('id').single()
            : await cliente().from('procedimentos').insert({ clinica_id: clinica, ...corpo }).select('id').single();
        if (error?.code === '23505') throw new Error(`Já existe um procedimento chamado ${nome}.`);
        erro('Falha ao salvar o procedimento', error);

        const id = (data as { id: string }).id;

        /* As três listas são trocadas inteiras — casar linha a linha deixaria
           insumo órfão no primeiro item removido — e numa transação só
           (`0061`): apagar aqui e inserir depois deixava o procedimento sem
           insumo nenhum quando a segunda chamada falhava. */
        const { error: erroListas } = await cliente().rpc('substituir_listas_do_procedimento', {
            p_procedimento: id,
            p_insumos: dados.insumos.filter((i) => i.quantidade > 0).map((i) => ({ produto_id: i.produtoId, quantidade: i.quantidade })),
            p_extras: dados.custosExtras.filter((c) => c.nome.trim()).map((c) => ({
                nome: c.nome.trim(), quantidade: c.quantidade, unidade: c.unidade, valor_centavos: c.valorCentavos,
            })),
            p_profissionais: dados.profissionaisIds,
        });
        erro('Procedimento salvo, mas insumos, custos e profissionais não mudaram', erroListas);

        const carga = await this.procedimentos({ porPagina: 500 });
        const salvo = carga.itens.find((p) => p.id === id);
        if (!salvo) throw new Error('Procedimento salvo, mas não foi encontrado na releitura.');
        return salvo;
    },

    async reservarInsumos(procedimentoId: string, contexto: ContextoDaReserva): Promise<ReservaDeEstoque> {
        const clinica = clinicaId();
        const carga = await this.procedimentos({ porPagina: 500 });
        const procedimento = carga.itens.find((p) => p.id === procedimentoId);
        if (!procedimento) throw new Error('Procedimento não encontrado.');
        if (procedimento.insumos.length === 0) throw new Error('Este procedimento não consome insumos.');

        /* Marcada, e não recusada: reservar em silêncio deixaria a recepção
           marcar um procedimento que não dá para executar, e recusar esconderia
           que o estoque acabou. */
        const faltando = procedimento.insumos.some((i) => i.disponivel < i.embalagens);

        const { data, error } = await cliente().from('reservas_estoque').insert({
            clinica_id: clinica,
            procedimento_id: procedimento.id,
            procedimento: procedimento.nome,
            contato_id: contexto.contatoId ?? null,
            paciente: contexto.paciente ?? null,
            origem: contexto.origem ?? 'Proposta aceita',
            status: faltando ? 'insuficiente' : 'reservada',
        }).select('id, criado_em').single();
        erro('Falha ao reservar os insumos', error);

        const reserva = data as { id: string; criado_em: string };
        const { error: erroItens } = await cliente().from('reserva_itens').insert(
            procedimento.insumos.map((i) => ({
                clinica_id: clinica, reserva_id: reserva.id, produto_id: i.produtoId,
                quantidade_consumo: i.quantidade, unidade_consumo: i.unidadeConsumo,
            })),
        );
        erro('Reserva criada, mas os insumos não entraram nela', erroItens);

        return {
            id: reserva.id,
            procedimentoId: procedimento.id,
            procedimento: procedimento.nome,
            contatoId: contexto.contatoId,
            paciente: contexto.paciente,
            origem: contexto.origem ?? 'Proposta aceita',
            status: faltando ? 'insuficiente' : 'reservada',
            criadoEm: reserva.criado_em,
            itens: procedimento.insumos.map((i) => ({
                produtoId: i.produtoId, produto: i.produto,
                quantidadeConsumo: i.quantidade, unidadeConsumo: i.unidadeConsumo,
            })),
        };
    },

    async compras(filtro: FiltroDeCompras): Promise<CargaDeCompras> {
        const [pedidosResp, produtosResp, fornecedoresResp] = await Promise.all([
            cliente().from('pedidos_compra').select(CAMPOS_DO_PEDIDO).order('criado_em', { ascending: false }),
            cliente().from('produtos').select(CAMPOS_DO_PRODUTO).eq('ativo', true).order('nome'),
            cliente().from('fornecedores').select('id, nome, cnpj, contato, telefone, email, prazo_entrega, ativo, marcas(nome)').order('nome'),
        ]);
        erro('Falha ao ler os pedidos de compra', pedidosResp.error);
        erro('Falha ao ler o estoque', produtosResp.error);

        const todos = (pedidosResp.data ?? []).map((l) => paraPedido(l as unknown as Record<string, unknown>));
        const catalogo = (produtosResp.data ?? []).map((l) => paraProduto(l as unknown as Record<string, unknown>));
        const emAberto = todos.filter((p) => p.status === 'rascunho' || p.status === 'enviado' || p.status === 'parcial');
        const recebidos = todos.filter((p) => p.status === 'recebido');

        const busca = filtro.busca?.trim() ?? '';
        const bate = (p: PedidoDeCompra) => !busca
            || semAcento(p.numero).includes(semAcento(busca))
            || semAcento(p.fornecedor).includes(semAcento(busca))
            || p.itens.some((i) => semAcento(i.produto).includes(semAcento(busca)));
        const lista = (filtro.aba === 'abertos' ? emAberto : filtro.aba === 'recebidos' ? recebidos : todos).filter(bate);
        const pagina = paginar(lista, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });

        const sugestoes = sugestoesDe(catalogo);
        const itensSugeridos = sugestoes.reduce((s, g) => s + g.itens.length, 0);
        const aReceber = emAberto.reduce((s, p) => s + (p.totalCentavos - p.recebidoCentavos), 0);
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

        return {
            aba: filtro.aba,
            pedidos: filtro.aba === 'sugestao' ? [] : pagina.itens,
            sugestoes,
            fornecedores: (fornecedoresResp.data ?? []).map((f) => ({
                id: f.id as string,
                nome: f.nome as string,
                cnpj: (f.cnpj as string | null) ?? undefined,
                contato: (f.contato as string | null) ?? undefined,
                telefone: (f.telefone as string | null) ?? undefined,
                email: (f.email as string | null) ?? undefined,
                prazoEntrega: (f.prazo_entrega as string | null) ?? undefined,
                ativo: f.ativo as boolean,
                marcas: ((f.marcas as { nome: string }[] | null) ?? []).map((m) => m.nome),
            })),
            produtos: catalogo,
            total: filtro.aba === 'sugestao' ? itensSugeridos : pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'abertos', rotulo: 'Pedidos em aberto', valor: String(emAberto.length), apoio: `${reais(aReceber)} por chegar`, icone: 'truck' },
                { id: 'repor', rotulo: 'Itens a repor', valor: String(itensSugeridos), apoio: `${sugestoes.length} ${sugestoes.length === 1 ? 'fornecedor' : 'fornecedores'}`, tendencia: itensSugeridos ? 'down' : 'neutral', icone: 'package-search' },
                { id: 'sugerido', rotulo: 'Compra sugerida', valor: reais(sugestoes.reduce((s, g) => s + g.totalCentavos, 0)), apoio: 'para voltar ao dobro do mínimo', icone: 'circle-dollar-sign' },
                { id: 'recebidos', rotulo: 'Recebidos', valor: String(recebidos.length), apoio: 'no histórico', icone: 'package-check' },
            ],
            totais: { abertos: emAberto.length, recebidos: recebidos.length, todos: todos.length, sugeridos: itensSugeridos },
        };
    },

    async salvarPedido(dados: DadosDoPedido): Promise<PedidoDeCompra> {
        const clinica = clinicaId();
        const itens = dados.itens.filter((i) => i.quantidade > 0);
        if (itens.length === 0) throw new Error('Escolha ao menos um produto para o pedido.');
        if (!dados.fornecedorId) throw new Error('Escolha o fornecedor.');

        /* Cabeçalho e itens numa transação só (`0061`): trocar os itens apagava
           os antigos antes de saber se os novos entrariam, e uma falha no meio
           deixava o pedido vazio. O número nasce no banco (`0060`). */
        const { data, error } = await cliente().rpc('salvar_pedido_de_compra', {
            p_clinica: clinica,
            p_id: dados.id ?? null,
            p_fornecedor: dados.fornecedorId,
            p_previsao: dados.previsaoEntrega ?? null,
            p_observacoes: dados.observacoes ?? null,
            p_enviar: Boolean(dados.enviar),
            p_itens: itens.map((i) => ({
                produto_id: i.produtoId, quantidade: i.quantidade, custo_unitario_centavos: i.custoUnitarioCentavos,
            })),
        });
        /* `unique (pedido_id, produto_id)`: o mesmo produto duas vezes no
           pedido daria duas entradas de estoque para uma compra só. */
        if (error?.code === '23505') throw new Error('O mesmo produto aparece duas vezes no pedido.');
        if (error) throw new Error(error.message);

        return lerPedido(String(data));
    },

    async receberPedido(dados: Recebimento): Promise<PedidoDeCompra> {
        const chegando = dados.itens.filter((i) => i.quantidade > 0);
        if (chegando.length === 0) throw new Error('Informe o que chegou.');

        /* Entrada no estoque, avanço do item, custo do produto e conta a pagar
           numa transação só (`0065`). Em pedidos separados, uma falha no meio
           deixava metade das entradas — e quem não tem acesso ao financeiro
           recebia a mercadoria sem a conta entrar. A entrada continua pelo
           mesmo caminho da manual: é o gatilho do estoque que mexe no saldo. */
        const { error } = await cliente().rpc('receber_pedido_de_compra', {
            p_pedido: dados.pedidoId,
            p_recebido_em: dados.recebidoEm ?? null,
            p_itens: chegando.map((i) => ({ item_id: i.itemId, quantidade: i.quantidade })),
            p_lancar_conta: Boolean(dados.lancarContaAPagar),
        });
        if (error) throw new Error(error.message);

        /* O status vem do gatilho do banco, então relemos em vez de deduzir. */
        return lerPedido(dados.pedidoId);
    },

    async cancelarPedido(id: string): Promise<void> {
        const { data, error: erroLeitura } = await cliente().from('pedidos_compra').select('status').eq('id', id).single();
        erro('Falha ao ler o pedido', erroLeitura);
        if ((data as { status: string }).status === 'recebido') throw new Error('Pedido já recebido não se cancela.');

        /* Cancelar não desfaz entrada: o que chegou está no estoque e no
           extrato, e apagar isso seria reescrever o inventário. */
        const { error } = await cliente().from('pedidos_compra').update({ status: 'cancelado' }).eq('id', id);
        erro('Falha ao cancelar o pedido', error);
    },

    async documentos(filtro: FiltroDeDocumentos): Promise<CargaDeDocumentos> {
        const [documentosResp, modelosResp, contatosResp, procedimentosResp, membrosResp] = await Promise.all([
            cliente().from('documentos').select(CAMPOS_DO_DOCUMENTO).order('atualizado_em', { ascending: false }),
            cliente().from('documentos_modelos').select(CAMPOS_DO_MODELO).order('atualizado_em', { ascending: false }),
            cliente().from('contatos').select('id, nome').order('nome'),
            cliente().from('procedimentos').select('id, nome').eq('ativo', true).order('nome'),
            cliente().from('membros').select('id, nome').is('arquivado_em', null).eq('acesso', 'ativo').order('nome'),
        ]);
        erro('Falha ao ler os documentos', documentosResp.error);
        erro('Falha ao ler os modelos', modelosResp.error);

        const emitidos = (documentosResp.data ?? []).map((l) => paraDocumento(l as unknown as Record<string, unknown>));
        const modelosTodos = (modelosResp.data ?? []).map((l) => paraModeloDeDocumento(l as unknown as Record<string, unknown>));

        const busca = filtro.busca?.trim() ?? '';
        const bate = (texto: string) => !busca || semAcento(texto).includes(semAcento(busca));
        const aguardando = emitidos.filter((d) => d.status === 'aguardando');
        const assinados = emitidos.filter((d) => d.status === 'assinado');
        const recusados = emitidos.filter((d) => d.status === 'rejeitado');

        const visiveis = emitidos.filter((d) => (bate(d.titulo) || bate(d.paciente))
            && (filtro.aba !== 'assinaturas' || d.status === 'aguardando')
            && (!filtro.tipos?.length || filtro.tipos.includes(d.tipo))
            && (!filtro.status?.length || filtro.status.includes(d.status)));
        const modelos = modelosTodos.filter((m) => bate(m.nome) && (!filtro.favoritos || m.favorito));

        const visivel: unknown[] = filtro.aba === 'modelos' ? modelos : visiveis;
        const pagina = paginar(visivel, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const agora = Date.now();

        return {
            aba: filtro.aba,
            itens: filtro.aba === 'modelos' ? [] : (pagina.itens as Documento[]),
            modelos: filtro.aba === 'modelos' ? (pagina.itens as ModeloDeDocumento[]) : modelos,
            pacientes: (contatosResp.data ?? []).map((c) => ({ id: c.id as string, nome: c.nome as string })),
            procedimentos: (procedimentosResp.data ?? []).map((p) => ({ id: p.id as string, nome: p.nome as string })),
            profissionais: (membrosResp.data ?? []).map((m) => ({ id: m.id as string, nome: m.nome as string })),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'total', rotulo: 'Documentos emitidos', valor: String(emitidos.length), apoio: `${modelosTodos.length} modelos`, icone: 'file-text' },
                { id: 'assinados', rotulo: 'Assinados', valor: String(assinados.length), apoio: emitidos.length ? `${Math.round((assinados.length / emitidos.length) * 100)}% do acervo` : 'nenhum ainda', tendencia: 'up', icone: 'file-check' },
                { id: 'aguardando', rotulo: 'Aguardando assinatura', valor: String(aguardando.length), apoio: 'com a paciente', icone: 'clock' },
                { id: 'recusados', rotulo: 'Recusados', valor: String(recusados.length), apoio: recusados.length ? 'revisar' : 'nenhum', tendencia: recusados.length ? 'down' : 'neutral', icone: 'file-x' },
            ],
            tipos: [...new Set(emitidos.map((d) => d.tipo))].sort(),
            totais: { todos: emitidos.length, aguardando: aguardando.length, modelos: modelosTodos.length },
            parados: aguardando.filter((d) => d.enviadoEm && agora - new Date(d.enviadoEm).getTime() > 48 * 3600 * 1000),
        };
    },

    async salvarModeloDeDocumento(dados: DadosDoModeloDeDocumento): Promise<ModeloDeDocumento> {
        const clinica = clinicaId();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao modelo.');
        if (dados.publicado && !dados.corpo.trim()) throw new Error('Escreva o corpo antes de publicar o modelo.');

        const corpo = {
            nome,
            categoria: dados.categoria,
            assinantes: dados.assinantes,
            corpo: dados.corpo,
            publicado: dados.publicado,
            ...(dados.favorito === undefined ? {} : { favorito: dados.favorito }),
        };
        const { data, error } = dados.id
            ? await cliente().from('documentos_modelos').update(corpo).eq('id', dados.id).select(CAMPOS_DO_MODELO).single()
            : await cliente().from('documentos_modelos').insert({ clinica_id: clinica, ...corpo }).select(CAMPOS_DO_MODELO).single();
        if (error?.code === '23505') throw new Error(`Já existe um modelo chamado ${nome}.`);
        erro('Falha ao salvar o modelo', error);

        return paraModeloDeDocumento(data as unknown as Record<string, unknown>);
    },

    async duplicarModeloDeDocumento(id: string): Promise<ModeloDeDocumento> {
        const clinica = clinicaId();
        const original = await lerModelo(id);

        /* A cópia nasce em rascunho: duplicar é para alterar, e texto que ainda
           vai mudar não pode ser oferecido para assinatura. */
        const { data, error } = await cliente().from('documentos_modelos').insert({
            clinica_id: clinica,
            nome: `${original.nome} (cópia)`,
            categoria: original.categoria,
            assinantes: original.assinantes,
            corpo: original.corpo,
            publicado: false,
            favorito: false,
        }).select(CAMPOS_DO_MODELO).single();
        if (error?.code === '23505') throw new Error('Já existe uma cópia deste modelo.');
        erro('Falha ao duplicar o modelo', error);

        return paraModeloDeDocumento(data as unknown as Record<string, unknown>);
    },

    async removerModeloDeDocumento(id: string): Promise<{ removido: boolean }> {
        const { count } = await cliente().from('documentos').select('id', { count: 'exact', head: true }).eq('modelo_id', id);

        /* Modelo já usado sai de circulação, não some: os documentos emitidos
           apontam para ele, e o termo assinado ficaria sem origem. */
        if ((count ?? 0) > 0) {
            const { error } = await cliente().from('documentos_modelos').update({ publicado: false }).eq('id', id);
            erro('Falha ao despublicar o modelo', error);
            return { removido: false };
        }

        const { error } = await cliente().from('documentos_modelos').delete().eq('id', id);
        erro('Falha ao excluir o modelo', error);
        return { removido: true };
    },

    async emitirDocumento(novo: NovoDocumento): Promise<Documento> {
        const clinica = clinicaId();
        const modelo = await lerModelo(novo.modeloId);
        if (!modelo.publicado) throw new Error('Este modelo ainda é rascunho: publique antes de emitir.');

        const [contatoResp, pacienteResp, procedimentoResp, membroResp, clinicaResp] = await Promise.all([
            cliente().from('contatos').select('id, nome').eq('id', novo.contatoId).single(),
            cliente().from('pacientes').select('cpf').eq('contato_id', novo.contatoId).maybeSingle(),
            novo.procedimentoId
                ? cliente().from('procedimentos').select('nome, preco_centavos').eq('id', novo.procedimentoId).maybeSingle()
                : Promise.resolve({ data: null }),
            novo.profissionalId
                ? cliente().from('membros').select('nome').eq('id', novo.profissionalId).maybeSingle()
                : Promise.resolve({ data: null }),
            cliente().from('clinicas').select('nome').eq('id', clinica).single(),
        ]);
        erro('Falha ao ler o paciente', contatoResp.error);

        const contato = contatoResp.data as { id: string; nome: string };
        const procedimento = procedimentoResp.data as { nome: string; preco_centavos: number } | null;
        const membro = membroResp.data as { nome: string } | null;
        const hoje = new Date().toLocaleDateString('pt-BR', { timeZone: FUSO });

        const corpo = preencherCorpo(modelo.corpo, {
            paciente: contato.nome,
            cpf: (pacienteResp.data as { cpf: string | null } | null)?.cpf ?? undefined,
            procedimento: procedimento?.nome,
            valorCentavos: procedimento?.preco_centavos,
            data: hoje,
            profissional: membro?.nome,
            clinica: (clinicaResp.data as { nome: string } | null)?.nome ?? 'a clínica',
        });

        const semAssinatura = modelo.assinantes === 'Não requer assinatura';
        const agora = new Date().toISOString();
        const { data, error } = await cliente().from('documentos').insert({
            clinica_id: clinica,
            contato_id: contato.id,
            modelo_id: modelo.id,
            titulo: modelo.nome,
            tipo: modelo.categoria === 'Formulário' ? 'Anamnese' : modelo.categoria === 'Contrato' ? 'Contrato' : 'Termo de consentimento',
            corpo,
            assinantes: modelo.assinantes,
            canal: novo.enviar ? novo.canal : null,
            lembrete_automatico: novo.lembreteAutomatico,
            profissional_id: novo.profissionalId ?? null,
            procedimento_id: novo.procedimentoId ?? null,
            /* Modelo que não pede assinatura nasce concluído: ficar
               "aguardando" para sempre é fila que ninguém limpa. */
            status: !novo.enviar ? 'rascunho' : semAssinatura ? 'assinado' : 'aguardando',
            paginas: Math.max(1, Math.ceil(corpo.length / 1800)),
            enviado_em: novo.enviar ? agora : null,
            assinado_em: novo.enviar && semAssinatura ? agora : null,
        }).select('id').single();
        erro('Falha ao emitir o documento', error);

        return lerDocumento((data as { id: string }).id);
    },

    async decidirDocumento(id: string, status: StatusDoDocumento, motivo?: string): Promise<Documento> {
        if (status === 'rejeitado' && !motivo?.trim()) {
            throw new Error('Diga por que o documento foi recusado.');
        }

        const agora = new Date().toISOString();
        const { error } = await cliente().from('documentos').update({
            status,
            assinado_em: status === 'assinado' ? agora : null,
            recusado_em: status === 'rejeitado' ? agora : null,
            motivo_da_recusa: status === 'rejeitado' ? motivo?.trim() : null,
        }).eq('id', id);
        erro('Falha ao atualizar o documento', error);

        return lerDocumento(id);
    },

    async relatorios(filtro: FiltroDeRelatorios): Promise<CargaDeRelatorios> {
        const [agendaResp, procedimentosResp, jornadaResp] = await Promise.all([
            cliente().from('agendamentos')
                .select('inicio_em, duracao_min, status, procedimentos(nome, preco_centavos), profissional:membros!profissional_id(nome),'
                    + ' atendimentos(iniciado_em, encerrado_em, fichas_preenchidas(id))')
                .neq('status', 'bloqueio')
                .order('inicio_em'),
            cliente().from('procedimentos').select('nome, preco_centavos'),
            /* A jornada é o denominador da ocupação: sem ela o relatório volta
               a comparar os dias entre si. */
            cliente().from('horarios_funcionamento')
                .select('dia_semana, inicio, fim').is('unidade_id', null).order('dia_semana').order('inicio'),
        ]);
        erro('Falha ao ler a agenda', agendaResp.error);
        erro('Falha ao ler a jornada', jornadaResp.error);

        /* Financeiro e funil vêm das mesmas leituras que as telas deles usam —
           uma segunda aritmética aqui divergiria da primeira. */
        const [financeiro, pipeline, vendas] = await Promise.all([
            this.financeiro({ aba: 'extrato', porPagina: 2000 }),
            this.pipeline({}),
            this.vendas({ aba: 'orcamentos', porPagina: 500 }),
        ]);

        const precos: Record<string, number> = {};
        for (const p of (procedimentosResp.data ?? []) as unknown as { nome: string; preco_centavos: number }[]) {
            precos[p.nome] = Number(p.preco_centavos ?? 0);
        }

        const linhas: AgendamentoDoRelatorio[] = ((agendaResp.data ?? []) as unknown as Record<string, unknown>[]).map((l) => {
            const procedimento = (Array.isArray(l.procedimentos) ? l.procedimentos[0] : l.procedimentos) as { nome?: string; preco_centavos?: number } | null;
            /* `atendimentos.agendamento_id` é `unique`: o PostgREST enxerga um
               para-um e devolve OBJETO, não lista. Tratar como lista faz o
               atendimento sumir em silêncio — e com ele a duração executada e a
               ficha preenchida. */
            const atendimento = (Array.isArray(l.atendimentos) ? l.atendimentos[0] : l.atendimentos) as
                { iniciado_em: string | null; encerrado_em: string | null; fichas_preenchidas: { id: string }[] } | null;
            const duracaoRealMin = atendimento?.iniciado_em && atendimento?.encerrado_em
                ? Math.max(1, Math.round((new Date(atendimento.encerrado_em).getTime() - new Date(atendimento.iniciado_em).getTime()) / 60000))
                : undefined;
            if (procedimento?.nome && precos[procedimento.nome] === undefined) {
                precos[procedimento.nome] = Number(procedimento.preco_centavos ?? 0);
            }
            return {
                inicioEm: l.inicio_em as string,
                duracaoMin: Number(l.duracao_min ?? 0),
                status: l.status as AgendamentoDoRelatorio['status'],
                procedimento: procedimento?.nome ?? '—',
                profissional: nomeEmbutido(l.profissional) ?? '',
                duracaoRealMin,
                comFicha: (atendimento?.fichas_preenchidas?.length ?? 0) > 0,
            };
        });

        const carga = montarRelatorio(
            {
                agendamentos: linhas,
                precos,
                movimentacoes: financeiro.movimentacoes,
                leads: pipeline.colunas.flatMap((c) => c.leads),
                orcamentos: vendas.orcamentos,
                jornada: paraJornada(jornadaResp.data as unknown as LinhaDeHorario[] | null),
            },
            filtro,
            new Date().toLocaleDateString('en-CA', { timeZone: FUSO }),
        );

        return {
            ...carga,
            profissionais: [...new Set(linhas.map((l) => l.profissional).filter(Boolean))].sort(),
        };
    },

    async configuracoes(aba: AbaDeConfiguracoes): Promise<CargaDeConfiguracoes> {
        const [clinicaResp, unidadesResp, horariosResp, salasResp, lembretesResp, canaisResp, equipe, pareamentoDisponivel, vagasResp] = await Promise.all([
            cliente().from('clinicas')
                .select('id, nome, razao_social, documento, tipo_pessoa, email, fuso, plano')
                .eq('id', clinicaId()).single(),
            cliente().from('unidades')
                .select('id, nome, telefone, email, cep, estado, cidade, bairro, rua, numero, complemento')
                .is('arquivado_em', null).order('nome'),
            cliente().from('horarios_funcionamento')
                .select('dia_semana, inicio, fim').is('unidade_id', null).order('dia_semana').order('inicio'),
            cliente().from('salas').select('id, nome, ativa').order('nome'),
            cliente().from('lembretes_regua')
                .select('id, quando, descricao, ativo, canal, offset_min')
                .order('offset_min', { ascending: false }),
            this.canaisDaClinica(),
            /* A equipe é a MESMA lista da aba Profissionais de Contatos. Uma
               segunda consulta com outro mapeamento daria duas verdades sobre
               quem trabalha aqui. */
            this.contatos('profissional', { porPagina: 200 }),
            this.pareamentoDisponivel(),
            /* As vagas do plano (0165). Números, não linhas: a tela mostra
               "4 de 20" e some com o botão quando lotar — mas quem recusa de
               verdade é o gatilho do banco. */
            cliente().rpc('vagas_de_membros'),
        ]);
        erro('Falha ao ler os dados da clínica', clinicaResp.error);
        erro('Falha ao ler as unidades', unidadesResp.error);
        erro('Falha ao ler a jornada', horariosResp.error);
        erro('Falha ao ler as salas', salasResp.error);
        erro('Falha ao ler a régua de lembretes', lembretesResp.error);

        const c = clinicaResp.data as unknown as Record<string, unknown> | null;
        const horarios = paraJornada(horariosResp.data as unknown as LinhaDeHorario[] | null);

        return {
            aba,
            clinica: {
                id: String(c?.id ?? ''),
                nome: String(c?.nome ?? ''),
                razaoSocial: (c?.razao_social as string | null) ?? '',
                documento: (c?.documento as string | null) ?? '',
                tipoPessoa: (c?.tipo_pessoa as DadosDaClinica['tipoPessoa']) ?? 'juridica',
                email: (c?.email as string | null) ?? '',
                fuso: String(c?.fuso ?? FUSO),
                plano: String(c?.plano ?? ''),
            },
            unidades: ((unidadesResp.data ?? []) as unknown as Record<string, unknown>[]).map((u) => ({
                id: String(u.id),
                nome: String(u.nome ?? ''),
                telefone: (u.telefone as string | null) ?? '',
                email: (u.email as string | null) ?? '',
                cep: (u.cep as string | null) ?? '',
                estado: (u.estado as string | null) ?? '',
                cidade: (u.cidade as string | null) ?? '',
                bairro: (u.bairro as string | null) ?? '',
                rua: (u.rua as string | null) ?? '',
                numero: (u.numero as string | null) ?? '',
                complemento: (u.complemento as string | null) ?? '',
            })),
            horarios,
            horasSemanais: horasSemanais(horarios),
            salas: ((salasResp.data ?? []) as unknown as Record<string, unknown>[]).map((s) => ({
                id: String(s.id), nome: String(s.nome ?? ''), ativa: Boolean(s.ativa),
            })),
            equipe: equipe.tipo === 'profissional' ? equipe.itens : [],
            lembretes: ((lembretesResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraLembrete),
            /* A mesma lista da tela de integrações, com o estado da credencial:
               duas leituras de canal dariam duas verdades sobre o pareamento. */
            canais: canaisResp,
            pareamentoDisponivel,
            /* Sem linha (conta sem clínica resolvida) a tela não inventa teto:
               limite nulo é "sem teto", e o banco continua sendo quem recusa. */
            vagas: paraVagas((vagasResp.data ?? [])[0] as Record<string, unknown> | undefined, equipe.tipo === 'profissional' ? equipe.itens : []),
        };
    },

    async salvarDadosDaClinica(mudancas: EdicaoDaClinica): Promise<DadosDaClinica> {
        const { error } = await cliente().from('clinicas').update({
            nome: mudancas.nome,
            razao_social: mudancas.razaoSocial,
            documento: mudancas.documento,
            tipo_pessoa: mudancas.tipoPessoa,
            email: mudancas.email,
            fuso: mudancas.fuso,
        }).eq('id', clinicaId());
        erro('Falha ao salvar os dados da clínica', error);
        return (await this.configuracoes('clinica')).clinica;
    },

    async salvarUnidade(id: string, mudancas: EdicaoDaUnidade): Promise<UnidadeDaClinica> {
        const { error } = await cliente().from('unidades').update({
            nome: mudancas.nome,
            telefone: mudancas.telefone,
            email: mudancas.email,
            cep: mudancas.cep,
            estado: mudancas.estado,
            cidade: mudancas.cidade,
            bairro: mudancas.bairro,
            rua: mudancas.rua,
            numero: mudancas.numero,
            complemento: mudancas.complemento,
        }).eq('id', id);
        erro('Falha ao salvar a unidade', error);
        const carga = await this.configuracoes('clinica');
        const unidade = carga.unidades.find((u) => u.id === id);
        if (!unidade) throw new Error(`Unidade ${id} não existe.`);
        return unidade;
    },

    async salvarHorarios(dias: DiaDeFuncionamento[]): Promise<DiaDeFuncionamento[]> {
        /* A semana vai inteira numa chamada só: `salvar_jornada` apaga e
           reinsere dentro de uma transação. Apagar e inserir daqui deixaria a
           clínica fechada a semana toda para quem lesse no meio. */
        const faixas = normalizarDias(dias).flatMap((d) => d.faixas.map((f) => ({
            diaSemana: d.diaSemana, inicio: f.inicio, fim: f.fim,
        })));
        const { data, error } = await cliente().rpc('salvar_jornada', { p_dias: faixas, p_clinica: clinicaId() });
        erro('Falha ao salvar a jornada', error);
        return paraJornada((data ?? []) as unknown as LinhaDeHorario[]);
    },

    async automacoes(aba: AbaDeAutomacoes): Promise<CargaDeAutomacoes> {
        const [linhasResp, canaisResp, alcances] = await Promise.all([
            cliente().from('automacoes')
                .select('id, nome, gatilho, acao, ativa, execucoes, ultima_execucao_em').order('nome'),
            cliente().from('canais').select('status').eq('status', 'conectado'),
            alcancesDasAutomacoes(),
        ]);
        erro('Falha ao ler as automações', linhasResp.error);
        erro('Falha ao ler os canais', canaisResp.error);

        const conectado = (canaisResp.data ?? []).length > 0;
        const lista = ((linhasResp.data ?? []) as unknown as Record<string, unknown>[]).map((l) => {
            const linha: LinhaDeAutomacao = {
                id: String(l.id),
                nome: String(l.nome),
                gatilho: l.gatilho as LinhaDeAutomacao['gatilho'],
                acao: l.acao as LinhaDeAutomacao['acao'],
                ativa: Boolean(l.ativa),
                execucoes: Number(l.execucoes ?? 0),
                ...(l.ultima_execucao_em ? { ultimaExecucaoEm: String(l.ultima_execucao_em) } : {}),
            };
            return montarAutomacao(linha, alcances[linha.gatilho], conectado);
        });

        return {
            aba,
            automacoes: lista.filter((a) => aba === 'todas' || (aba === 'ativas' ? a.ativa : !a.ativa)),
            indicadores: indicadoresDeAutomacoes(lista),
            canalConectado: conectado,
            totais: {
                ativas: lista.filter((a) => a.ativa).length,
                pausadas: lista.filter((a) => !a.ativa).length,
                todas: lista.length,
            },
        };
    },

    async alternarAutomacao(id: string, ativa: boolean): Promise<Automacao> {
        const { data, error } = await cliente().from('automacoes').update({ ativa }).eq('id', id)
            .select('id, nome, gatilho, acao, ativa, execucoes, ultima_execucao_em').single();
        erro('Falha ao mudar a automação', error);
        const l = data as unknown as Record<string, unknown>;
        const [alcances, canais] = await Promise.all([
            alcancesDasAutomacoes(),
            cliente().from('canais').select('status').eq('status', 'conectado'),
        ]);
        const linha: LinhaDeAutomacao = {
            id: String(l.id), nome: String(l.nome),
            gatilho: l.gatilho as LinhaDeAutomacao['gatilho'],
            acao: l.acao as LinhaDeAutomacao['acao'],
            ativa: Boolean(l.ativa), execucoes: Number(l.execucoes ?? 0),
            ...(l.ultima_execucao_em ? { ultimaExecucaoEm: String(l.ultima_execucao_em) } : {}),
        };
        return montarAutomacao(linha, alcances[linha.gatilho], (canais.data ?? []).length > 0);
    },

    async executarAutomacao(id: string): Promise<ResultadoDaExecucao> {
        const carga = await this.automacoes('todas');
        const automacao = carga.automacoes.find((a) => a.id === id);
        if (!automacao) throw new Error(`Automação ${id} não existe.`);
        if (!automacao.executavel) throw new Error(automacao.bloqueio ?? 'Esta automação não pode ser executada agora.');

        const titulos = await titulosDeTarefaDaAutomacao(automacao.gatilho);
        let criadas = 0;
        let repetidas = 0;
        for (const titulo of titulos) {
            /* O índice único `tarefa_unica_por_automacao` recusa a repetida —
               é o banco que garante, não uma conferência que pode correr em
               paralelo com outra execução. */
            const { error } = await cliente().from('tarefas').insert({
                clinica_id: clinicaId(), titulo, automacao_id: id,
                vence_em: new Date().toLocaleDateString('en-CA', { timeZone: FUSO }),
                prioridade: 'media',
            });
            if (error?.code === '23505') repetidas += 1;
            else if (error) throw new Error(`Falha ao criar tarefa: ${error.message}`);
            else criadas += 1;
        }

        await cliente().from('automacoes').update({
            execucoes: automacao.execucoes + 1,
            ultima_execucao_em: new Date().toISOString(),
        }).eq('id', id);

        return {
            criadas,
            repetidas,
            descricao: criadas
                ? `${criadas} ${criadas === 1 ? 'tarefa criada' : 'tarefas criadas'} para a equipe`
                : 'Nada a fazer: as tarefas desta regra já estão abertas',
        };
    },

    async marketing(aba: AbaDeMarketing): Promise<CargaDeMarketing> {
        const mes = new Date().toLocaleDateString('en-CA', { timeZone: FUSO }).slice(0, 7);
        const [campanhasResp, leadsResp, pacientesResp, canaisResp, semRetorno, totalDePacientes] = await Promise.all([
            cliente().from('campanhas')
                .select('id, nome, canal, publico_tipo, publico_param, origem_lead, status, inicio, fim, investimento_centavos')
                .order('criado_em', { ascending: false }),
            cliente().from('leads').select('origem, etapa, valor_estimado_centavos, criado_em'),
            cliente().from('pacientes').select('contato_id, nascimento'),
            cliente().from('canais').select('status').eq('status', 'conectado'),
            contatosSemRetorno(90),
            /* Contado no servidor: a lista acima pode vir cortada no `max_rows`. */
            cliente().from('pacientes').select('contato_id', { count: 'exact', head: true }),
        ]);
        erro('Falha ao ler as campanhas', campanhasResp.error);
        erro('Falha ao ler os leads', leadsResp.error);

        const leads = ((leadsResp.data ?? []) as unknown as Record<string, unknown>[]).map((l) => ({
            origem: String(l.origem ?? 'Sem origem'),
            situacao: String(l.etapa ?? ''),
            valorCentavos: Number(l.valor_estimado_centavos ?? 0),
            criadoEm: String(l.criado_em ?? '').slice(0, 10),
        }));
        const nascimentos = ((pacientesResp.data ?? []) as unknown as { nascimento: string | null }[])
            .map((p) => p.nascimento).filter((n): n is string => !!n);

        /* A conta mora em `publicos.ts`. "Todos os pacientes" contava só quem
           tinha data de nascimento cadastrada. */
        const dadosDosPublicos = {
            semRetorno: semRetorno.length,
            nascimentos,
            totalPacientes: totalDePacientes.count ?? ((pacientesResp.data ?? []) as unknown[]).length,
            leads,
            mes,
        };
        const tamanhoDoPublico = (tipo: PublicoDaCampanha, param: Campanha['publicoParam']): number =>
            tamanhoDoPublicoDe(tipo, param, dadosDosPublicos);

        const lista: Campanha[] = ((campanhasResp.data ?? []) as unknown as Record<string, unknown>[]).map((c) => {
            const publicoParam = (c.publico_param ?? {}) as Campanha['publicoParam'];
            const origemLead = (c.origem_lead as string | null) ?? undefined;
            const inicio = (c.inicio as string | null) ?? undefined;
            const fim = (c.fim as string | null) ?? undefined;
            const doPeriodo = origemLead
                ? leads.filter((l) => l.origem === origemLead
                    && (!inicio || l.criadoEm >= inicio)
                    && (!fim || l.criadoEm <= fim))
                : [];
            const ganhos = doPeriodo.filter((l) => l.situacao === 'ganho');
            const receitaCentavos = ganhos.reduce((s, l) => s + l.valorCentavos, 0);
            const investimentoCentavos = Number(c.investimento_centavos ?? 0);

            return {
                id: String(c.id),
                nome: String(c.nome),
                canal: c.canal as Campanha['canal'],
                publicoTipo: c.publico_tipo as PublicoDaCampanha,
                publicoParam,
                publicoTamanho: tamanhoDoPublico(c.publico_tipo as PublicoDaCampanha, publicoParam),
                ...(origemLead ? { origemLead } : {}),
                status: c.status as Campanha['status'],
                ...(inicio ? { inicio } : {}),
                ...(fim ? { fim } : {}),
                investimentoCentavos,
                leads: doPeriodo.length,
                ganhos: ganhos.length,
                receitaCentavos,
                ...derivadosDaCampanha({ investimentoCentavos, leads: doPeriodo.length, receitaCentavos }),
            };
        });

        const porOrigem = new Map<string, LinhaDeOrigem>();
        for (const l of leads.filter((x) => x.criadoEm.startsWith(mes))) {
            const atual = porOrigem.get(l.origem)
                ?? { origem: l.origem, leads: 0, ganhos: 0, receitaCentavos: 0, investimentoCentavos: 0 };
            atual.leads += 1;
            if (l.situacao === 'ganho') {
                atual.ganhos += 1;
                atual.receitaCentavos += l.valorCentavos;
            }
            porOrigem.set(l.origem, atual);
        }
        for (const c of lista) {
            if (!c.origemLead || !c.investimentoCentavos) continue;
            const atual = porOrigem.get(c.origemLead)
                ?? { origem: c.origemLead, leads: 0, ganhos: 0, receitaCentavos: 0, investimentoCentavos: 0 };
            atual.investimentoCentavos += c.investimentoCentavos;
            porOrigem.set(c.origemLead, atual);
        }
        const origens = [...porOrigem.values()].sort((a, b) => b.leads - a.leads);

        const publicos = publicosDisponiveis({
            'sem-retorno': tamanhoDoPublico('sem-retorno', { dias: 90 }),
            'aniversariantes': tamanhoDoPublico('aniversariantes', {}),
            'por-origem': tamanhoDoPublico('por-origem', { origem: origens[0]?.origem }),
            'todos-pacientes': tamanhoDoPublico('todos-pacientes', {}),
        });

        return {
            aba,
            campanhas: aba === 'ativas' ? lista.filter((c) => c.status === 'ativa') : lista,
            publicos,
            origens,
            indicadores: indicadoresDeMarketing(lista, origens),
            canalConectado: (canaisResp.data ?? []).length > 0,
            totais: {
                campanhas: lista.length,
                ativas: lista.filter((c) => c.status === 'ativa').length,
                publicos: publicos.length,
            },
        };
    },

    async plataforma(): Promise<CargaDaPlataforma> {
        /* Quatro funções do banco, todas `security definer` com o filtro de
           operador dentro: para qualquer outra pessoa elas devolvem vazio, e
           não erro — o painel simplesmente não tem o que mostrar. */
        const [clinicasResp, usuariosResp, auditoriaResp, impersonacoesResp] = await Promise.all([
            cliente().rpc('painel_das_clinicas'),
            cliente().rpc('painel_dos_usuarios'),
            cliente().rpc('painel_da_auditoria', { p_limite: 100 }),
            cliente().rpc('painel_das_impersonacoes', { p_limite: 50 }),
        ]);
        erro('Falha ao ler as clínicas', clinicasResp.error);
        erro('Falha ao ler os usuários', usuariosResp.error);
        erro('Falha ao ler a auditoria', auditoriaResp.error);
        erro('Falha ao ler as impersonações', impersonacoesResp.error);

        const clinicas = ((clinicasResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraClinicaDaPlataforma);
        return {
            clinicas,
            usuarios: ((usuariosResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraUsuarioDaPlataforma),
            auditoria: ((auditoriaResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraEventoDeAuditoria),
            impersonacoes: ((impersonacoesResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraImpersonacao),
            indicadores: indicadoresDaPlataforma(clinicas),
        };
    },

    async clinicaDaPlataforma(id: string): Promise<CargaDaClinicaNaPlataforma> {
        const carga = await this.plataforma();
        const clinica = carga.clinicas.find((c) => c.id === id);
        /* 404 de verdade: ficha de clínica que não existe não é ficha vazia. */
        if (!clinica) throw new Response('Clínica não encontrada', { status: 404 });

        /* As unidades vêm por função do painel, e não da tabela: a RLS de
           `unidades` só responde a membro, e abri-la daria ao operador a tabela
           inteira. A função dá o cadastro e a contagem de salas. */
        const [unidadesResp, creditosResp, descontosResp, vagasResp, faturamento, consumoDeIa, orcamentos] = await Promise.all([
            cliente().rpc('painel_das_unidades', { p_clinica: id }),
            cliente().rpc('painel_dos_creditos', { p_clinica: id }),
            cliente().rpc('painel_dos_descontos', { p_clinica: id }),
            cliente().rpc('painel_das_vagas', { p_clinica: id }),
            this.faturamento(),
            this.consumoDeIa(30),
            this.orcamentosDeIa(),
        ]);
        erro('Falha ao ler as unidades', unidadesResp.error);
        erro('Falha ao ler os créditos', creditosResp.error);
        erro('Falha ao ler os descontos', descontosResp.error);

        const assinatura = faturamento.assinaturas.find((a) => a.clinicaId === id);
        const orcamentoDeIa = orcamentos.find((o) => o.clinicaId === id);

        return {
            clinica,
            unidades: ((unidadesResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraUnidadeDoPainel),
            usuarios: carga.usuarios.filter((u) => u.clinica === clinica.nome),
            auditoria: carga.auditoria.filter((a) => (a.clinicaId ? a.clinicaId === id : a.clinica === clinica.nome)),
            impersonacoes: carga.impersonacoes.filter((i) => i.clinica === clinica.nome),
            planos: faturamento.planos.filter((p) => p.ativo),
            ...(assinatura ? { assinatura } : {}),
            faturas: faturamento.faturas.filter((f) => f.clinicaId === id),
            creditos: ((creditosResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraCredito),
            descontos: ((descontosResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraDesconto),
            consumoDeIa: consumoDeIa.filter((l) => l.clinicaId === id),
            ...(orcamentoDeIa ? { orcamentoDeIa } : {}),
            vagas: paraVagasDoPainel((vagasResp.data ?? [])[0] as Record<string, unknown> | undefined),
        };
    },

    async faturamento(): Promise<CargaDoFaturamento> {
        const [planosResp, assinaturasResp, faturasResp] = await Promise.all([
            cliente().from('planos')
                .select('id, codigo, nome, ciclo, preco_centavos, limite_unidades, limite_membros, ativo')
                .order('nome'),
            cliente().rpc('painel_das_assinaturas'),
            cliente().rpc('painel_das_faturas', { p_limite: 200 }),
        ]);
        erro('Falha ao ler os planos', planosResp.error);
        erro('Falha ao ler as assinaturas', assinaturasResp.error);
        erro('Falha ao ler as faturas', faturasResp.error);

        const assinaturas = ((assinaturasResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraAssinatura);
        const faturas = ((faturasResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraFaturaDaPlataforma);
        const planos = ((planosResp.data ?? []) as unknown as Record<string, unknown>[]).map((p) => ({
            ...paraPlano(p),
            clinicas: assinaturas.filter((a) => a.planoCodigo === String(p.codigo) && !a.canceladaEm).length,
        }));

        return {
            planos,
            assinaturas,
            faturas,
            indicadores: indicadoresDoFaturamento(planos, assinaturas, faturas),
            temPreco: planos.some((p) => p.precoCentavos !== undefined),
        };
    },

    async salvarPlano(id: string | null, dados: DadosDoPlano): Promise<Plano> {
        const codigo = dados.codigo.trim();
        if (!codigo) throw new Error('O plano precisa de um código.');

        const linha = {
            codigo,
            nome: dados.nome.trim() || codigo,
            ciclo: dados.ciclo,
            /* `null` apaga o preço de propósito: voltar a "sem preço decidido"
               tem de ser possível, e não é a mesma coisa que preço zero. */
            preco_centavos: dados.precoCentavos === undefined ? null : dados.precoCentavos,
            limite_unidades: dados.limiteUnidades ?? null,
            limite_membros: dados.limiteMembros ?? null,
            ativo: dados.ativo,
        };
        const resposta = id
            ? await cliente().from('planos').update(linha).eq('id', id)
                .select('id, codigo, nome, ciclo, preco_centavos, limite_unidades, limite_membros, ativo').single()
            : await cliente().from('planos').insert(linha)
                .select('id, codigo, nome, ciclo, preco_centavos, limite_unidades, limite_membros, ativo').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe um plano com o código ${codigo}.`);
        erro('Falha ao salvar o plano', resposta.error);

        const carga = await this.faturamento();
        const salvo = carga.planos.find((p) => p.codigo === codigo);
        if (!salvo) throw new Error('Plano salvo, mas não encontrado na releitura.');
        return salvo;
    },

    async gerarFaturas(competencia: string): Promise<{ criadas: number }> {
        const { data, error } = await cliente().rpc('gerar_faturas', { p_competencia: competencia });
        erro('Falha ao gerar as faturas', error);
        return { criadas: Number(data ?? 0) };
    },

    async baixarFatura(id: string, pagoEm: string | null): Promise<Fatura> {
        const { error } = await cliente().from('faturas').update({ pago_em: pagoEm }).eq('id', id);
        erro('Falha ao baixar a fatura', error);
        const carga = await this.faturamento();
        const fatura = carga.faturas.find((f) => f.id === id);
        if (!fatura) throw new Error(`Fatura ${id} não existe.`);
        return fatura;
    },

    async onboarding(): Promise<CargaDoOnboarding> {
        const { data, error } = await cliente().rpc('painel_do_onboarding');
        erro('Falha ao ler o onboarding', error);

        const clinicas: OnboardingDaClinica[] = ((data ?? []) as unknown as Record<string, unknown>[]).map((l) => {
            const etapas = etapasDe({
                temCadastro: Boolean(l.tem_cadastro),
                temEndereco: Boolean(l.tem_endereco),
                temJornada: Boolean(l.tem_jornada),
                temLogin: Boolean(l.tem_login),
                temProcedimentos: Boolean(l.tem_procedimentos),
                temCanal: Boolean(l.tem_canal),
                temAtendimento: Boolean(l.tem_atendimento),
            });
            return {
                clinicaId: String(l.clinica_id),
                clinica: String(l.clinica ?? ''),
                situacao: l.situacao as OnboardingDaClinica['situacao'],
                criadoEm: String(l.criado_em ?? ''),
                etapas,
                concluidas: etapas.filter((e) => e.concluida).length,
                total: etapas.length,
            };
        });

        return { clinicas, indicadores: indicadoresDoOnboarding(clinicas) };
    },

    async suporte(): Promise<CargaDoSuporte> {
        const [chamadosResp, clinicasResp, impersonacoesResp] = await Promise.all([
            cliente().rpc('painel_dos_chamados', { p_limite: 200 }),
            cliente().rpc('painel_das_clinicas'),
            cliente().rpc('painel_das_impersonacoes', { p_limite: 100 }),
        ]);
        erro('Falha ao ler os chamados', chamadosResp.error);
        erro('Falha ao ler as clínicas', clinicasResp.error);
        erro('Falha ao ler as impersonações', impersonacoesResp.error);

        const chamados = ((chamadosResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraChamadoDaPlataforma);
        return {
            chamados,
            indicadores: indicadoresDoSuporte(chamados),
            clinicas: ((clinicasResp.data ?? []) as unknown as Record<string, unknown>[])
                .map((c) => ({ id: String(c.id), nome: String(c.nome ?? '') })),
            impersonacoes: ((impersonacoesResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraImpersonacao),
        };
    },

    async salvarChamado(id: string | null, dados: DadosDoChamado): Promise<Chamado> {
        const assunto = dados.assunto.trim();
        if (!assunto) throw new Error('O chamado precisa de um assunto.');
        if (!dados.clinicaId) throw new Error('O chamado precisa de uma clínica.');

        const linha = {
            clinica_id: dados.clinicaId,
            assunto,
            descricao: dados.descricao,
            canal: dados.canal,
            prioridade: dados.prioridade,
            situacao: dados.situacao,
            responsavel_nome: dados.responsavelNome ?? null,
            /* O `check` do banco recusa resolvido sem data; a data entra aqui,
               no momento em que a situação vira resolvido. */
            resolvido_em: dados.situacao === 'resolvido' ? new Date().toISOString() : null,
        };

        const resposta = id
            ? await cliente().from('chamados').update(linha).eq('id', id).select('id').single()
            : await cliente().from('chamados').insert(linha).select('id').single();
        erro('Falha ao salvar o chamado', resposta.error);

        const salvo = String((resposta.data as unknown as { id: string }).id);
        const carga = await this.suporte();
        const chamado = carga.chamados.find((c) => c.id === salvo);
        if (!chamado) throw new Error('Chamado salvo, mas não encontrado na releitura.');
        return chamado;
    },

    async flags(): Promise<CargaDasFlags> {
        const [flagsResp, clinicasResp] = await Promise.all([
            cliente().rpc('painel_das_flags'),
            cliente().rpc('painel_das_clinicas'),
        ]);
        erro('Falha ao ler as flags', flagsResp.error);
        erro('Falha ao ler as clínicas', clinicasResp.error);

        return {
            flags: ((flagsResp.data ?? []) as unknown as Record<string, unknown>[]).map(paraFlag),
            clinicas: ((clinicasResp.data ?? []) as unknown as Record<string, unknown>[])
                .map((c) => ({ id: String(c.id), nome: String(c.nome ?? '') })),
        };
    },

    async definirPadraoDaFlag(id: string, ativo: boolean): Promise<FlagDeModulo> {
        const { error } = await cliente().from('flags').update({ ativo_padrao: ativo }).eq('id', id);
        erro('Falha ao mudar a flag', error);
        return flagPorId(await this.flags(), id);
    },

    async definirExcecaoDaFlag(id: string, clinicaId: string, ativo: boolean | null): Promise<FlagDeModulo> {
        /* Nulo REMOVE a exceção: voltar ao padrão é diferente de gravar uma
           exceção que por acaso coincide com ele — a segunda não acompanha o
           padrão quando ele muda. */
        const resposta = ativo === null
            ? await cliente().from('flags_clinica').delete().eq('flag_id', id).eq('clinica_id', clinicaId)
            : await cliente().from('flags_clinica').upsert(
                { flag_id: id, clinica_id: clinicaId, ativo, atualizado_em: new Date().toISOString() },
                { onConflict: 'flag_id,clinica_id' },
            );
        erro('Falha ao mudar a exceção', resposta.error);
        return flagPorId(await this.flags(), id);
    },

    async canaisDaClinica(): Promise<CanalDeIntegracao[]> {
        const [canaisResp, credenciaisResp, conversas] = await Promise.all([
            cliente().from('canais')
                .select('id, nome, tipo, provedor, numero_e164, instancia_externa_id, status, ultima_conexao_em, ultimo_erro, ativo, fila_padrao_id, atendente_padrao_id')
                .order('nome'),
            cliente().rpc('credenciais_dos_canais'),
            lerTudo<Record<string, unknown>>((de, ate) => cliente().from('conversas')
                .select('id, canal_id, estado, nao_lidas').order('id').range(de, ate)),
        ]);
        erro('Falha ao ler os canais', canaisResp.error);
        erro('Falha ao ler o estado das credenciais', credenciaisResp.error);

        const credenciais = new Map(
            ((credenciaisResp.data ?? []) as unknown as Record<string, unknown>[])
                .map((c) => [String(c.canal_id), c]),
        );
        /* Eram zero fixo: a tela de integrações dizia que o canal não tinha
           conversa nenhuma enquanto a caixa de entrada estava cheia. */
        const porCanal = contagemPorCanal(conversas);

        return ((canaisResp.data ?? []) as unknown as Record<string, unknown>[]).map((c) => {
            const cred = credenciais.get(String(c.id));
            return {
                id: String(c.id),
                nome: String(c.nome ?? ''),
                tipo: c.tipo as Canal['tipo'],
                provedor: String(c.provedor ?? ''),
                ...(c.instancia_externa_id ? { instanciaExternaId: String(c.instancia_externa_id) } : {}),
                ...(c.numero_e164 ? { numero: String(c.numero_e164) } : {}),
                status: c.status as Canal['status'],
                ...(c.ultima_conexao_em ? { ultimaConexaoEm: String(c.ultima_conexao_em) } : {}),
                ...(c.ultimo_erro ? { ultimoErro: String(c.ultimo_erro) } : {}),
                conversasAbertas: porCanal.get(String(c.id))?.abertas ?? 0,
                naoLidas: porCanal.get(String(c.id))?.naoLidas ?? 0,
                temCredencial: Boolean(cred),
                ...(c.ativo === false ? { ativo: false } : {}),
                ...(c.fila_padrao_id ? { filaPadraoId: String(c.fila_padrao_id) } : {}),
                ...(c.atendente_padrao_id ? { atendentePadraoId: String(c.atendente_padrao_id) } : {}),
                ...(cred?.criado_em ? { credencialEm: String(cred.criado_em) } : {}),
                ...(cred?.rotacionado_em ? { credencialRotacionadaEm: String(cred.rotacionado_em) } : {}),
            };
        });
    },

    async salvarCanal(id: string | null, dados: DadosDoCanal): Promise<CanalDeIntegracao> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O canal precisa de um nome.');

        const linha = {
            clinica_id: clinicaId(),
            nome,
            tipo: dados.tipo,
            provedor: dados.provedor,
            numero_e164: dados.numero ?? null,
            ativo: dados.ativo,
            fila_padrao_id: dados.filaPadraoId ?? null,
            atendente_padrao_id: dados.atendentePadraoId ?? null,
        };
        const resposta = id
            ? await cliente().from('canais').update(linha).eq('id', id).select('id').single()
            : await cliente().from('canais').insert(linha).select('id').single();
        if (resposta.error?.code === '23505') throw new Error('Já existe um canal deste tipo com esta instância.');
        /* 0161: pessoa de fora da fila, ou fila desativada — a frase do banco já diz qual. */
        if (resposta.error?.code === '22023') throw new Error(resposta.error.message);
        erro('Falha ao salvar o canal', resposta.error);

        const salvo = String((resposta.data as unknown as { id: string }).id);
        return canalPorId(await this.canaisDaClinica(), salvo);
    },

    async salvarCredencialDoCanal(canalId: string, credenciais: Record<string, string>): Promise<CanalDeIntegracao> {
        const { error } = await cliente().rpc('salvar_credencial_do_canal', {
            p_canal: canalId, p_credenciais: credenciais,
        });
        erro('Falha ao salvar a credencial', error);
        return canalPorId(await this.canaisDaClinica(), canalId);
    },

    async removerCredencialDoCanal(canalId: string): Promise<CanalDeIntegracao> {
        const { error } = await cliente().rpc('remover_credencial_do_canal', { p_canal: canalId });
        erro('Falha ao remover a credencial', error);
        return canalPorId(await this.canaisDaClinica(), canalId);
    },

    async pareamentoDisponivel(): Promise<boolean> {
        const { data, error } = await cliente().rpc('pareamento_disponivel', { p_provedor: 'uazapi' });
        erro('Falha ao ver se o pareamento está disponível', error);
        return Boolean(data);
    },

    async parearCanal(canalId: string, acao: AcaoDePareamento, telefone?: string): Promise<PareamentoDoCanal> {
        const { data, error } = await cliente().functions.invoke('canal', {
            body: { acao, canal: canalId, ...(telefone ? { telefone } : {}) },
        });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? error.message);
        }
        const r = (data ?? {}) as Record<string, unknown>;
        return {
            instancia: String(r.instancia ?? ''),
            status: (r.status as StatusDoCanal) ?? 'desconectado',
            ...(r.qrcode ? { qrcode: String(r.qrcode) } : {}),
            ...(r.paircode ? { paircode: String(r.paircode) } : {}),
            ...(r.numero ? { numero: String(r.numero) } : {}),
            ...(r.perfil ? { perfil: String(r.perfil) } : {}),
            ...(r.motivo ? { motivo: String(r.motivo) } : {}),
            ...(r.aviso ? { aviso: String(r.aviso) } : {}),
        };
    },

    async validarCanalDeEmail(canalId: string): Promise<{ status: StatusDoCanal; motivo?: string }> {
        const { data, error } = await cliente().functions.invoke('canal', { body: { acao: 'validar_email', canal: canalId } });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? error.message);
        }
        const r = (data ?? {}) as Record<string, unknown>;
        return {
            status: (r.status as StatusDoCanal) ?? 'erro',
            ...(r.motivo ? { motivo: String(r.motivo) } : {}),
        };
    },

    async provedoresDeCanalDaPlataforma(): Promise<ProvedorDeCanalDaPlataforma[]> {
        const { data, error } = await cliente().rpc('provedores_de_canal_da_plataforma');
        erro('Falha ao ler os servidores de canal', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map((c) => ({
            provedor: String(c.provedor ?? ''),
            baseUrl: String(c.base_url ?? ''),
            temToken: Boolean(c.tem_token),
            dica: String(c.dica ?? ''),
            ativo: Boolean(c.ativo),
            atualizadoEm: String(c.atualizado_em ?? ''),
        }));
    },

    async salvarProvedorDeCanal(provedor: string, baseUrl: string, token: string): Promise<ProvedorDeCanalDaPlataforma[]> {
        const { error } = await cliente().rpc('salvar_provedor_de_canal', {
            p_provedor: provedor, p_base_url: baseUrl, p_token: token,
        });
        erro('Falha ao guardar o servidor de canal', error);
        return this.provedoresDeCanalDaPlataforma();
    },

    async removerProvedorDeCanal(provedor: string): Promise<ProvedorDeCanalDaPlataforma[]> {
        const { error } = await cliente().rpc('remover_provedor_de_canal', { p_provedor: provedor });
        erro('Falha ao remover o servidor de canal', error);
        return this.provedoresDeCanalDaPlataforma();
    },

    async chavesDeIa(): Promise<ChaveDeIa[]> {
        const { data, error } = await cliente().rpc('chaves_de_ia');
        erro('Falha ao ler as chaves de IA', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map((c) => ({
            provedor: c.provedor as ProvedorDeIa,
            temChave: Boolean(c.tem_chave),
            dica: String(c.dica ?? ''),
            ...(c.modelo ? { modelo: String(c.modelo) } : {}),
            ativo: Boolean(c.ativo),
            atualizadoEm: String(c.atualizado_em ?? ''),
        }));
    },

    async salvarChaveDeIa(provedor: ProvedorDeIa, chave: string, modelo?: string): Promise<ChaveDeIa[]> {
        const { error } = await cliente().rpc('salvar_chave_de_ia', {
            p_provedor: provedor, p_chave: chave, p_modelo: modelo ?? null,
        });
        erro('Falha ao salvar a chave', error);
        return this.chavesDeIa();
    },

    async removerChaveDeIa(provedor: ProvedorDeIa): Promise<ChaveDeIa[]> {
        const { error } = await cliente().rpc('remover_chave_de_ia', { p_provedor: provedor });
        erro('Falha ao remover a chave', error);
        return this.chavesDeIa();
    },

    async baseDaClinica(): Promise<DocumentoDaBase[]> {
        const { data, error } = await cliente().rpc('base_da_clinica');
        erro('Falha ao ler a base de conhecimento', error);
        return ((data ?? []) as Record<string, unknown>[]).map(paraDocumentoDaBase);
    },

    async adicionarNaBase({ titulo, texto, arquivo }: { titulo: string; texto?: string; arquivo?: File }): Promise<DocumentoDaBase> {
        const clinica = clinicaId();

        /* O arquivo sobe ANTES da linha existir? Não: a linha primeiro, com o
           caminho já decidido. Se o envio falhar, sobra uma linha `falhou` que
           a clínica vê e apaga — enquanto um arquivo sem linha seria lixo no
           balde que ninguém encontra. */
        const nomeLimpo = arquivo ? arquivo.name.replace(/[^\w.-]+/g, '_').slice(-80) : '';
        const caminho = arquivo ? `${clinica}/${Date.now().toString(36)}-${nomeLimpo}` : null;

        const { data, error } = await cliente()
            .from('base_documentos')
            .insert({
                clinica_id: clinica,
                titulo: titulo.trim(),
                caminho,
                tipo: arquivo ? (arquivo.type || 'application/octet-stream') : 'texto',
                bytes: arquivo ? arquivo.size : (texto ?? '').length,
            })
            .select('id, titulo, tipo, bytes, situacao, erro, trechos, criado_em')
            .single();
        erro('Falha ao criar o documento', error);
        const documento = paraDocumentoDaBase(data as Record<string, unknown>);

        if (arquivo && caminho) {
            /* Os bytes, e não o `File`: ver a nota em `enviarAnexoDaFicha`. */
            const bytes = await arquivo.arrayBuffer();
            const { error: erroEnvio } = await cliente().storage.from('base').upload(caminho, bytes, {
                contentType: arquivo.type || 'application/octet-stream',
                upsert: false,
            });
            if (erroEnvio) {
                await cliente().from('base_documentos')
                    .update({ situacao: 'falhou', erro: `Não foi possível enviar o arquivo: ${erroEnvio.message}` })
                    .eq('id', documento.id);
                throw new Error(`Não foi possível enviar ${arquivo.name}.`);
            }
        }

        return this.prepararDocumentoDaBase(documento.id, texto);
    },

    async prepararDocumentoDaBase(id: string, texto?: string): Promise<DocumentoDaBase> {
        const { error } = await cliente().functions.invoke('base', {
            body: { documentoId: id, clinica: clinicaAtiva(), ...(texto ? { texto } : {}) },
        });
        /* Falha de preparação NÃO derruba o documento: a função já gravou o
           motivo em `situacao`/`erro`, e é de lá que a tela conta a história.
           Estourar aqui sumiria com a linha que explica o problema. */
        if (error) console.error('falha ao preparar o documento da base', error);

        const { data } = await cliente()
            .from('base_documentos')
            .select('id, titulo, tipo, bytes, situacao, erro, trechos, criado_em')
            .eq('id', id)
            .single();
        return paraDocumentoDaBase((data ?? {}) as Record<string, unknown>);
    },

    async removerDaBase(id: string): Promise<void> {
        /* Os trechos vão junto pelo `on delete cascade`, e o arquivo no balde
           também precisa sair — deixá-lo seria guardar o texto que a clínica
           mandou apagar, num lugar que a tela não mostra mais. */
        const { data } = await cliente().from('base_documentos').select('caminho').eq('id', id).maybeSingle();
        const caminho = (data as { caminho?: string } | null)?.caminho;
        const { error } = await cliente().from('base_documentos').delete().eq('id', id);
        erro('Falha ao remover o documento', error);
        if (caminho) await cliente().storage.from('base').remove([caminho]);
    },

    async orcamentosDeIa(): Promise<OrcamentoDeIa[]> {
        const { data, error } = await cliente().rpc('painel_dos_orcamentos_de_ia');
        erro('Falha ao ler os tetos de IA', error);
        return ((data ?? []) as Record<string, unknown>[]).map((l) => ({
            clinicaId: String(l.clinica_id),
            clinica: String(l.clinica ?? ''),
            limiteTokensMes: l.limite_tokens_mes === null ? null : Number(l.limite_tokens_mes),
            tokensDoMes: Number(l.tokens_do_mes ?? 0),
            estourado: Boolean(l.estourado),
        }));
    },

    async salvarOrcamentoDeIa(clinicaId: string, limite: number | null): Promise<void> {
        const { error } = await cliente().rpc('salvar_orcamento_de_ia', { p_clinica: clinicaId, p_limite: limite });
        erro('Falha ao salvar o teto de IA', error);
    },

    async meuOrcamentoDeIa(): Promise<MeuOrcamentoDeIa | null> {
        const { data, error } = await cliente().rpc('ia_orcamento_da_clinica');
        if (error) return null;
        /* A função devolve zero ou uma linha: sem clínica no contexto (operador
           da plataforma fora de impersonação) não há teto a mostrar. */
        const linha = (data as Record<string, unknown>[] | null)?.[0];
        if (!linha) return null;
        return {
            limiteTokensMes: linha.limite_tokens_mes === null ? null : Number(linha.limite_tokens_mes),
            tokensDoMes: Number(linha.tokens_do_mes ?? 0),
            estourado: Boolean(linha.estourado),
        };
    },

    async iaDisponivel(): Promise<boolean> {
        /* A função guarda o nome de quando servia ao copiloto (0029, 0035): ela
           responde se há chave e se a clínica está dentro do teto. */
        const { data, error } = await cliente().rpc('copiloto_disponivel');
        if (error) return false;
        return Boolean(data);
    },

    async retornosEmAberto(dias = 90): Promise<RetornoEmAberto[]> {
        return retornosEmAbertoDoServidor(dias);
    },

    async agentes(): Promise<Agente[]> {
        const { data, error } = await cliente().from('agentes')
            .select('id, tipo, nome, ativo, tom, missao, instrucoes, temperatura, modelo, ferramentas, saudacao, limites, atualizado_em')
            /* O copiloto saiu do produto; a linha que a 0031 semeou fica no banco. */
            .neq('tipo', 'copiloto')
            .order('tipo').order('nome');
        erro('Falha ao ler os agentes', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraAgente);
    },

    async salvarAgente(id: string | null, dados: DadosDoAgente): Promise<Agente> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O agente precisa de um nome.');

        const linha = {
            clinica_id: clinicaId(),
            tipo: dados.tipo,
            nome,
            ativo: dados.ativo,
            tom: dados.tom,
            missao: dados.missao,
            instrucoes: dados.instrucoes,
            temperatura: dados.temperatura,
            modelo: dados.modelo ?? null,
            ferramentas: dados.ferramentas,
            saudacao: dados.saudacao ?? null,
            limites: dados.limites ?? {},
        };
        const resposta = id
            ? await cliente().from('agentes').update(linha).eq('id', id)
                .select('id, tipo, nome, ativo, tom, missao, instrucoes, temperatura, modelo, ferramentas, saudacao, limites, atualizado_em').single()
            : await cliente().from('agentes').insert(linha)
                .select('id, tipo, nome, ativo, tom, missao, instrucoes, temperatura, modelo, ferramentas, saudacao, limites, atualizado_em').single();

        if (resposta.error?.code === '23505') {
            throw new Error(`Já existe um agente chamado ${nome}.`);
        }
        erro('Falha ao salvar o agente', resposta.error);
        return paraAgente(resposta.data as unknown as Record<string, unknown>);
    },

    async removerAgente(id: string): Promise<{ removido: boolean }> {
        const { error } = await cliente().from('agentes').delete().eq('id', id);
        erro('Falha ao remover o agente', error);
        return { removido: true };
    },

    async conversarComAgente(pedido: { tipo: 'atendimento' | 'pre-atendimento'; mensagens: MensagemDoAgente[] }): Promise<RespostaDoAgente> {
        const { data, error } = await cliente().functions.invoke('agente', { body: { ...pedido, clinica: clinicaAtiva() } });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? error.message);
        }
        const r = data as Partial<RespostaDoAgente>;
        return {
            texto: String(r?.texto ?? ''),
            propostas: r?.propostas ?? [],
            usos: r?.usos ?? [],
            provedor: String(r?.provedor ?? ''),
            modelo: String(r?.modelo ?? ''),
            tokens: r?.tokens ?? { entrada: 0, saida: 0 },
        };
    },

    async confirmarPropostaDoAgente(proposta: PropostaDoAgente): Promise<{ descricao: string }> {
        return executarProposta(this, proposta);
    },

    async ferramentasDaBorda(): Promise<{ id: string; escreve: boolean }[]> {
        const base = import.meta.env.VITE_SUPABASE_URL;
        const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (!base || !chave) return [];
        const r = await fetch(`${base}/functions/v1/agente`, { headers: { apikey: chave } });
        if (!r.ok) throw new Error(`A borda respondeu ${r.status}.`);
        const corpo = await r.json() as { ferramentas?: { id: string; escreve: boolean }[] };
        return corpo.ferramentas ?? [];
    },

    async fluxos(): Promise<Fluxo[]> {
        const { data, error } = await cliente().from('fluxos')
            .select('id, nome, descricao, gatilho, passos, ativo, execucoes, ultima_execucao_em, atualizado_em')
            .order('nome');
        erro('Falha ao ler os fluxos', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraFluxo);
    },

    async salvarFluxo(id: string | null, dados: DadosDoFluxo): Promise<Fluxo> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O fluxo precisa de um nome.');

        const linha = {
            clinica_id: clinicaId(),
            nome,
            descricao: dados.descricao,
            gatilho: dados.gatilho,
            passos: dados.passos,
            ativo: dados.ativo,
        };
        const colunas = 'id, nome, descricao, gatilho, passos, ativo, execucoes, ultima_execucao_em, atualizado_em';
        const resposta = id
            ? await cliente().from('fluxos').update(linha).eq('id', id).select(colunas).single()
            : await cliente().from('fluxos').insert(linha).select(colunas).single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe um fluxo chamado ${nome}.`);
        erro('Falha ao salvar o fluxo', resposta.error);
        return paraFluxo(resposta.data as unknown as Record<string, unknown>);
    },

    async removerFluxo(id: string): Promise<{ removido: boolean }> {
        const { error } = await cliente().from('fluxos').delete().eq('id', id);
        erro('Falha ao remover o fluxo', error);
        return { removido: true };
    },

    async simularFluxo(id: string): Promise<SimulacaoDoFluxo> {
        const fluxo = (await this.fluxos()).find((f) => f.id === id);
        if (!fluxo) throw new Error(`Fluxo ${id} não existe.`);
        return simular(this, fluxo);
    },

    async executarFluxo(id: string): Promise<ResultadoDoFluxo> {
        const fluxo = (await this.fluxos()).find((f) => f.id === id);
        if (!fluxo) throw new Error(`Fluxo ${id} não existe.`);
        const resultado = await executar(this, fluxo);
        await cliente().from('fluxos').update({
            execucoes: fluxo.execucoes + 1,
            ultima_execucao_em: new Date().toISOString(),
        }).eq('id', id);
        return resultado;
    },

    async gerarFluxoComIa(descricao: string): Promise<DadosDoFluxo> {
        const { data, error } = await cliente().functions.invoke('fluxo', { body: { descricao, clinica: clinicaAtiva() } });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? error.message);
        }
        const r = data as Partial<DadosDoFluxo> | null;
        if (!r?.gatilho || !Array.isArray(r.passos)) {
            throw new Error('A IA devolveu um fluxo que a tela não consegue abrir.');
        }

        /* O rascunho passa pela MESMA validação da tela: fluxo gerado que não
           abre é pior que nenhum rascunho. */
        const problemas = validarFluxo(r.gatilho, r.passos as PassoDoFluxo[]);
        if (problemas.length) {
            throw new Error(`O rascunho veio inválido: ${problemas.map((p) => p.mensagem).join(' ')}`);
        }

        return {
            nome: String(r.nome ?? 'Fluxo sem nome'),
            descricao: String(r.descricao ?? descricao),
            gatilho: r.gatilho,
            passos: r.passos,
            /* Nasce desligado, sempre: rascunho de IA que já chega ativo
               executa antes de alguém ler o que ele faz. */
            ativo: false,
        };
    },

    async caminhosDaApi(): Promise<string[]> {
        /* O OpenAPI do PostgREST: a raiz do `/rest/v1/` publica todos os
           caminhos. É a fonte contra a qual o catálogo se confere. */
        const base = import.meta.env.VITE_SUPABASE_URL;
        const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (!base || !chave) return [];

        const r = await fetch(`${base}/rest/v1/`, { headers: { apikey: chave } });
        if (!r.ok) throw new Error(`O servidor respondeu ${r.status} ao publicar a API.`);
        const spec = await r.json() as { paths?: Record<string, unknown> };
        return Object.keys(spec.paths ?? {});
    },

    async consumoDeIa(dias = 30): Promise<LinhaDeConsumoDeIa[]> {
        const { data, error } = await cliente().rpc('painel_do_consumo_de_ia', { p_dias: dias });
        erro('Falha ao ler o consumo de IA', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map((l) => ({
            ...(l.clinica_id ? { clinicaId: String(l.clinica_id) } : {}),
            clinica: String(l.clinica ?? 'Plataforma'),
            provedor: String(l.provedor ?? ''),
            modelo: String(l.modelo ?? ''),
            perguntas: Number(l.perguntas ?? 0),
            tokensEntrada: Number(l.tokens_entrada ?? 0),
            tokensSaida: Number(l.tokens_saida ?? 0),
            erros: Number(l.erros ?? 0),
            ultimaEm: String(l.ultima_em ?? ''),
        }));
    },

    async salvarLembrete(id: string | null, dados: DadosDoLembrete): Promise<Lembrete> {
        const quando = dados.quando.trim();
        if (!quando) throw new Error('O lembrete precisa de um rótulo de quando.');
        if (dados.offsetMin < 0) throw new Error('A antecedência não pode ser negativa.');

        const linha = {
            clinica_id: clinicaId(),
            quando,
            descricao: dados.descricao,
            canal: dados.canal,
            ativo: dados.ativo,
            offset_min: dados.offsetMin,
        };
        const resposta = id
            ? await cliente().from('lembretes_regua').update(linha).eq('id', id)
                .select('id, quando, descricao, ativo, canal, offset_min').single()
            : await cliente().from('lembretes_regua').insert(linha)
                .select('id, quando, descricao, ativo, canal, offset_min').single();
        erro('Falha ao salvar o lembrete', resposta.error);
        return paraLembrete(resposta.data as unknown as Record<string, unknown>);
    },

    async removerLembrete(id: string): Promise<{ removido: boolean }> {
        const { error } = await cliente().from('lembretes_regua').delete().eq('id', id);
        erro('Falha ao remover o lembrete', error);
        return { removido: true };
    },

    async salvarCampanha(id: string | null, dados: DadosDaCampanha): Promise<Campanha> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A campanha precisa de um nome.');
        if (dados.status !== 'rascunho' && !dados.inicio) {
            throw new Error('Campanha fora do rascunho precisa de data de início.');
        }

        const linha = {
            clinica_id: clinicaId(),
            nome,
            canal: dados.canal,
            publico_tipo: dados.publicoTipo,
            publico_param: dados.publicoParam ?? {},
            origem_lead: dados.origemLead ?? null,
            status: dados.status,
            inicio: dados.inicio ?? null,
            fim: dados.fim ?? null,
            investimento_centavos: dados.investimentoCentavos,
        };

        const resposta = id
            ? await cliente().from('campanhas').update(linha).eq('id', id).select('id').single()
            : await cliente().from('campanhas').insert(linha).select('id').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma campanha chamada ${nome}.`);
        erro('Falha ao salvar a campanha', resposta.error);

        const salvo = String((resposta.data as unknown as { id: string }).id);
        const carga = await this.marketing('campanhas');
        const campanha = carga.campanhas.find((c) => c.id === salvo);
        if (!campanha) throw new Error('Campanha salva, mas não encontrada na releitura.');
        return campanha;
    },

    async salvarSala(id: string | null, dados: { nome: string; ativa: boolean }): Promise<SalaDaClinica> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A sala precisa de um nome.');
        const linha = { clinica_id: clinicaId(), nome, ativa: dados.ativa };
        const resposta = id
            ? await cliente().from('salas').update({ nome, ativa: dados.ativa }).eq('id', id).select('id, nome, ativa').single()
            : await cliente().from('salas').insert(linha).select('id, nome, ativa').single();
        if (resposta.error?.code === '23505') throw new Error(`Já existe uma sala chamada ${nome}.`);
        erro('Falha ao salvar a sala', resposta.error);
        const s = resposta.data as unknown as Record<string, unknown>;
        return { id: String(s.id), nome: String(s.nome), ativa: Boolean(s.ativa) };
    },

    /* ---- trilha T5a ---- */
    ...cadastrosNoSupabase,
    ...comunicacaoNoSupabase,
    ...anunciosNoSupabase,
    ...financeiroAvulsoNoSupabase,
    ...notasFiscaisNoSupabase,
    ...atendimentoNoSupabase,
    ...notaDeIaNoSupabase,
    ...vendasNoSupabase,
    ...planosNoSupabase,
    ...fichaNoSupabase,

    /* ---- trilha T2 ---- */

    async opcoesDoAgendamento(): Promise<OpcoesDoAgendamento> {
        const recorte = await recorteDaUnidade();
        const [profissionais, salas, procedimentos, horarios] = await Promise.all([
            cliente().from('membros').select('id, nome').eq('acesso', 'ativo').is('arquivado_em', null).order('nome'),
            salasParaAgendar(recorte),
            cliente().from('procedimentos').select('id, nome, duracao_min').eq('ativo', true).order('nome'),
            cliente().from('horarios_funcionamento')
                .select('dia_semana, inicio, fim').is('unidade_id', null).order('dia_semana').order('inicio'),
        ]);
        erro('Falha ao ler a equipe', profissionais.error);
        erro('Falha ao ler os procedimentos', procedimentos.error);
        erro('Falha ao ler a jornada', horarios.error);

        const linhas = (v: unknown) => (v ?? []) as Record<string, unknown>[];
        return {
            profissionais: linhas(profissionais.data).map((p) => ({ id: String(p.id), nome: String(p.nome) })),
            salas,
            procedimentos: linhas(procedimentos.data).map((p) => ({
                id: String(p.id), nome: String(p.nome), duracaoMin: Number(p.duracao_min ?? 60),
            })),
            horarios: paraJornada(horarios.data as unknown as LinhaDeHorario[] | null),
        };
    },

    async buscarContatos(busca: string): Promise<ContatoParaEscolha[]> {
        /* Vírgula, ponto, aspas e parênteses são sintaxe do filtro `or` do
           PostgREST; `%`, `_` e `*` são curinga. Tirá-los é o que impede a busca
           digitada de virar outra consulta. */
        const termo = busca.replace(/[,.()"%_\\*:]/g, ' ').replace(/\s+/g, ' ').trim();
        if (termo.length < 2) return [];
        const digitos = termo.replace(/\D/g, '');
        const filtro = digitos.length >= 4
            ? `nome.ilike."*${termo}*",telefone_e164.ilike."*${digitos}*"`
            : `nome.ilike."*${termo}*"`;

        const { data, error } = await cliente()
            .from('contatos')
            .select('id, nome, telefone, pacientes(contato_id), leads(id)')
            .is('arquivado_em', null)
            .or(filtro)
            .order('nome')
            .limit(8);
        erro('Falha ao buscar contatos', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraContatoParaEscolha);
    },

    async contatoPorId(id: string): Promise<ContatoParaEscolha | null> {
        const { data, error } = await cliente()
            .from('contatos')
            .select('id, nome, telefone, pacientes(contato_id), leads(id)')
            .eq('id', id)
            .maybeSingle();
        erro('Falha ao ler o contato', error);
        return data ? paraContatoParaEscolha(data as unknown as Record<string, unknown>) : null;
    },

    async definirLembreteDoAgendamento(agendamentoId: string, lembreteId: string, ativo: boolean): Promise<Lembrete[]> {
        const { error } = await cliente()
            .from('agendamentos_lembretes')
            .upsert(
                { clinica_id: clinicaId(), agendamento_id: agendamentoId, lembrete_id: lembreteId, ativo },
                { onConflict: 'agendamento_id,lembrete_id' },
            );
        erro('Falha ao salvar o lembrete deste agendamento', error);
        return this.lembretesDoAgendamento(agendamentoId);
    },

    async minhaConta(): Promise<MinhaConta> {
        const { data: usuario, error: erroUsuario } = await cliente().auth.getUser();
        const uid = usuario.user?.id;
        if (erroUsuario || !uid) throw new Error('Sessão expirada: entre novamente.');

        /* Filtra pelo usuário: a RLS deixa ler a equipe inteira, e sem este
           filtro "minha conta" seria a do primeiro membro da lista. */
        const { data, error } = await cliente()
            .from('membros')
            .select('nome, telefone, email, presenca')
            .eq('usuario_id', uid)
            .eq('clinica_id', clinicaId())
            .maybeSingle();
        erro('Falha ao ler a sua conta', error);
        if (!data) throw new Error('Não há cadastro seu nesta clínica.');

        const m = data as unknown as Record<string, unknown>;
        return {
            nome: String(m.nome ?? ''),
            telefone: String(m.telefone ?? ''),
            email: String(m.email ?? usuario.user?.email ?? ''),
            presenca: (m.presenca as MinhaConta['presenca'] | null) ?? 'online',
        };
    },

    async salvarMinhaConta(dados: { nome: string; telefone: string }): Promise<MinhaConta> {
        const { error } = await cliente().rpc('salvar_minha_conta', { p_nome: dados.nome, p_telefone: dados.telefone });
        erro('Não foi possível salvar a sua conta', error);
        return this.minhaConta();
    },

    async salvarMinhaPresenca(presenca: MinhaConta['presenca']): Promise<MinhaConta> {
        const { error } = await cliente().rpc('salvar_minha_presenca', { p_presenca: presenca });
        erro('Não foi possível trocar o status', error);
        return this.minhaConta();
    },

    async importarContatos(tipo: TipoImportavel, linhas: LinhaImportada[]): Promise<ResultadoDaImportacao> {
        const recusadas: RecusaDaImportacao[] = [];
        const avisos: RecusaDaImportacao[] = [];
        let criados = 0;

        const equipe = linhas.some((l) => l.contato.responsavelNome) ? (await this.opcoesDoAgendamento()).profissionais : [];
        const comparavel = (v: string) => semAcento(v).replace(/^dra?\.?\s+/, '').trim();

        /* Em grupos de cinco: um de cada vez levaria minutos numa planilha de mil
           linhas; todos juntos estouram o limite de conexões do servidor. */
        for (let i = 0; i < linhas.length; i += 5) {
            await Promise.all(linhas.slice(i, i + 5).map(async (l) => {
                const contato: NovoContato = { ...l.contato, tipo };
                if (contato.responsavelNome && !contato.profissionalId) {
                    const achado = equipe.find((m) => comparavel(m.nome) === comparavel(contato.responsavelNome ?? ''));
                    if (achado) contato.profissionalId = achado.id;
                    else avisos.push({ linha: l.linha, motivo: `Responsável "${contato.responsavelNome}" não está na equipe: entrou sem responsável.` });
                }
                try {
                    await this.criarContato(contato);
                    criados += 1;
                } catch (e) {
                    recusadas.push({ linha: l.linha, motivo: e instanceof Error ? e.message : 'Falha ao gravar.' });
                }
            }));
        }

        const porLinha = (a: RecusaDaImportacao, b: RecusaDaImportacao) => a.linha - b.linha;
        return { criados, recusadas: recusadas.sort(porLinha), avisos: avisos.sort(porLinha) };
    },

    async etiquetarContatos(contatoIds: string[], etiquetaId: string): Promise<number> {
        if (contatoIds.length === 0) return 0;
        /* `ignoreDuplicates` em vez de recusar o lote inteiro: quem já tinha a etiqueta
           não é erro, e sem isto uma pessoa repetida derrubaria as outras dezenove. */
        const { data, error } = await cliente()
            .from('contato_etiquetas')
            .upsert(
                contatoIds.map((contato_id) => ({ clinica_id: clinicaId(), contato_id, etiqueta_id: etiquetaId, origem: 'manual' })),
                { onConflict: 'contato_id,etiqueta_id', ignoreDuplicates: true },
            )
            .select('contato_id');
        erro('Falha ao etiquetar as pessoas', error);
        return (data ?? []).length;
    },

    async trocarProfissionalDosPacientes(contatoIds: string[], profissionalId: string): Promise<number> {
        if (contatoIds.length === 0) return 0;
        const { data, error } = await cliente()
            .from('pacientes').update({ profissional_id: profissionalId }).in('contato_id', contatoIds).select('contato_id');
        erro('Falha ao trocar o profissional', error);
        return (data ?? []).length;
    },

    async trocarResponsavelDosLeads(leadIds: string[], membroId: string): Promise<number> {
        if (leadIds.length === 0) return 0;
        const { data, error } = await cliente()
            .from('leads').update({ responsavel_id: membroId }).in('id', leadIds).select('id');
        erro('Falha ao trocar o responsável', error);
        return (data ?? []).length;
    },

    async moverLeadsDeEtapa(leadIds: string[], etapaId: string): Promise<number> {
        /* Pelo mesmo caminho do quadro, um a um: mover para ganho ou perdido tem
           regra, e a regra que só o lote pula é a que falta quando importa. */
        const falhas: string[] = [];
        for (const id of leadIds) {
            try {
                await this.moverLeadDeEtapa(id, etapaId);
            } catch (e) {
                falhas.push(e instanceof Error ? e.message : 'falha ao mover');
            }
        }
        if (falhas.length > 0) throw new Error(`${falhas.length} de ${leadIds.length} não mudaram de etapa: ${falhas[0]}`);
        return leadIds.length;
    },

    async registrarNoHistoricoDoContato(contatoId: string, texto: string): Promise<void> {
        const nota = texto.trim();
        if (!nota) throw new Error('Escreva o que foi conversado.');
        const { error } = await cliente().from('eventos_do_contato').insert({
            clinica_id: clinicaId(), contato_id: contatoId,
            titulo: 'Contato registrado', descricao: nota, icone: 'message-square', tom: 'neutral',
            detalhes: [], autor_nome: atual()?.nome ?? null, ocorrido_em: new Date().toISOString(),
        });
        erro('Não foi possível registrar no histórico', error);
    },

    async origensDeLead(): Promise<string[]> {
        const { data, error } = await cliente().from('leads').select('origem').not('origem', 'is', null).limit(1000);
        erro('Falha ao ler as origens', error);
        const origens = ((data ?? []) as unknown as { origem: string }[]).map((l) => String(l.origem).trim()).filter(Boolean);
        return [...new Set(origens)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    },

    async arquivarContato(contatoId: string): Promise<void> {
        const { data, error } = await cliente()
            .from('contatos').update({ arquivado_em: new Date().toISOString() }).eq('id', contatoId).select('id');
        erro('Não foi possível arquivar', error);
        if ((data ?? []).length === 0) throw new Error('O contato não foi encontrado, ou você não tem permissão para arquivar.');
    },

    async salvarDadosDaClinicaEUnidade(novos: EdicaoDaClinica, unidade?: { id: string; mudancas: EdicaoDaUnidade }): Promise<void> {
        /* Uma chamada só (`0051`): gravar a clínica e depois a unidade deixava
           metade salva quando o segundo pedido falhava. */
        const m = unidade?.mudancas;
        const { error } = await cliente().rpc('salvar_dados_da_clinica', {
            p_clinica: clinicaId(),
            p_dados: {
                nome: novos.nome, documento: novos.documento, tipo_pessoa: novos.tipoPessoa,
                razao_social: novos.razaoSocial, email: novos.email, fuso: novos.fuso,
            },
            p_unidade: unidade?.id ?? null,
            p_endereco: m
                ? {
                    nome: m.nome, telefone: m.telefone, email: m.email, cep: m.cep, estado: m.estado,
                    cidade: m.cidade, bairro: m.bairro, rua: m.rua, numero: m.numero, complemento: m.complemento,
                }
                : null,
        });
        erro('Não foi possível salvar os dados da clínica', error);
    },

    /* ---- trilha T5b ---- */

    /* Toda ação do operador é uma função do banco que cobra a permissão
       específica e registra na auditoria. O que chega aqui de mensagem de erro é
       a do banco — escrita para quem opera, não para o console. */

    async criarClinica(dadosDaClinica: DadosDaNovaClinica): Promise<{ clinicaId: string }> {
        const { data, error } = await cliente().rpc('painel_criar_clinica', { p_dados: dadosDaClinica });
        erro('Não foi possível abrir a clínica', error);
        return { clinicaId: String(data) };
    },

    async suspenderClinica(id: string, motivo: string): Promise<void> {
        const { error } = await cliente().rpc('painel_suspender_clinica', { p_clinica: id, p_motivo: motivo });
        erro('Não foi possível suspender', error);
    },

    async reativarClinica(id: string, motivo: string): Promise<SituacaoDaClinica> {
        const { data, error } = await cliente().rpc('painel_reativar_clinica', { p_clinica: id, p_motivo: motivo });
        erro('Não foi possível reativar', error);
        return String(data) as SituacaoDaClinica;
    },

    async trocarPlanoDaClinica(id: string, planoCodigo: string, motivo: string): Promise<void> {
        const { error } = await cliente().rpc('painel_trocar_plano', { p_clinica: id, p_plano: planoCodigo, p_motivo: motivo });
        erro('Não foi possível trocar o plano', error);
    },

    async definirLimiteDeMembros(clinicaId: string, limite: number | null): Promise<number> {
        const { data, error } = await cliente().rpc('definir_limite_de_membros', { p_clinica: clinicaId, p_limite: limite });
        erro('Não foi possível definir as vagas da clínica', error);
        return Number(data ?? 0);
    },

    async aplicarDesconto(idDaClinica: string, desconto: DadosDoDesconto): Promise<void> {
        const { error } = await cliente().rpc('painel_aplicar_desconto', {
            p_clinica: idDaClinica, p_percentual: desconto.percentual, p_meses: desconto.meses, p_motivo: desconto.motivo,
        });
        erro('Não foi possível aplicar o desconto', error);
    },

    async removerDesconto(idDaClinica: string, motivo: string): Promise<void> {
        const { error } = await cliente().rpc('painel_remover_desconto', { p_clinica: idDaClinica, p_motivo: motivo });
        erro('Não foi possível remover o desconto', error);
    },

    async emitirFaturaAvulsa(fatura: DadosDaFaturaAvulsa): Promise<Fatura> {
        const { data, error } = await cliente().rpc('painel_emitir_fatura_avulsa', {
            p_clinica: fatura.clinicaId, p_descricao: fatura.descricao,
            p_valor_centavos: fatura.valorCentavos, p_vencimento: fatura.vencimento,
        });
        erro('Não foi possível emitir a fatura avulsa', error);
        const emitida = (await this.faturamento()).faturas.find((f) => f.id === String(data));
        if (!emitida) throw new Error('Fatura emitida, mas não encontrada na releitura.');
        return emitida;
    },

    async estornarFatura(id: string, motivo: string): Promise<Fatura> {
        const { error } = await cliente().rpc('painel_estornar_fatura', { p_fatura: id, p_motivo: motivo });
        erro('Não foi possível registrar o estorno', error);
        const estornada = (await this.faturamento()).faturas.find((f) => f.id === id);
        if (!estornada) throw new Error('Estorno registrado, mas a fatura não foi encontrada na releitura.');
        return estornada;
    },

    async salvarCredito(idDaClinica: string, id: string | null, credito: DadosDoCredito): Promise<CreditoDaClinica> {
        const { data, error } = await cliente().rpc('painel_salvar_credito', {
            p_clinica: idDaClinica, p_id: id, p_tipo: credito.tipo,
            p_valor_centavos: credito.valorCentavos, p_motivo: credito.motivo,
        });
        erro('Não foi possível salvar o crédito', error);
        const lidos = await cliente().rpc('painel_dos_creditos', { p_clinica: idDaClinica });
        erro('Falha ao reler os créditos', lidos.error);
        const salvo = ((lidos.data ?? []) as unknown as Record<string, unknown>[]).map(paraCredito).find((c) => c.id === String(data));
        if (!salvo) throw new Error('Crédito salvo, mas não encontrado na releitura.');
        return salvo;
    },

    async excluirCredito(id: string, motivo: string): Promise<void> {
        const { error } = await cliente().rpc('painel_excluir_credito', { p_id: id, p_motivo: motivo });
        erro('Não foi possível excluir o crédito', error);
    },

    async definirRolloutDaFlag(id: string, percentual: number | null): Promise<FlagDeModulo> {
        const { error } = await cliente().rpc('painel_definir_rollout', { p_flag: id, p_percentual: percentual });
        erro('Não foi possível mudar o rollout', error);
        return flagPorId(await this.flags(), id);
    },

    async acionarKillSwitch(id: string, desligar: boolean, motivo: string): Promise<FlagDeModulo> {
        const { error } = await cliente().rpc('painel_kill_switch', { p_flag: id, p_desligar: desligar, p_motivo: motivo });
        erro(desligar ? 'Não foi possível acionar o kill switch' : 'Não foi possível religar o módulo', error);
        return flagPorId(await this.flags(), id);
    },

    async operadoresDaPlataforma(): Promise<OperadorDaPlataforma[]> {
        const { data, error } = await cliente().rpc('painel_dos_operadores');
        erro('Falha ao ler a equipe da plataforma', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraOperador);
    },

    async definirPermissoesDoOperador(usuarioId: string, permissoes: string[]): Promise<OperadorDaPlataforma[]> {
        const { error } = await cliente().rpc('definir_permissoes_do_operador', { p_usuario: usuarioId, p_permissoes: permissoes });
        erro('Não foi possível mudar as permissões', error);
        return this.operadoresDaPlataforma();
    },

    async saudeDoBanco(): Promise<SaudeDoBanco | null> {
        const { data, error } = await cliente().rpc('painel_da_saude');
        erro('Falha ao medir o banco', error);
        const s = data as Record<string, unknown> | null;
        if (!s) return null;
        const agendador = s.agendador as { falhas24h?: number; ultimaExecucaoEm?: string | null } | null | undefined;
        return {
            conexoes: Number(s.conexoes ?? 0),
            conexoesAtivas: Number(s.conexoesAtivas ?? 0),
            maxConexoes: Number(s.maxConexoes ?? 0),
            tamanhoBytes: Number(s.tamanhoBytes ?? 0),
            iniciadoEm: String(s.iniciadoEm ?? ''),
            versao: String(s.versao ?? ''),
            maioresTabelas: ((s.maioresTabelas ?? []) as { tabela: string; bytes: number }[])
                .map((t) => ({ tabela: String(t.tabela), bytes: Number(t.bytes) })),
            agendador: agendador
                ? {
                    falhas24h: Number(agendador.falhas24h ?? 0),
                    ...(agendador.ultimaExecucaoEm ? { ultimaExecucaoEm: String(agendador.ultimaExecucaoEm) } : {}),
                }
                : null,
        };
    },

    async chavesDeApi(): Promise<ChaveDeApi[]> {
        const { data, error } = await cliente().rpc('chaves_de_api_da_clinica', { p_clinica: clinicaId() });
        erro('Falha ao ler as chaves de API', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraChaveDeApi);
    },

    async criarChaveDeApi(nome: string): Promise<ChaveDeApiCriada> {
        const { data, error } = await cliente().rpc('criar_chave_de_api', { p_clinica: clinicaId(), p_nome: nome });
        erro('Não foi possível gerar a chave', error);
        const linha = ((data ?? []) as unknown as { id: string; chave: string }[])[0];
        if (!linha?.chave) throw new Error('O servidor não devolveu a chave gerada.');
        const chave = (await this.chavesDeApi()).find((k) => k.id === String(linha.id));
        if (!chave) throw new Error('Chave gerada, mas não encontrada na releitura.');
        return { chave, segredo: linha.chave };
    },

    async revogarChaveDeApi(id: string): Promise<ChaveDeApi[]> {
        const { error } = await cliente().rpc('revogar_chave_de_api', { p_id: id });
        erro('Não foi possível revogar a chave', error);
        return this.chavesDeApi();
    },

    /* ---- trilha T4 ---- */
    async baseDeConhecimentoDisponivel(): Promise<{ disponivel: boolean; provedor?: string }> {
        const { data, error } = await cliente().rpc('base_de_conhecimento_disponivel');
        erro('Falha ao ver se a base de conhecimento pode vetorizar', error);
        const r = (data ?? {}) as { disponivel?: boolean; provedor?: string | null };
        return { disponivel: Boolean(r.disponivel), ...(r.provedor ? { provedor: r.provedor } : {}) };
    },

    async convidarMembro(membroId: string): Promise<{ email: string | null }> {
        const { data, error } = await cliente().functions.invoke('convite', {
            body: { membro: membroId, clinica: clinicaAtiva() },
        });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? 'Não foi possível enviar o convite.');
        }
        const email = (data as { email?: string } | null)?.email;
        if (!email) throw new Error('O servidor não confirmou para onde o convite saiu.');
        return { email };
    },

    async criarUsuarioDaEquipe(novo: NovoUsuarioDaEquipe): Promise<{ id: string; email: string }> {
        const { data, error } = await cliente().functions.invoke('equipe-usuario', {
            body: { ...novo, clinica: clinicaAtiva() },
        });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? 'Não foi possível criar o usuário.');
        }
        const r = (data ?? {}) as { id?: string; email?: string };
        if (!r.id || !r.email) throw new Error('O servidor não confirmou a criação do usuário.');
        return { id: r.id, email: r.email };
    },

    async definirSenhaDoMembro(membroId: string, senha: string): Promise<void> {
        const { error } = await cliente().functions.invoke('equipe-usuario', {
            method: 'PATCH',
            body: { membro: membroId, senha, clinica: clinicaAtiva() },
        });
        if (error) throw new Error((await mensagemDaFuncao(error)) ?? 'Não foi possível definir a senha.');
    },

    async enviosAutomaticos(): Promise<boolean> {
        const { data, error } = await cliente().from('clinicas').select('envios_automaticos').eq('id', clinicaId()).maybeSingle();
        erro('Falha ao ler o envio automático', error);
        return Boolean((data as { envios_automaticos?: boolean } | null)?.envios_automaticos);
    },

    async definirEnviosAutomaticos(ligado: boolean): Promise<boolean> {
        const { data, error } = await cliente().rpc('definir_envios_automaticos', { p_ligado: ligado, p_clinica: clinicaAtiva() });
        erro('Falha ao mudar o envio automático', error);
        return Boolean(data);
    },

    async publicoDaCampanha(id: string): Promise<{ contatoId: string; nome: string }[]> {
        const { data: campanha, error } = await cliente().from('campanhas').select('publico_tipo, publico_param').eq('id', id).single();
        erro('Falha ao ler a campanha', error);
        const tipo = (campanha as { publico_tipo: string }).publico_tipo;
        const param = ((campanha as { publico_param?: { dias?: number; origem?: string } }).publico_param ?? {});
        const unicos = (linhas: { contatoId: string; nome: string }[]) => [...new Map(linhas.map((l) => [l.contatoId, l])).values()];

        /* Sem retorno pela MESMA consulta da lista da tela: uma segunda versão
           da pergunta é o defeito de contagem que já existiu aqui. */
        if (tipo === 'sem-retorno') {
            const retornos = await this.retornosEmAberto(param.dias ?? 90);
            return unicos(retornos.map((r) => ({ contatoId: r.contatoId, nome: r.nome })));
        }
        if (tipo === 'por-origem') {
            const { data, error: erroDosLeads } = await cliente().from('leads')
                .select('contato_id, etapa, contatos(nome)')
                .eq('origem', param.origem ?? '')
                .not('etapa', 'in', '("perdido","ganho")');
            erro('Falha ao ler o público da campanha', erroDosLeads);
            return unicos(((data ?? []) as unknown as Record<string, unknown>[])
                .filter((l) => l.contato_id)
                .map((l) => ({ contatoId: String(l.contato_id), nome: nomeEmbutido(l.contatos) ?? '' })));
        }
        const { data, error: erroDosPacientes } = await cliente().from('pacientes').select('contato_id, nascimento, contatos(nome)');
        erro('Falha ao ler o público da campanha', erroDosPacientes);
        const mes = new Date().toLocaleDateString('en-CA', { timeZone: FUSO }).slice(5, 7);
        return unicos(((data ?? []) as unknown as Record<string, unknown>[])
            .filter((p) => tipo !== 'aniversariantes' || String(p.nascimento ?? '').slice(5, 7) === mes)
            .map((p) => ({ contatoId: String(p.contato_id), nome: nomeEmbutido(p.contatos) ?? '' })));
    },

    async dispararCampanha(id: string, mensagem: string): Promise<ResultadoDoDisparo> {
        const problema = problemaDaMensagemDaCampanha(mensagem);
        if (problema) throw new Error(problema);
        const publico = await this.publicoDaCampanha(id);
        if (publico.length === 0) throw new Error('O público está vazio.');
        const { data, error } = await cliente().rpc('enfileirar_campanha', {
            p_campanha: id, p_contatos: publico.map((p) => p.contatoId), p_mensagem: mensagem.trim(),
        });
        erro('Não foi possível disparar a campanha', error);
        return lerResultadoDoDisparo(data);
    },

    async enviarArquivo(conversaId: string, arquivo: File, legenda = ''): Promise<Mensagem> {
        const problema = problemaDoArquivo(arquivo);
        if (problema) throw new Error(problema);

        /* Arquivo não fica "pendente": o reenvio automático é só de texto, e um
           PDF esperando um canal que talvez não volte seria promessa. Sem
           WhatsApp conectado, recusa antes de subir o arquivo. */
        const canal = await canalDaConversa(conversaId);
        if (!canal) throw new Error('Arquivo só sai com o WhatsApp desta conversa conectado.');

        const clinica = clinicaId();
        const tipo = tipoDaMensagemDoArquivo(arquivo.type);
        const caminho = `${clinica}/${conversaId}/${crypto.randomUUID()}-${nomeSeguroDoArquivo(arquivo.name)}`;
        /* Os bytes, e não o `File`: ver a nota em `enviarAnexoDaFicha`. */
        const bytes = await arquivo.arrayBuffer();
        const { error: erroEnvio } = await cliente().storage.from('mensagens').upload(caminho, bytes, {
            contentType: arquivo.type, upsert: false,
        });
        if (erroEnvio) throw new Error(`Não foi possível enviar ${arquivo.name}: ${erroEnvio.message}`);

        const agora = new Date().toISOString();
        const autor = atual()?.nome ?? 'Equipe';
        const texto = legenda.trim() || arquivo.name;
        const { data, error } = await cliente().from('mensagens')
            .insert({
                clinica_id: clinica, conversa_id: conversaId, direcao: 'enviada', tipo, texto,
                midia_path: caminho, autor_nome: autor, status: 'pendente', ocorrido_em: agora,
            })
            .select('id')
            .single();
        erro('Falha ao gravar o arquivo na conversa', error);
        await cliente().from('conversas').update({ ultima_mensagem_em: agora, nao_lidas: 0, estado: 'em-atendimento' }).eq('id', conversaId);

        let status: Mensagem['status'] = 'pendente';
        const { data: entrega, error: erroDaEntrega } = await cliente().functions.invoke('canal', {
            body: { acao: 'enviar', canal, mensagem: (data as { id: string }).id },
        });
        if (erroDaEntrega) {
            const detalhe = await mensagemDaFuncao(erroDaEntrega);
            throw new Error(detalhe ?? 'O arquivo foi gravado, mas não saiu para o WhatsApp.');
        }
        if ((entrega as { enviada?: boolean } | null)?.enviada) status = 'enviada';

        return {
            id: (data as { id: string }).id, conversaId, direcao: 'enviada', tipo, texto, autor, status, ocorridoEm: agora,
        };
    },

    /* ---- trilha T7 ---- */

    async unidadesAtivas(): Promise<UnidadeDaClinica[]> {
        const { data, error } = await cliente().from('unidades')
            .select('id, nome, telefone, email, cep, estado, cidade, bairro, rua, numero, complemento')
            .is('arquivado_em', null).order('nome');
        erro('Falha ao ler as unidades', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraUnidade);
    },

    async criarUnidade(dados: NovaUnidade): Promise<UnidadeDaClinica> {
        const { data, error } = await cliente().rpc('criar_unidade', { p_clinica: clinicaId(), p_dados: dados });
        /* A mensagem é a regra que recusou (nome, telefone, permissão), sem prefixo. */
        if (error) throw new Error(error.message);
        const { data: linha, error: erroDeLeitura } = await cliente().from('unidades')
            .select('id, nome, telefone, email, cep, estado, cidade, bairro, rua, numero, complemento')
            .eq('id', String(data)).single();
        erro('A unidade foi criada, mas não voltou do servidor', erroDeLeitura);
        return paraUnidade(linha as unknown as Record<string, unknown>);
    },

    async arquivarUnidade(id: string): Promise<void> {
        const { error } = await cliente().rpc('arquivar_unidade', { p_unidade: id });
        /* A mensagem diz qual trava barrou: última unidade ativa ou agendamento futuro. */
        if (error) throw new Error(error.message);
    },

    async funis(): Promise<Funil[]> {
        const { data, error } = await cliente()
            .from('funis')
            .select('id, nome, ordem, responsavel_padrao_id, funil_etapas(id, funil_id, titulo, tipo, ordem, cor, arquivado_em)')
            .is('arquivado_em', null)
            .order('ordem');
        erro('Falha ao ler os pipelines', error);
        const linhas = (data ?? []) as unknown as {
            id: string; nome: string; ordem: number; responsavel_padrao_id: string | null;
            funil_etapas: { id: string; funil_id: string; titulo: string; tipo: TipoDaEtapa; ordem: number; cor: CorDaEtapa; arquivado_em: string | null }[] | null;
        }[];
        return ordenarFunis(linhas.map((f) => ({
            id: f.id, nome: f.nome, ordem: f.ordem,
            ...(f.responsavel_padrao_id ? { responsavelPadraoId: f.responsavel_padrao_id } : {}),
            etapas: (f.funil_etapas ?? []).map((e) => ({
                id: e.id, funilId: e.funil_id, titulo: e.titulo, tipo: e.tipo, ordem: e.ordem, cor: e.cor,
                ...(e.arquivado_em ? { arquivada: true } : {}),
            })),
        })));
    },

    async criarFunil(dados: { nome: string; modelo: ModeloDeFunil; responsavelPadraoId?: string }): Promise<Funil> {
        const { data, error } = await cliente().rpc('criar_funil', {
            p_clinica: clinicaId(), p_nome: dados.nome, p_modelo: dados.modelo, p_responsavel: dados.responsavelPadraoId ?? null,
        });
        erro('Não foi possível criar o pipeline', error);
        const criado = (await this.funis()).find((f) => f.id === String(data));
        if (!criado) throw new Error('O pipeline foi criado, mas não voltou na leitura.');
        return criado;
    },

    async salvarFunil(funilId: string, dados: { nome?: string; etapas: EtapaEditada[] }): Promise<Funil> {
        const { error } = await cliente().rpc('salvar_funil', {
            p_funil: funilId,
            p_nome: dados.nome?.trim() || null,
            /* O tipo não vai: é o banco que sabe qual etapa é final. */
            p_etapas: dados.etapas.map((e) => ({ ...(e.id ? { id: e.id } : {}), titulo: e.titulo.trim(), cor: e.cor })),
        });
        erro('Não foi possível salvar o pipeline', error);
        const salvo = (await this.funis()).find((f) => f.id === funilId);
        if (!salvo) throw new Error('O pipeline foi salvo, mas não voltou na leitura.');
        return salvo;
    },

    async arquivarEtapa(etapaId: string, destinoId?: string): Promise<number> {
        const { data, error } = await cliente().rpc('arquivar_etapa', { p_etapa: etapaId, p_destino: destinoId ?? null });
        erro('Não foi possível arquivar a etapa', error);
        return Number(data ?? 0);
    },

    /* ---- trilha T6 ---- */
    async usoDasChavesDeApi(dias = 30): Promise<UsoDeChaveDeApi[]> {
        const { data, error } = await cliente().rpc('uso_das_chaves_de_api', { p_clinica: clinicaId(), p_dias: dias });
        erro('Falha ao ler o uso das chaves de API', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map((u) => ({
            chaveId: String(u.chave_id),
            dia: String(u.dia).slice(0, 10),
            chamadas: Number(u.chamadas ?? 0),
        }));
    },

    async logoDaClinica(): Promise<string | null> {
        const { data, error } = await cliente().from('clinicas').select('logo_caminho').eq('id', clinicaId()).maybeSingle();
        erro('Falha ao ler o logotipo', error);
        const caminho = (data as { logo_caminho?: string | null } | null)?.logo_caminho;
        return caminho ? cliente().storage.from('logos').getPublicUrl(caminho).data.publicUrl : null;
    },

    async enviarLogoDaClinica(arquivo: File): Promise<string> {
        const problema = problemaDoLogo(arquivo);
        if (problema) throw new Error(problema);
        const clinica = clinicaId();
        const extensao = arquivo.type === 'image/png' ? 'png' : arquivo.type === 'image/webp' ? 'webp' : 'jpg';
        const caminho = `${clinica}/logo-${Date.now()}.${extensao}`;
        /* Os bytes, e não o `File`: ver a nota em `enviarAnexoDaFicha`. */
        const bytes = await arquivo.arrayBuffer();
        const { error: erroEnvio } = await cliente().storage.from('logos').upload(caminho, bytes, { contentType: arquivo.type, upsert: false });
        if (erroEnvio) throw new Error(`Não foi possível enviar o logotipo: ${erroEnvio.message}`);
        const { error } = await cliente().rpc('definir_logo_da_clinica', { p_clinica: clinica, p_caminho: caminho });
        erro('O logotipo foi enviado, mas não ficou marcado como o da clínica', error);
        return cliente().storage.from('logos').getPublicUrl(caminho).data.publicUrl;
    },

    async removerLogoDaClinica(): Promise<void> {
        const { error } = await cliente().rpc('definir_logo_da_clinica', { p_clinica: clinicaId(), p_caminho: null });
        erro('Não foi possível voltar à marca padrão', error);
    },

    async convidarProprietaria(clinica: string): Promise<{ email: string | null }> {
        const { data, error } = await cliente().functions.invoke('convite', { body: { clinica, proprietaria: true } });
        if (error) {
            const detalhe = await mensagemDaFuncao(error);
            throw new Error(detalhe ?? 'Não foi possível enviar o convite da proprietária.');
        }
        const email = (data as { email?: string } | null)?.email;
        if (!email) throw new Error('O servidor não confirmou para onde o convite saiu.');
        return { email };
    },

    async mfaObrigatorioParaEscrita(): Promise<boolean> {
        const { data, error } = await cliente().rpc('painel_mfa_obrigatorio');
        erro('Falha ao ler a exigência de MFA', error);
        return data === true;
    },

    async definirMfaObrigatorioParaEscrita(ligado: boolean): Promise<boolean> {
        const { data, error } = await cliente().rpc('painel_definir_mfa_obrigatorio', { p_ligado: ligado });
        erro('Não foi possível mudar a exigência de MFA', error);
        return data === true;
    },

    /* ---- trilha T9 ---- */
    async gatewaysDePagamento(): Promise<GatewayDePagamento[]> {
        const { data, error } = await cliente().rpc('gateways_de_pagamento');
        erro('Falha ao ler os gateways de pagamento', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map((g) => ({
            provedor: String(g.provedor) as ProvedorDeGateway,
            ambiente: g.ambiente === 'producao' ? 'producao' as const : 'sandbox' as const,
            temChave: Boolean(g.tem_chave),
            ...(g.dica ? { dica: String(g.dica) } : {}),
            temSegredoWebhook: Boolean(g.tem_segredo_webhook),
            ...(g.chave_publicavel ? { chavePublicavel: String(g.chave_publicavel) } : {}),
            ativo: Boolean(g.ativo),
            atualizadoEm: String(g.atualizado_em ?? ''),
        }));
    },

    async salvarGateway(provedor: ProvedorDeGateway, d: DadosDoGateway): Promise<void> {
        const { error } = await cliente().rpc('salvar_gateway', {
            p_provedor: provedor,
            p_ambiente: d.ambiente,
            p_chave: d.chave?.trim() || null,
            p_segredo_webhook: d.segredoWebhook?.trim() || null,
            p_chave_publicavel: d.chavePublicavel?.trim() || null,
        });
        erro('Não foi possível guardar o gateway', error);
    },

    async definirGatewayAtivo(provedor: ProvedorDeGateway | null): Promise<void> {
        const { error } = await cliente().rpc('definir_gateway_ativo', { p_provedor: provedor });
        erro('Não foi possível trocar o gateway ativo', error);
    },

    async removerGateway(provedor: ProvedorDeGateway): Promise<void> {
        const { error } = await cliente().rpc('remover_gateway', { p_provedor: provedor });
        erro('Não foi possível remover o gateway', error);
    },

    async testarGateway(provedor: ProvedorDeGateway): Promise<TesteDoGateway> {
        const r = await chamarCobranca<{ ok?: boolean; mensagem?: string }>(
            { acao: 'testar', provedor }, 'Não foi possível testar o gateway.',
        ).catch((e: unknown) => ({ ok: false, mensagem: e instanceof Error ? e.message : String(e) }));
        return { ok: r.ok === true, mensagem: String(r.mensagem ?? '') };
    },

    async criarCobranca(faturaId: string): Promise<CobrancaDaFatura> {
        return paraCobrancaDaFatura(await chamarCobranca({ acao: 'criar', fatura: faturaId }, 'Não foi possível criar a cobrança.'));
    },

    async criarCobrancasPendentes(): Promise<CobrancasCriadas> {
        const r = await chamarCobranca<{ criadas?: number; falhas?: { fatura: string; erro: string }[]; restantes?: number; semGateway?: boolean }>(
            { acao: 'criar_pendentes' }, 'Não foi possível criar as cobranças.',
        );
        return {
            criadas: Number(r.criadas ?? 0),
            falhas: r.falhas ?? [],
            restantes: Number(r.restantes ?? 0),
            semGateway: r.semGateway === true,
        };
    },

    async cancelarCobranca(faturaId: string): Promise<void> {
        await chamarCobranca({ acao: 'cancelar', fatura: faturaId }, 'Não foi possível cancelar a cobrança.');
    },

    async linkDePagamento(faturaId: string): Promise<CobrancaDaFatura> {
        return paraCobrancaDaFatura(await chamarCobranca({ acao: 'link', fatura: faturaId }, 'Não foi possível abrir o pagamento.'));
    },

    async minhaInadimplencia(): Promise<InadimplenciaDaClinica | null> {
        const { data, error } = await cliente().rpc('minha_inadimplencia');
        erro('Falha ao ler a inadimplência', error);
        return paraInadimplencia(data);
    },

    /* Sem filtro por id: o cabeçalho da clínica ativa já estreita a RLS de `clinicas`
       à clínica aberta (0064). A assinatura vem embutida como OBJETO — a FK é única. */
    async meuTeste() {
        const { data, error } = await cliente().from('clinicas')
            .select('teste_ate, assinaturas(cancelada_em, cartao_final, planos(nome, preco_centavos))')
            .eq('situacao', 'trial')
            .not('teste_ate', 'is', null)
            .limit(1);
        erro('Falha ao ler o teste da clínica', error);
        const linha = (Array.isArray(data) ? data[0] : null) as Record<string, unknown> | null | undefined;
        if (!linha?.teste_ate) return null;
        const umSo = (v: unknown) => (Array.isArray(v) ? v[0] : v) as Record<string, unknown> | null | undefined;
        const assinatura = umSo(linha.assinaturas);
        const plano = umSo(assinatura?.planos);
        return {
            testeAte: String(linha.teste_ate),
            plano: String(plano?.nome ?? ''),
            precoCentavos: plano?.preco_centavos === null || plano?.preco_centavos === undefined ? null : Number(plano.preco_centavos),
            cartaoFinal: assinatura?.cartao_final ? String(assinatura.cartao_final) : null,
            cancelado: Boolean(assinatura?.cancelada_em),
        };
    },

    /* ---- central de notificações (0151) ---- */
    /* Os nomes vêm na mesma chamada, lidos com a RLS de quem lê: nada de nome copiado. */
    async minhasNotificacoes() {
        const { data, error } = await cliente().rpc('minhas_notificacoes', { p_limite: 50 });
        erro('Falha ao ler as notificações', error);
        return (Array.isArray(data) ? data : [])
            .map((linha) => paraNotificacao(linha as Record<string, unknown>))
            .filter((n): n is NonNullable<typeof n> => n !== null);
    },

    async contarNotificacoes() {
        const { data, error } = await cliente().rpc('contar_minhas_notificacoes');
        erro('Falha ao contar as notificações', error);
        return Number(data ?? 0);
    },

    async marcarNotificacaoLida(id: string) {
        const { error } = await cliente().rpc('marcar_notificacao_lida', { p_id: id });
        erro('Falha ao marcar a notificação como lida', error);
    },

    async marcarTodasNotificacoesLidas() {
        const { error } = await cliente().rpc('marcar_todas_notificacoes_lidas');
        erro('Falha ao marcar as notificações como lidas', error);
    },

    /* ---- Google Agenda (0152) ---- */
    async minhaAgendaGoogle() {
        const { data, error } = await cliente().rpc('minha_agenda_google');
        erro('Falha ao ler a agenda Google', error);
        const a = data as Record<string, unknown> | null;
        if (!a || (a.situacao !== 'conectada' && a.situacao !== 'desconectando' && a.situacao !== 'revogada')) return null;
        return {
            email: typeof a.email === 'string' ? a.email : null,
            situacao: a.situacao,
            conectadaEm: typeof a.conectadaEm === 'string' ? a.conectadaEm : null,
            ultimoErro: typeof a.ultimoErro === 'string' ? a.ultimoErro : null,
            naFila: Number(a.naFila ?? 0),
        };
    },

    /* A URL do Google sai da borda: é ela que tem o cliente OAuth e cria o "state". */
    async conectarAgendaGoogle(destino: string) {
        const { data, error } = await cliente().functions.invoke('google-agenda/conectar', { body: { destino } });
        if (error) throw new Error((await mensagemDaFuncao(error)) ?? 'Não foi possível abrir o Google.');
        const url = (data as { url?: unknown } | null)?.url;
        if (typeof url !== 'string' || !url.startsWith('https://accounts.google.com/')) {
            throw new Error('O servidor não devolveu o endereço do Google.');
        }
        return url;
    },

    async desconectarAgendaGoogle() {
        const { error } = await cliente().rpc('google_agenda_desconectar');
        erro('Falha ao desconectar a agenda Google', error);
    },

    /* ---- Webhooks de saída (0153) ---- */
    async webhooks() {
        const { data, error } = await cliente().rpc('webhooks_da_clinica', { p_clinica: clinicaId() });
        erro('Falha ao ler os webhooks', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraWebhook);
    },

    async criarWebhook(dados: DadosDoWebhook) {
        const { data, error } = await cliente().rpc('criar_webhook', {
            p_clinica: clinicaId(), p_nome: dados.nome, p_url: dados.url, p_eventos: dados.eventos,
        });
        erro('Não foi possível criar o webhook', error);
        const linha = ((data ?? []) as unknown as { id: string; segredo: string }[])[0];
        if (!linha?.segredo) throw new Error('O servidor não devolveu o segredo do webhook.');
        const webhook = (await this.webhooks()).find((w) => w.id === String(linha.id));
        if (!webhook) throw new Error('Webhook criado, mas não encontrado na releitura.');
        return { webhook, segredo: linha.segredo };
    },

    async atualizarWebhook(id: string, dados: DadosDoWebhook) {
        const { error } = await cliente().rpc('atualizar_webhook', {
            p_id: id, p_nome: dados.nome, p_url: dados.url, p_eventos: dados.eventos, p_ativo: dados.ativo,
        });
        erro('Não foi possível salvar o webhook', error);
        return this.webhooks();
    },

    async girarSegredoDoWebhook(id: string) {
        const { data, error } = await cliente().rpc('girar_segredo_do_webhook', { p_id: id });
        erro('Não foi possível gerar o novo segredo', error);
        if (typeof data !== 'string' || !data.startsWith('whsec_')) throw new Error('O servidor não devolveu o novo segredo.');
        return data;
    },

    async excluirWebhook(id: string) {
        const { error } = await cliente().rpc('excluir_webhook', { p_id: id });
        erro('Não foi possível excluir o webhook', error);
        return this.webhooks();
    },

    async testarWebhook(id: string) {
        const { error } = await cliente().rpc('testar_webhook', { p_id: id });
        erro('Não foi possível enviar o teste', error);
    },

    async entregasDoWebhook(id: string) {
        const { data, error } = await cliente().rpc('entregas_do_webhook', { p_id: id, p_limite: 50 });
        erro('Falha ao ler as entregas do webhook', error);
        return ((data ?? []) as unknown as Record<string, unknown>[]).map(paraEntregaDeWebhook);
    },

    async reenviarEntregaDeWebhook(id: string) {
        const { error } = await cliente().rpc('reenviar_entrega_de_webhook', { p_id: id });
        erro('Não foi possível reenviar a entrega', error);
    },

    /* ---- trilha B4: brain ---- */
    ...brainNoSupabase,
};

const TIPOS_DE_LOGO = ['image/png', 'image/jpeg', 'image/webp'];
const LOGO_MAXIMO = 2 * 1024 * 1024;

/** O mesmo recorte do balde `logos` (0098), dito antes de subir. */
export function problemaDoLogo(arquivo: { type: string; size: number }): string | null {
    if (!TIPOS_DE_LOGO.includes(arquivo.type)) return 'O logotipo precisa ser PNG, JPEG ou WebP.';
    if (arquivo.size > LOGO_MAXIMO) return 'O logotipo pode ter até 2 MB.';
    return null;
}

/* ---- trilha T2 ---- */

/**
 * Id de um cadastro pelo nome, quando a tela não mandou o id.
 *
 * Precisa bater com UM registro. Achar zero e seguir sem — ou achar dois e pegar
 * o primeiro — grava o agendamento no nome errado sem ninguém ver.
 */
async function idPorNome(tabela: 'contatos' | 'procedimentos' | 'membros' | 'salas', nome: string, rotulo: string): Promise<string> {
    const alvo = nome.trim();
    if (!alvo) throw new Error(`Escolha ${rotulo === 'sala' ? 'a' : 'o'} ${rotulo} na lista.`);
    /* A proposta do agente escreve "Dra. Ana Prado"; o cadastro, "Ana Prado". */
    const semTitulo = alvo.replace(/^dra?\.?\s+/i, '');
    const escapar = (v: string) => v.replace(/[\\%_]/g, (c) => `\\${c}`);

    for (const candidato of [...new Set([alvo, semTitulo])]) {
        const { data, error } = await cliente().from(tabela).select('id').ilike('nome', escapar(candidato)).limit(2);
        erro(`Falha ao procurar ${rotulo}`, error);
        const achados = (data ?? []) as unknown as { id: string }[];
        if (achados.length > 1) throw new Error(`Há mais de um ${rotulo} chamado ${candidato}: escolha na lista.`);
        const [unico] = achados;
        if (unico) return String(unico.id);
    }
    throw new Error(`${rotulo.charAt(0).toUpperCase()}${rotulo.slice(1)} "${alvo}" não está no cadastro.`);
}

function paraContatoParaEscolha(c: Record<string, unknown>): ContatoParaEscolha {
    /* `pacientes.contato_id` é chave única: o PostgREST devolve OBJETO, não
       lista — e lido como lista o paciente sumiria em silêncio. */
    const temLinha = (v: unknown) => (Array.isArray(v) ? v.length > 0 : Boolean(v));
    return {
        id: String(c.id),
        nome: String(c.nome ?? ''),
        telefone: String(c.telefone ?? ''),
        tipo: temLinha(c.pacientes) ? 'paciente' : temLinha(c.leads) ? 'lead' : 'contato',
    };
}

/* ---- trilha T7 ---- */

interface RecorteDeUnidade {
    id: string;
    /** A principal é a ativa mais antiga, como `app.unidade_principal`: o que não tem unidade é dela. */
    principal: boolean;
}

/**
 * A unidade escolhida na topbar, se ainda ativa. Arquivada (ou de outra clínica) vale
 * "Todas": a tela vazia sem motivo à vista seria pior do que mostrar tudo.
 */
async function recorteDaUnidade(): Promise<RecorteDeUnidade | null> {
    const escolhida = unidadeAtiva();
    if (!escolhida) return null;
    const { data, error } = await cliente().from('unidades').select('id').is('arquivado_em', null)
        .order('criado_em').order('id');
    erro('Falha ao ler as unidades', error);
    const ativas = ((data ?? []) as { id: string }[]).map((u) => u.id);
    return ativas.includes(escolhida) ? { id: escolhida, principal: ativas[0] === escolhida } : null;
}

/** Filtro `or` de linha com unidade: as dela e, se for a principal, as sem unidade. */
function filtroDaUnidade(r: RecorteDeUnidade): string {
    return r.principal ? `unidade_id.eq.${r.id},unidade_id.is.null` : `unidade_id.eq.${r.id}`;
}

/** Como `filtroDaUnidade`, mas bloqueio sem unidade vale para a clínica inteira (0101). */
function filtroDosAgendamentos(r: RecorteDeUnidade): string {
    return r.principal
        ? `unidade_id.eq.${r.id},unidade_id.is.null`
        : `unidade_id.eq.${r.id},and(unidade_id.is.null,status.eq.bloqueio)`;
}

/**
 * Salas ativas que podem receber agendamento: sai a de unidade arquivada e, com unidade
 * escolhida, a de outra — o banco recusaria sala de uma unidade em agendamento de outra.
 */
async function salasParaAgendar(r: RecorteDeUnidade | null): Promise<{ id: string; nome: string }[]> {
    const { data, error } = await cliente().from('salas')
        .select('id, nome, unidade_id, unidades(arquivado_em)').eq('ativa', true).order('nome');
    erro('Falha ao ler as salas', error);
    const linhas = (data ?? []) as unknown as {
        id: string; nome: string; unidade_id: string | null; unidades: { arquivado_em: string | null } | null;
    }[];
    return linhas
        .filter((s) => !s.unidades?.arquivado_em)
        .filter((s) => !r || (s.unidade_id ? s.unidade_id === r.id : r.principal))
        .map((s) => ({ id: s.id, nome: s.nome }));
}

function paraUnidade(u: Record<string, unknown>): UnidadeDaClinica {
    return {
        id: String(u.id),
        nome: String(u.nome ?? ''),
        telefone: (u.telefone as string | null) ?? '',
        email: (u.email as string | null) ?? '',
        cep: (u.cep as string | null) ?? '',
        estado: (u.estado as string | null) ?? '',
        cidade: (u.cidade as string | null) ?? '',
        bairro: (u.bairro as string | null) ?? '',
        rua: (u.rua as string | null) ?? '',
        numero: (u.numero as string | null) ?? '',
        complemento: (u.complemento as string | null) ?? '',
    };
}
